import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { baseURL, imageURL } from "../../../../Services/api/baseURL";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftCircle, ArrowRight, Package, CheckCircle,
  Clock, AlertCircle, PlusCircle, Search, X,
  Truck, RefreshCw,
} from "react-feather";
import moment from "moment";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import { FaRegStickyNote } from "react-icons/fa";
import useRbac from "../../../../Hooks/useRbac";

const BRAND = "#d3178a";
const BRAND_LIGHT = "#fce7f3";
const BRAND_HOVER = "#a5116c";

const getToken = () => JSON.parse(localStorage.getItem("token"));

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "#6b7280", bg: "#f3f4f6", icon: Clock },
  in_transit: { label: "In Transit", color: "#0369a1", bg: "#e0f2fe", icon: Truck },
  received: { label: "Received", color: "#15803d", bg: "#dcfce7", icon: CheckCircle },
  partially_received: { label: "Partial", color: "#b45309", bg: "#fef3c7", icon: AlertCircle },
  cancelled: { label: "Cancelled", color: "#dc2626", bg: "#fee2e2", icon: X },
};

const STATUS_DOT = {
  draft: { dot: "#9ca3af", bg: "#f3f4f6", color: "#6b7280", label: "Draft" },
  in_transit: { dot: "#60a5fa", bg: "#eff6ff", color: "#1d4ed8", label: "In progress" },
  received: { dot: "#6b7280", bg: "#f3f4f6", color: "#374151", label: "Transferred" },
  partially_received: { dot: "#f59e0b", bg: "#fffbeb", color: "#b45309", label: "Partial" },
  cancelled: { dot: "#ef4444", bg: "#fef2f2", color: "#dc2626", label: "Cancelled" },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_DOT[status] || STATUS_DOT.draft;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
      style={{ color: cfg.color, background: cfg.bg }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.dot, flexShrink: 0, display: "inline-block" }} />
      {cfg.label}
    </span>
  );
};

const StatCard = ({ label, value, sub, accent }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-2 px-6 flex flex-col justify-between"
    style={{ borderBottom: `3px solid ${accent || BRAND}` }}>
    <div>
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">{label}</p>
      <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
    </div>
    {sub && <p className="text-xs text-gray-500">{sub}</p>}
  </div>
);

const PERIOD_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Today", value: "day" },
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
  { label: "Year", value: "year" },
  { label: "Custom", value: "custom" },
];

const buildDateParams = (period, startDate, endDate) => {
  const now = new Date();
  if (period === "day") {
    const s = new Date(now); s.setHours(0, 0, 0, 0);
    const e = new Date(now); e.setHours(23, 59, 59, 999);
    return { start_date: s.toISOString(), end_date: e.toISOString() };
  }
  if (period === "week") {
    const day = now.getDay();
    const s = new Date(now); s.setDate(now.getDate() - ((day + 6) % 7)); s.setHours(0, 0, 0, 0);
    const e = new Date(s); e.setDate(s.getDate() + 6); e.setHours(23, 59, 59, 999);
    return { start_date: s.toISOString(), end_date: e.toISOString() };
  }
  if (period === "month") {
    const s = new Date(now.getFullYear(), now.getMonth(), 1);
    const e = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start_date: s.toISOString(), end_date: e.toISOString() };
  }
  if (period === "year") {
    const s = new Date(now.getFullYear(), 0, 1);
    const e = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
    return { start_date: s.toISOString(), end_date: e.toISOString() };
  }
  if (period === "custom" && startDate && endDate) {
    const s = new Date(startDate); s.setHours(0, 0, 0, 0);
    const e = new Date(endDate); e.setHours(23, 59, 59, 999);
    return { start_date: s.toISOString(), end_date: e.toISOString() };
  }
  return {};
};

const DT_CUSTOM_STYLES = {
  headRow: { style: { backgroundColor: "#f9fafb", borderBottom: "1px solid #f3f4f6" } },
  headCells: { style: { fontSize: "11px", fontWeight: "600", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" } },
  rows: { style: { cursor: "pointer", fontSize: "13px" }, highlightOnHoverStyle: { backgroundColor: "#f9fafb", transition: "background 0.15s" } },
  cells: { style: { paddingTop: "10px", paddingBottom: "10px" } },
};

// ═════════════════════════════════════════════════════════════════════════════
// TRANSFER LIST
// ═════════════════════════════════════════════════════════════════════════════
const TransferList = ({ onCreateNew, onViewDetail }) => {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [period, setPeriod] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const { can } = useRbac();

  const fetchTransfers = useCallback(async () => {
    if (period === "custom" && (!startDate || !endDate)) return;
    setLoading(true);
    try {
      const params = { ...buildDateParams(period, startDate, endDate) };
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.search = search;
      const resp = await axios.get(`${baseURL}/api/admin/transfers`, {
        params, headers: { Authorization: getToken() },
      });
      setTransfers(resp.data.data || []);
    } catch { setTransfers([]); }
    finally { setLoading(false); }
  }, [search, statusFilter, period, startDate, endDate]);

  useEffect(() => { fetchTransfers(); }, [fetchTransfers]);

  const stats = {
    total: transfers.length,
    inTransit: transfers.filter(t => t.status === "in_transit").length,
    received: transfers.filter(t => ["received", "partially_received"].includes(t.status)).length,
    totalItems: transfers.reduce((s, t) => s + (t.items?.reduce((a, i) => a + (i.quantity_to_move || 0), 0) || 0), 0),
  };

  const filtered = transfers.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      t.transfer_number?.toLowerCase().includes(q) ||
      t.reference_name?.toLowerCase().includes(q) ||
      t.from_store?.storeName?.toLowerCase().includes(q) ||
      t.to_store?.storeName?.toLowerCase().includes(q)
    );
  });

  const todayStr = new Date().toISOString().split("T")[0];

  const [popupTransfer, setPopupTransfer] = useState(null);
  const [popupAnchor, setPopupAnchor] = useState({ x: 0, y: 0 });
  const [popupLoading, setPopupLoading] = useState(false);

  const handleReceivedClick = async (e, row) => {
    e.stopPropagation();
    if (popupTransfer?._id === row._id) { setPopupTransfer(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const popupH = 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const y = spaceBelow >= popupH ? rect.bottom + 6 : rect.top - popupH - 6;
    const x = Math.min(rect.left, window.innerWidth - 300);
    setPopupAnchor({ x, y });
    setPopupTransfer(row);
    setPopupLoading(true);
    try {
      const resp = await axios.get(`${baseURL}/api/admin/transfers/${row._id}`, {
        headers: { Authorization: getToken() },
      });
      setPopupTransfer(resp.data.data);
    } catch { }
    finally { setPopupLoading(false); }
  };

  const columns = [
    {
      name: "Transfer #",
      selector: row => row.transfer_number,
      sortable: true,
      width: "120px",
      cell: row => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span className="font-bold text-gray-900">{row.transfer_number}</span>
          {row.notes && row.notes.trim() !== "" && (
            <FaRegStickyNote style={{ color: "orange", fontSize: "15px", flexShrink: 0 }} title={`Note: ${row.notes}`} />
          )}
        </div>
      ),
    },
    {
      name: "Reference Name",
      selector: row => row.reference_name,
      sortable: true,
      width: "200px",
      cell: row => (
        <span className="text-gray-600 text-sm truncate" title={row.reference_name || ""}>
          {row.reference_name || <span className="text-gray-300">—</span>}
        </span>
      ),
    },
    {
      name: "Origin",
      selector: row => row.from_store?.storeName,
      width: "150px",
      sortable: true,
      cell: row => <span className="text-gray-600">{row.from_store?.storeName || "—"}</span>,
    },
    {
      name: "Destination",
      selector: row => row.to_store?.storeName,
      width: "150px",
      sortable: true,
      cell: row => <span className="text-gray-600">{row.to_store?.storeName || "—"}</span>,
    },
    {
      name: "Status",
      selector: row => row.status,
      sortable: true,
      width: "150px",
      cell: row => <StatusBadge status={row.status} />,
    },
    {
      name: "Received",
      selector: row => row.items?.reduce((s, i) => s + (i.accepted || 0), 0),
      sortable: true,
      width: "130px",
      cell: row => {
        const totalAccepted = row.items?.reduce((s, i) => s + (i.accepted || 0), 0) ?? 0;
        const totalQty = row.items?.reduce((s, i) => s + (i.quantity_to_move || 0), 0) ?? 0;
        const isInProgress = ["draft", "in_transit"].includes(row.status);
        const hasItems = row.items?.length > 0;
        const isClickable = !isInProgress && hasItems;
        return (
          <button onClick={e => isClickable && handleReceivedClick(e, row)}
            style={{
              display: "flex", alignItems: "center", gap: "5px",
              cursor: isClickable ? "pointer" : "default", background: "none", border: "none", padding: 0
            }}>
            <span className="text-sm text-gray-700 font-medium">{totalAccepted} of {totalQty}</span>
            {isClickable && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            )}
          </button>
        );
      },
    },
    {
      name: "Expected Arrival",
      selector: row => row.expected_arrival,
      sortable: true,
      width: "160px",
      cell: row => (
        <span className="text-gray-500 text-xs">
          {row.expected_arrival ? moment(row.expected_arrival).format("D MMM YYYY, h:mm a") : "—"}
        </span>
      ),
    },
    {
      name: "Created",
      selector: row => row.createdAt,
      sortable: true,
      width: "110px",
      cell: row => <span className="text-gray-400 text-xs">{moment(row.createdAt).format("D MMM YYYY")}</span>,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: BRAND_LIGHT }}>
            <Truck size={18} style={{ color: BRAND }} />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Transfers</h1>
        </div>
        {can('stock.create') && <button onClick={onCreateNew}
          className="flex items-center gap-2 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
          style={{ background: BRAND }}
          onMouseEnter={e => e.currentTarget.style.background = BRAND_HOVER}
          onMouseLeave={e => e.currentTarget.style.background = BRAND}>
          <PlusCircle size={15} />Create transfer
        </button>}
      </div>

      {/* Period filter */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {PERIOD_OPTIONS.map(o => (
              <button key={o.value}
                onClick={() => { setPeriod(o.value); setStartDate(""); setEndDate(""); }}
                className="px-4 py-1.5 text-xs font-semibold rounded-md transition-all whitespace-nowrap"
                style={period === o.value ? { background: BRAND, color: "#fff" } : { color: "#6b7280" }}>
                {o.label}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <input type="date" value={startDate} max={endDate || todayStr}
                  onChange={e => setStartDate(e.target.value)}
                  className="bg-transparent text-sm text-gray-700 outline-none w-32 cursor-pointer" />
              </div>
              <div className="flex items-center gap-1 px-2 border-x border-gray-200">
                <div className="w-3 h-px bg-gray-400" />
                <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <div className="flex items-center gap-2 px-3 py-2">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <input type="date" value={endDate} min={startDate} max={todayStr}
                  onChange={e => setEndDate(e.target.value)}
                  className="bg-transparent text-sm text-gray-700 outline-none w-32 cursor-pointer" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-0">
        <StatCard label="Total Transfers" value={stats.total} accent={BRAND} />
        <StatCard label="In Transit" value={stats.inTransit} sub="Active shipments" accent="#0369a1" />
        <StatCard label="Received" value={stats.received} sub="Completed" accent="#15803d" />
        <StatCard label="Total Items Moved" value={stats.totalItems} sub="Across all transfers" accent="#b45309" />
      </div>

      {/* Received items popup */}
      {popupTransfer && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setPopupTransfer(null)} />
          <div className="fixed z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 w-80"
            style={{ top: popupAnchor.y, left: popupAnchor.x, maxHeight: "320px", display: "flex", flexDirection: "column" }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded flex items-center justify-center bg-gray-100">
                  <CheckCircle size={10} className="text-gray-500" />
                </span>
                <span className="text-xs font-semibold text-gray-600">
                  Received ({popupTransfer.items?.filter(i => i.accepted > 0).length || 0})
                </span>
              </div>
              <span className="text-xs text-gray-400">{popupTransfer.transfer_number}</span>
            </div>
            <div className="p-3 space-y-3 max-h-64 overflow-y-auto">
              {popupLoading ? (
                <div className="flex justify-center py-4">
                  <RefreshCw size={18} className="animate-spin text-gray-300" />
                </div>
              ) : (popupTransfer.items || []).map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  {item.product_info?.variants?.[0]?.variantImage
                    ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`}
                      className="w-10 h-10 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
                    : <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Package size={13} className="text-gray-300" />
                    </div>}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate" title={item.product_info?.productName || ""}>
                      {item.product_info?.productName || "—"}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">{item.accepted ?? 0} of {item.quantity_to_move}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {["all", "draft", "in_transit", "received", "cancelled"].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className="px-3 py-1.5 text-xs font-semibold rounded-md transition-all capitalize"
                style={statusFilter === s ? { background: BRAND, color: "#fff" } : { color: "#6b7280" }}>
                {s === "in_transit" ? "In Transit" : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <div className="mb-0 file-content form-group position-relative search_outer d-flex align-items-center">
            <i className="fa fa-search"></i>
            <input className="form-control border-0" value={search}
              onChange={e => setSearch(e.target.value)} type="text" placeholder="Search..." />
          </div>
        </div>
        <DataTable
          data={filtered}
          columns={columns}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          onRowClicked={row => onViewDetail(row._id)}
          progressPending={loading}
          progressComponent={
            <div className="flex justify-center items-center py-20">
              <RefreshCw size={24} className="animate-spin text-gray-300" />
            </div>
          }
          noDataComponent={
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Truck size={40} className="text-gray-200" />
              <p className="text-sm text-gray-400">No transfers found</p>
              <button onClick={onCreateNew} className="text-sm font-semibold" style={{ color: BRAND }}>
                Create your first transfer →
              </button>
            </div>
          }
          customStyles={DT_CUSTOM_STYLES}
          highlightOnHover
          pointerOnHover
        />
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// CREATE TRANSFER
// ═════════════════════════════════════════════════════════════════════════════
const StoreStatusPill = ({ label, filled, destination }) => (
  <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
    style={filled
      ? { background: destination ? "#dcfce7" : BRAND_LIGHT, color: destination ? "#15803d" : BRAND }
      : { background: "#f3f4f6", color: "#9ca3af" }}>
    {filled ? label : (destination ? "Select destination" : "Select origin")}
  </div>
);

const ProductSearchModal = ({
  products, search, onSearchChange, onSelect, onRemove, onClose,
  selectedIds, loading, destStockMap = {}, fromStoreName = "", toStoreName = "",
  categoryOptions = [], subCategoryOptions = [],
  categoryFilter = "", onCategoryChange,
  subCategoryFilter = "", onSubCategoryChange,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h3 className="text-base font-semibold text-gray-900 m-0">Add products</h3>
        <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 border-0 bg-transparent" style={{ cursor: 'pointer' }}>
          <X size={15} />
        </button>
      </div>
      <div className="px-5 py-3 border-b border-gray-100 space-y-2">
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus-within:border-[#d3178a]">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input value={search} onChange={e => onSearchChange(e.target.value)} placeholder="Search products..." autoFocus
            className="bg-transparent text-sm text-gray-700 outline-none flex-1 border-0 w-full" />
        </div>
        {categoryOptions.length > 0 && (
          <div className="flex gap-2">
            <select value={categoryFilter} onChange={e => onCategoryChange(e.target.value)}
              className="flex-1 h-9 border border-gray-200 rounded-lg px-3 text-sm text-gray-700 bg-gray-50 focus:outline-none focus:border-[#d3178a]"
              style={{ cursor: 'pointer' }}>
              <option value="">All Categories</option>
              {categoryOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={subCategoryFilter} onChange={e => onSubCategoryChange(e.target.value)}
              disabled={!categoryFilter}
              className="flex-1 h-9 border border-gray-200 rounded-lg px-3 text-sm text-gray-700 bg-gray-50 focus:outline-none focus:border-[#d3178a] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ cursor: 'pointer' }}>
              <option value="">All Sub-Categories</option>
              {subCategoryOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="overflow-y-auto flex-1" style={{ minHeight: '200px' }}>
        {loading
          ? <div className="flex justify-center py-10"><RefreshCw size={20} className="animate-spin text-gray-300" /></div>
          : products.length === 0
            ? <p className="text-sm text-gray-400 text-center py-10">No products found</p>
            : (
              <table className="w-full text-sm table-fixed border-collapse m-0">
                <colgroup>
                  <col style={{ width: '45px' }} />
                  <col />
                  <col style={{ width: '130px' }} />
                  <col style={{ width: '130px' }} />
                </colgroup>
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="ps-4 pe-0 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase border-0"></th>
                    <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase border-0">Product</th>
                    <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase border-0">
                      <span className="flex items-center justify-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: BRAND }} />
                        {fromStoreName ? fromStoreName.split(" ").slice(0, 2).join(" ") : "At origin"}
                      </span>
                    </th>
                    <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase border-0">
                      <span className="flex items-center justify-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                        {toStoreName ? toStoreName.split(" ").slice(0, 2).join(" ") : "At dest."}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {products.map(p => {
                    const isSelected = selectedIds.includes(p._id);
                    const isOutOfStock = p.number_of_products === 0;
                    const pid = (p.product_id?._id || p.product_id || "").toString();
                    const destQty = destStockMap[pid] ?? null;

                    const handleToggle = () => {
                      if (isSelected) {
                        onRemove(p._id);
                      } else {
                        if (!isOutOfStock) {
                          onSelect(p);
                        }
                      }
                    };

                    return (
                      <tr key={p._id} className={`${isOutOfStock && !isSelected ? "opacity-50" : ""} ${isSelected ? "bg-gray-50" : ""}`}>
                        <td className="ps-4 pe-0 py-3 text-left border-0">
                          <div className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              id={`checkbox-transfer-${p._id}`}
                              style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                              checked={isSelected}
                              disabled={isOutOfStock && !isSelected}
                              onChange={handleToggle}
                            />
                            <label
                              htmlFor={`checkbox-transfer-${p._id}`}
                              className="d-flex align-items-center justify-content-center border rounded-circle"
                              style={{
                                width: '20px',
                                height: '20px',
                                margin: 0,
                                cursor: isOutOfStock && !isSelected ? 'not-allowed' : 'pointer',
                                backgroundColor: isSelected ? '#d3178a' : '#ffffff',
                                borderColor: isSelected ? '#d3178a' : '#ced4da',
                                color: '#ffffff',
                                transition: 'all 0.2s ease-in-out'
                              }}
                            >
                              {isSelected && (
                                <svg 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="#ffffff" 
                                  strokeWidth="3.5" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round" 
                                  style={{ width: '12px', height: '12px' }}
                                >
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </label>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-left border-0">
                          <div className="flex items-center gap-3">
                            {p.product_info?.[0]?.variants?.[0]?.variantImage
                              ? <img src={`${imageURL}${p.product_info[0].variants[0].variantImage}`}
                                className={`w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0 ${isSelected ? "opacity-50" : ""}`} alt="" />
                              : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <Package size={13} className="text-gray-300" />
                              </div>}
                            <div className="min-w-0 flex-1">
                              <p className={`font-medium text-sm truncate m-0 ${isSelected ? "text-gray-400" : "text-gray-800"}`}
                                title={p.product_info?.[0]?.productName || ""}>
                                {p.product_info?.[0]?.productName || "—"}
                              </p>
                              {isSelected && (
                                <p className="text-xs mt-0.5 truncate m-0" style={{ color: BRAND }}>Item already selected</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center border-0">
                          <span className={`text-sm font-semibold ${isSelected ? "text-gray-300" : "text-gray-700"}`}>
                            {p.number_of_products}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center border-0">
                          <span className={`text-sm font-semibold ${isSelected ? "text-gray-300" : "text-gray-700"}`}>
                            {destQty != null ? destQty : <span className="text-gray-300 text-xs">new</span>}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
      </div>
      <div className="px-5 py-3.5 border-t border-gray-100 flex items-center justify-between">
        <div className="text-sm font-medium text-gray-500">
          {selectedIds.length} variants selected
        </div>
        <button onClick={onClose} className="text-sm font-semibold px-5 py-2 rounded-xl text-white border-0" style={{ background: BRAND, cursor: 'pointer' }}>
          Done
        </button>
      </div>
    </div>
  </div>
);

const CreateTransfer = ({ onBack, onCreated }) => {
  const [stores, setStores] = useState([]);
  const [fromStore, setFromStore] = useState(null);
  const [toStore, setToStore] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subCategoryFilter, setSubCategoryFilter] = useState("");
  const [showProductModal, setShowProductModal] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [destStockMap, setDestStockMap] = useState({});
  const [expectedArrival, setExpectedArrival] = useState("");
  const [referenceName, setReferenceName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [categoryMap, setCategoryMap] = useState({});
  const [subCategoryMap, setSubCategoryMap] = useState({});
  const { can } = useRbac();

  useEffect(() => {
    axios.get(`${baseURL}/api/admin/get-collections?page=1&limit=1000`, { headers: { Authorization: getToken() } })
      .then(r => {
        const map = {};
        (r.data.data || []).forEach(c => { map[c._id] = c.collection_name; });
        setCategoryMap(map);
      }).catch(() => { });
    axios.get(`${baseURL}/api/admin/get-sub-collections?page=1&limit=1000`, { headers: { Authorization: getToken() } })
      .then(r => {
        const map = {};
        (r.data.data || []).forEach(s => { map[s._id] = s.sub_collection_name; });
        setSubCategoryMap(map);
      }).catch(() => { });
  }, []);

  // useEffect(() => {
  //   axios.get(`${baseURL}/api/store/get-all-stores`, { headers: { Authorization: getToken() } })
  //     .then(r => setStores((r.data.data || []).filter(s => s.status === "active")))
  //     .catch(() => { });
  // }, []);

  useEffect(() => {
  axios.get(`${baseURL}/api/store/get-all-stores`, { headers: { Authorization: getToken() } })
    .then(r => setStores(r.data.data || [])) // keep all stores, active + inactive
    .catch(() => { });
}, []);

  useEffect(() => {
    if (!fromStore) { setAvailableProducts([]); return; }
    setLoadingProducts(true);
    axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${fromStore._id}`, {
      params: { page: 1, limit: 10000 }, headers: { Authorization: getToken() },
    })
      .then(r => {
        console.log(r.data.data);
        setAvailableProducts(r.data.data || []);
      })
      .finally(() => setLoadingProducts(false));
  }, [fromStore]);

  useEffect(() => {
    if (!toStore) { setDestStockMap({}); return; }
    axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${toStore._id}`, {
      params: { page: 1, limit: 10000 }, headers: { Authorization: getToken() },
    })
      .then(r => {
        console.log(r.data.data);
        const map = {};
        (r.data.data || []).forEach(s => {
          const pid = (s.product_id?._id || s.product_id || "").toString();
          if (pid) map[pid] = s.number_of_products;
        });
        setDestStockMap(map);
      })
      .catch(() => setDestStockMap({}));
  }, [toStore]);

  // const filteredProducts = availableProducts.filter(p =>
  //   !productSearch || p?.product_info?.[0]?.productName?.toLowerCase().includes(productSearch.toLowerCase())
  // );

  const filteredProducts = availableProducts.filter(p => {
    if (productSearch) {
      const q = productSearch.toLowerCase();
      const name = (p?.product_info?.[0]?.productName || "").toLowerCase();
      const variantName = (p?.product_info?.[0]?.variants?.[0]?.variantName || "").toLowerCase();
      if (!name.includes(q) && !variantName.includes(q)) return false;
    }
    if (categoryFilter) {
      const cid = (p.category_id?._id || p.category_id || "").toString();
      if (cid !== categoryFilter) return false;
    }
    if (subCategoryFilter) {
      const sid = (p.sub_category_id?._id || p.sub_category_id || "").toString();
      if (sid !== subCategoryFilter) return false;
    }
    return true;
  });

  const categoryOptions = [...new Map(
    availableProducts.filter(p => p.category_id).map(p => {
      const id = (p.category_id?._id || p.category_id || "").toString();
      const name = categoryMap[id] || id;
      // const name = p.category_id?.categoryName || p.category_id?.name || id;
      return [id, { id, name }];
    })
  ).values()];

  const subCategoryOptions = [...new Map(
    availableProducts.filter(p => {
      if (!categoryFilter) return p.sub_category_id;
      const cid = (p.category_id?._id || p.category_id || "").toString();
      return cid === categoryFilter && p.sub_category_id;
    }).map(p => {
      const id = (p.sub_category_id?._id || p.sub_category_id || "").toString();
      // const name = p.sub_category_id?.sub_collection_name || p.sub_category_id?.name || id;
      const name = subCategoryMap[id] || id;
      return [id, { id, name }];
    })
  ).values()];



  const handleAddProduct = (stockItem) => {
    if (selectedItems.find(i => i.stock_id === stockItem._id)) return;
    setSelectedItems(prev => [...prev, {
      stock_id: stockItem._id,
      product_id: stockItem.product_id,
      category_id: stockItem.category_id,
      sub_category_id: stockItem.sub_category_id,
      product_info: stockItem.product_info?.[0],
      quantity_to_move: 1,
      available_at_origin: stockItem.number_of_products,
    }]);
  };

  const handleRemoveItem = id => setSelectedItems(p => p.filter(i => i.stock_id !== id));
  const handleQtyChange = (id, qty) => {
    setSelectedItems(p => p.map(i =>
      i.stock_id === id
        ? {
          ...i,
          quantity_to_move: qty === '' ? '' : Math.max(1, Math.min(parseInt(qty) || 1, i.available_at_origin))
        }
        : i
    ));
  };

  const handleSaveDraft = async () => {
    if (!fromStore || !toStore) return Swal.fire({ icon: "warning", title: "Please select origin and destination stores." });
    if (!selectedItems.length) return Swal.fire({ icon: "warning", title: "Add at least one product to transfer." });
    setSaving(true);
    try {
      const resp = await axios.post(`${baseURL}/api/admin/transfers`, {
        from_store_id: fromStore._id,
        to_store_id: toStore._id,
        items: selectedItems.map(i => ({
          stock_id: i.stock_id, product_id: i.product_id,
          category_id: i.category_id, sub_category_id: i.sub_category_id,
          quantity_to_move: i.quantity_to_move,
        })),
        expected_arrival: expectedArrival || null,
        reference_name: referenceName, notes,
      }, { headers: { Authorization: getToken() } });
      Swal.fire({ icon: "success", title: "Transfer created!", text: `Draft ${resp.data.data.transfer_number} saved.` });
      onCreated(resp.data.data._id);
    } catch (err) {
      Swal.fire({ icon: "error", title: err?.response?.data?.message || "Failed to create transfer." });
    } finally { setSaving(false); }
  };

  const canProceed = fromStore && toStore && fromStore._id !== toStore?._id;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack}
          className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center transition-all hover:border-[#d3178a]"
          style={{ color: BRAND }}>
          <ArrowLeftCircle size={18} />
        </button>
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-0.5">
            <span className="cursor-pointer hover:underline" onClick={onBack}>Transfers</span>
            <span>›</span>
            <span style={{ color: BRAND }}>Create transfer</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">Create transfer</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[160px]">
                <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Origin store</label>
                <select value={fromStore?._id || ""}
                  onChange={e => { setFromStore(stores.find(s => s._id === e.target.value) || null); setSelectedItems([]); }}
                  className="w-full h-10 border border-gray-200 rounded-xl px-3 text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-[#d3178a]">
                  <option value="">Select origin</option>
                  {stores.map(s => <option key={s._id} value={s._id} disabled={s._id === toStore?._id}>{s.storeName}</option>)}
                </select>
              </div>
              <div className="flex items-center justify-center mt-5">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: BRAND_LIGHT }}>
                  <ArrowRight size={14} style={{ color: BRAND }} />
                </div>
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Destination store</label>
                <select value={toStore?._id || ""}
                  onChange={e => setToStore(stores.find(s => s._id === e.target.value) || null)}
                  className="w-full h-10 border border-gray-200 rounded-xl px-3 text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-[#d3178a]">
                  <option value="">Select destination</option>
                  {stores.map(s => <option key={s._id} value={s._id} disabled={s._id === fromStore?._id}>{s.storeName}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3 text-xs">
              <StoreStatusPill label={fromStore?.storeName} filled={!!fromStore} />
              <div className="flex-1 h-px border-t border-dashed border-gray-200" />
              <div className="w-7 h-7 rounded-full border-2 border-gray-200 flex items-center justify-center">
                <Truck size={12} className="text-gray-400" />
              </div>
              <div className="flex-1 h-px border-t border-dashed border-gray-200" />
              <StoreStatusPill label={toStore?.storeName} filled={!!toStore} destination />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-2 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Products to transfer</h2>
              <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{selectedItems.length} added</span>
            </div>
            {selectedItems.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Product</th>
                    <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-20">Move</th>
                    <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-32">
                      <span className="flex items-center justify-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full inline-block flex-shrink-0" style={{ background: BRAND }} />
                        origin
                      </span>
                    </th>
                    <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-36">
                      <span className="flex items-center justify-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block flex-shrink-0" />
                        destination
                      </span>
                    </th>
                    <th className="px-4 py-2.5 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {selectedItems.map(item => {
                    const pid = (item.product_id?._id || item.product_id || "").toString();
                    const destQty = destStockMap[pid] ?? null;
                    return (
                      <tr key={item.stock_id}>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2.5">
                            {item.product_info?.variants?.[0]?.variantImage
                              ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`} className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
                              : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><Package size={14} className="text-gray-300" /></div>}
                            <span className="font-medium text-gray-800 text-sm" title={item.product_info?.productName || ""}>
                              {item.product_info?.productName || "—"}
                            </span>
                          </div>
                        </td>

                        {/* <td className="px-4 py-3 text-center w-40">
                          <div className="flex flex-col items-center">
                            <input
                              type="number"
                              min={1}
                              max={item.available_at_origin}
                              value={item.quantity_to_move}
                              onChange={e =>
                                handleQtyChange(
                                  item.stock_id,
                                  parseInt(e.target.value) || 1
                                )
                              }
                              className="w-28 h-10 border border-gray-200 rounded-lg px-2 text-base text-center focus:outline-none focus:border-[#d3178a]"
                            />

                            <span className="text-[11px] text-gray-400 mt-1">
                              Min 1 • Max {item.available_at_origin}
                            </span>
                          </div>
                        </td> */}
                        <td className="px-4 py-3 text-center w-40">
                          <div className="flex flex-col items-center">
                            <input
                              type="text"
                              inputMode="numeric"
                              value={item.quantity_to_move}
                              onChange={e => {
                                const val = e.target.value;
                                // Allow empty string (for backspacing)
                                if (val === '') {
                                  handleQtyChange(item.stock_id, '');
                                  return;
                                }
                                // Only allow numbers
                                if (!/^\d+$/.test(val)) return;
                                const num = parseInt(val);
                                // Validate against min/max
                                if (num >= 1 && num <= item.available_at_origin) {
                                  handleQtyChange(item.stock_id, num);
                                }
                              }}
                              onBlur={e => {
                                // If empty or invalid on blur, reset to 1
                                if (!e.target.value || parseInt(e.target.value) < 1) {
                                  handleQtyChange(item.stock_id, 1);
                                }
                              }}
                              className="w-28 h-10 border border-gray-200 rounded-lg px-2 text-base text-center focus:outline-none focus:border-[#d3178a]"
                            />

                            <span className="text-[11px] text-gray-400 mt-1">
                              Min 1 • Max {item.available_at_origin}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <span className="text-gray-500 text-sm">{item.available_at_origin}</span>
                          <span className="mx-1 text-gray-300 text-sm">→</span>
                          <span className={`font-semibold text-sm ${item.available_at_origin - item.quantity_to_move < 0 ? "text-red-500" : ""}`}
                            style={item.available_at_origin - item.quantity_to_move >= 0 ? { color: BRAND } : {}}>
                            {item.available_at_origin - item.quantity_to_move}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          {destQty != null
                            ? <>
                              <span className="text-gray-500 text-sm">{destQty}</span>
                              <span className="mx-1 text-gray-300 text-sm">→</span>
                              <span className="font-semibold text-sm text-emerald-600">{destQty + item.quantity_to_move}</span>
                            </>
                            : toStore
                              ? <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">New</span>
                              : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => handleRemoveItem(item.stock_id)}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                            <X size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <div className="p-4">
              <button disabled={!fromStore} onClick={() => setShowProductModal(true)}
                className="w-full flex items-center gap-2 text-sm text-gray-400 bg-gray-50 border border-dashed border-gray-200 rounded-xl px-4 py-3 hover:border-[#d3178a] hover:text-[#d3178a] transition-colors disabled:cursor-not-allowed disabled:opacity-50">
                <Search size={14} />
                {fromStore ? "Search products to add" : "Select an origin store first"}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">Transfer details</h3>
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1.5 block">Expected arrival</label>
              <input type="datetime-local" value={expectedArrival} min={new Date().toISOString().slice(0, 16)}
                onChange={e => setExpectedArrival(e.target.value)}
                className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-gray-50 focus:outline-none focus:border-[#d3178a]" />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1.5 block">Reference name</label>
              <input type="text" value={referenceName} onChange={e => setReferenceName(e.target.value)}
                placeholder="e.g. Weekly restock"
                className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-gray-50 focus:outline-none focus:border-[#d3178a]" />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1.5 block">Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Add notes about this transfer..." rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#d3178a] resize-none" />
            </div>
          </div>

          {selectedItems.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Products</span><span className="font-semibold text-gray-900">{selectedItems.length}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Total units</span>
                  <span className="font-semibold text-gray-900">{selectedItems.reduce((s, i) => s + i.quantity_to_move, 0)}</span>
                </div>
                {fromStore && (
                  <div className="flex justify-between text-gray-600 pt-2 border-t border-gray-100">
                    <span>From</span>
                    <span className="font-medium text-gray-800 text-right max-w-[130px] truncate">{fromStore.storeName}</span>
                  </div>
                )}
                {toStore && (
                  <div className="flex justify-between text-gray-600">
                    <span>To</span>
                    <span className="font-medium text-gray-800 text-right max-w-[130px] truncate">{toStore.storeName}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {can('stock.create') && (<button onClick={handleSaveDraft} disabled={!canProceed || !selectedItems.length || saving}
            className="w-full flex items-center justify-center gap-2 text-white text-sm font-semibold py-3 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: BRAND }}
            onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = BRAND_HOVER; }}
            onMouseLeave={e => e.currentTarget.style.background = BRAND}>
            {saving ? <RefreshCw size={15} className="animate-spin" /> : null}
            Save as draft
          </button>)}
          <button onClick={onBack}
            className="w-full text-gray-500 text-sm font-medium py-2.5 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
            Cancel
          </button>
        </div>
      </div>

      {showProductModal && (
        // <ProductSearchModal
        //   products={filteredProducts} search={productSearch} onSearchChange={setProductSearch}
        //   onSelect={handleAddProduct} onClose={() => { setShowProductModal(false); setProductSearch(""); }}
        //   selectedIds={selectedItems.map(i => i.stock_id)} loading={loadingProducts}
        //   destStockMap={destStockMap} fromStoreName={fromStore?.storeName || ""} toStoreName={toStore?.storeName || ""}
        // />
        <ProductSearchModal
          products={filteredProducts} search={productSearch} onSearchChange={setProductSearch}
          onSelect={handleAddProduct}
          onRemove={handleRemoveItem}
          onClose={() => { setShowProductModal(false); setProductSearch(""); setCategoryFilter(""); setSubCategoryFilter(""); }}
          selectedIds={selectedItems.map(i => i.stock_id)} loading={loadingProducts}
          destStockMap={destStockMap} fromStoreName={fromStore?.storeName || ""} toStoreName={toStore?.storeName || ""}
          categoryOptions={categoryOptions} subCategoryOptions={subCategoryOptions}
          categoryFilter={categoryFilter} onCategoryChange={v => { setCategoryFilter(v); setSubCategoryFilter(""); }}
          subCategoryFilter={subCategoryFilter} onSubCategoryChange={setSubCategoryFilter}
        />
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// TRANSFER DETAIL
// ═════════════════════════════════════════════════════════════════════════════
const InfoRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-xs text-gray-400 flex-shrink-0">{label}</span>
    <span className="text-xs font-medium text-gray-700 text-right">{value}</span>
  </div>
);

const TransferTimeline = ({ status, transfer }) => {
  const steps = [
    { key: "draft", label: "Draft created", date: transfer.createdAt },
    { key: "in_transit", label: "Moved to transit", date: transfer.transit_at },
    { key: "received", label: "Items received", date: transfer.received_at },
  ];
  const ORDER = ["draft", "in_transit", "received"];
  const currentIndex = ORDER.indexOf(status === "partially_received" ? "received" : status);
  return (
    <div className="space-y-3">
      {steps.map((step, i) => {
        const done = i <= currentIndex;
        return (
          <div key={step.key} className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                style={done ? { background: BRAND } : { background: "#f3f4f6" }}>
                {done ? <CheckCircle size={11} color="white" /> : <div className="w-2 h-2 rounded-full bg-gray-300" />}
              </div>
              {i < steps.length - 1 && (
                <div className="w-px flex-1 min-h-[16px]" style={{ background: done && i < currentIndex ? BRAND : "#e5e7eb" }} />
              )}
            </div>
            <div className="pb-3">
              <p className="text-xs font-medium text-gray-700">{step.label}</p>
              {step.date && done && (
                <p className="text-[10px] text-gray-400">{moment(step.date).format("D MMM YYYY, h:mm a")}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Store Pricing Modal ───────────────────────────────────────────────────────
const StorePricingModal = ({ newItems, toStoreName, fromStoreId, fromStoreName = "Origin store", onConfirm, onSkip }) => {
  const [prices, setPrices] = useState(
    newItems.map(item => ({
      stock_id: item.stock_id,
      product_id: item.product_id,
      product_info: item.product_info,
      sellingPrice: "",
      discount: "0",
      discountType: "percentage",
      finalSellingPrice: "",
      memberPrice: "",
    }))
  );
  // Origin store pricing fetched for reference display
  const [originPricing, setOriginPricing] = useState({});

  useEffect(() => {
    if (!fromStoreId) return;
    newItems.forEach(async (item) => {
      const pid = (item.product_id?._id || item.product_id || "").toString();
      if (!pid) return;
      try {
        const resp = await axios.get(`${baseURL}/api/admin/get-variant-pricing`, {
          params: { product_id: pid, store_id: fromStoreId },
          headers: { Authorization: getToken() },
        });
        if (resp.data.data) {
          setOriginPricing(prev => ({ ...prev, [pid]: resp.data.data }));
        }
      } catch { }
    });
  }, [fromStoreId]);

  const handleChange = (idx, field, val) => {
    const updated = [...prices];
    updated[idx][field] = val;
    const sp = parseFloat(updated[idx].sellingPrice) || 0;
    const disc = parseFloat(updated[idx].discount) || 0;
    const type = updated[idx].discountType;
    if (["sellingPrice", "discount", "discountType"].includes(field)) {
      const final = type === "percentage" ? sp - (sp * disc / 100) : sp - disc;
      updated[idx].finalSellingPrice = final > 0 ? parseFloat(final.toFixed(4)) : 0;
    }
    setPrices(updated);
  };

  const isValid = prices.every(p => parseFloat(p.sellingPrice) > 0);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Store Pricing</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              New products at <span className="font-medium text-gray-600">{toStoreName}</span> — set prices before saving
            </p>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {prices.map((p, i) => {
            const pid = (p.product_id?._id || p.product_id || "").toString();
            const op = originPricing[pid];
            return (
              <div key={p.stock_id} className="border border-gray-100 rounded-xl p-4 bg-gray-50">
                {/* Product header */}
                <div className="flex items-center gap-2.5 mb-3">
                  {p.product_info?.variants?.[0]?.variantImage
                    ? <img src={`${imageURL}${p.product_info.variants[0].variantImage}`} className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
                    : <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0"><Package size={13} className="text-gray-400" /></div>}
                  <span className="font-semibold text-gray-800 text-sm">{p.product_info?.productName || "—"}</span>
                </div>

                {/* Origin store price reference — table style matching Store Pricing */}
                {op && (
                  <div className="mb-3 rounded-lg border border-gray-200 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase tracking-wider">Store Name</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase tracking-wider">Selling Price</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase tracking-wider">Discount</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase tracking-wider">Final Price</th>
                          {op.memberPrice > 0 && (
                            <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase tracking-wider">Member Price</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="bg-white">
                          <td className="px-3 py-2.5 text-gray-700 font-medium">{fromStoreName}</td>
                          <td className="px-3 py-2.5 text-gray-700">{op.sellingPrice}</td>
                          <td className="px-3 py-2.5 text-gray-700">
                            {op.discount || 0} {op.discountType === "percentage" ? "%" : "$"}
                          </td>
                          <td className="px-3 py-2.5 text-gray-700">{op.finalSellingPrice}</td>
                          {op.memberPrice > 0 && (
                            <td className="px-3 py-2.5 text-gray-700">{op.memberPrice}</td>
                          )}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1.5">Selling Price</label>
                    <input type="number" min="0" step="0.01" value={p.sellingPrice}
                      onChange={e => handleChange(i, "sellingPrice", e.target.value)} placeholder="0"
                      className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-[#d3178a]" />
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1.5">Discount</label>
                    <div className="flex gap-1">
                      <input type="number" min="0" value={p.discount}
                        onChange={e => handleChange(i, "discount", e.target.value)} placeholder="0"
                        // className="flex-1 h-9 border border-gray-200 rounded-lg px-2 text-sm bg-white focus:outline-none focus:border-[#d3178a] w-2"/>
                        className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-[#d3178a]" />
                      <select value={p.discountType} onChange={e => handleChange(i, "discountType", e.target.value)}
                        className="h-9 border border-gray-200 rounded-lg px-1 text-xs bg-white focus:outline-none cursor-pointer">
                        <option value="percentage">%</option>
                        <option value="amount">$</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1.5">Final Price</label>
                    <input type="number" readOnly value={p.finalSellingPrice} placeholder="0"
                      className="w-full h-9 border border-gray-100 rounded-lg px-3 text-sm bg-gray-100 text-gray-600 outline-none cursor-default" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1.5">Member Price</label>
                    <input type="number" min="0" step="0.01" value={p.memberPrice}
                      onChange={e => handleChange(i, "memberPrice", e.target.value)} placeholder="0"
                      className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-[#d3178a]" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
          <button onClick={onSkip}
            className="text-sm text-gray-500 border border-gray-200 px-4 py-2 rounded-lg hover:border-gray-300 transition-colors">
            Skip (set prices later)
          </button>
          <button onClick={() => onConfirm(prices)} disabled={!isValid}
            className="flex items-center gap-2 text-white text-sm font-semibold px-5 py-2 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            style={{ background: BRAND }}>
            <CheckCircle size={14} />
            Save Details
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Receive Items Modal ───────────────────────────────────────────────────────
const ReceiveItemsModal = ({ transfer, destStockMap = {}, onClose, onReceived }) => {
  const [receiveData, setReceiveData] = useState(
    (transfer.items || []).map(item => ({
      stock_id: item.stock_id, product_id: item.product_id,
      category_id: item.category_id, sub_category_id: item.sub_category_id,
      product_info: item.product_info, quantity_to_move: item.quantity_to_move,
      accepted: item.quantity_to_move, rejected: 0,
    }))
  );
  const [saving, setSaving] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);

  const handleChange = (index, field, val) => {
    const updated = [...receiveData];
    updated[index][field] = Math.max(0, parseInt(val) || 0);
    setReceiveData(updated);
  };

  const isNewAtDest = (item) => {
    const pid = (item.product_id?._id || item.product_id || "").toString();
    return destStockMap[pid] == null;
  };

  const newItems = receiveData.filter(item => item.accepted > 0 && isNewAtDest(item));

  const doSubmit = async (items, pricing = []) => {
    setSaving(true);
    try {
      await axios.patch(`${baseURL}/api/admin/transfers/${transfer._id}/receive`,
        { items, pricing }, { headers: { Authorization: getToken() } });
      Swal.fire({ icon: "success", title: "Items received!", text: "Stock has been updated." });
      onReceived();
    } catch (err) {
      Swal.fire({ icon: "error", title: err?.response?.data?.message || "Failed to receive items." });
    } finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    const overItems = receiveData.filter(i => (i.accepted + i.rejected) > i.quantity_to_move);
    if (overItems.length > 0) {
      const confirm = await Swal.fire({
        icon: "warning", title: "Over-received items",
        text: "Some items exceed quantity to move. Continue anyway?",
        showCancelButton: true, confirmButtonColor: BRAND, confirmButtonText: "Yes, continue",
      });
      if (!confirm.isConfirmed) return;
    }
    if (newItems.length > 0) {
      setPendingSubmit(receiveData);
      setShowPricing(true);
    } else {
      doSubmit(receiveData, []);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Receive items</h3>
              <p className="text-xs text-gray-400 mt-0.5">at {transfer.to_store?.storeName}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setReceiveData(d => d.map(i => ({ ...i, accepted: i.quantity_to_move, rejected: 0 })))}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg border"
                style={{ color: "#15803d", borderColor: "#bbf7d0", background: "#dcfce7" }}>Accept all</button>
              <button onClick={() => setReceiveData(d => d.map(i => ({ ...i, accepted: 0, rejected: i.quantity_to_move })))}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg border"
                style={{ color: "#dc2626", borderColor: "#fecaca", background: "#fee2e2" }}>Reject all</button>
              <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 ml-1">
                <X size={15} />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Product</th>
                  <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Accept</th>
                  <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Reject</th>
                  <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Received</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {receiveData.map((item, i) => {
                  const total = item.accepted + item.rejected;
                  const over = total - item.quantity_to_move;
                  const isOver = over > 0;
                  const isNew = item.accepted > 0 && isNewAtDest(item);
                  return (
                    <tr key={i} className={isOver ? "bg-amber-50" : ""}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          {item.product_info?.variants?.[0]?.variantImage
                            ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`}
                              className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
                            : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <Package size={13} className="text-gray-300" />
                            </div>}
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{item.product_info?.productName || "—"}</p>
                            {isNew && (
                              <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                                New — price required
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-center">
                        <input type="number" min={0} value={item.accepted}
                          onChange={e => handleChange(i, "accepted", e.target.value)}
                          className="w-20 border rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none"
                          style={{ borderColor: isOver ? "#fbbf24" : "#bbf7d0", background: isOver ? "#fffbeb" : "#f0fdf4" }} />
                      </td>
                      <td className="px-5 py-3 text-center">
                        <input type="number" min={0} value={item.rejected}
                          onChange={e => handleChange(i, "rejected", e.target.value)}
                          className="w-20 border rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none"
                          style={{ borderColor: isOver ? "#fbbf24" : "#fecaca", background: isOver ? "#fffbeb" : "#fff5f5" }} />
                      </td>
                      <td className="px-5 py-3 text-center">
                        {isOver ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="text-xs font-semibold text-amber-600 flex items-center gap-1">
                              <AlertCircle size={11} />{over} unit{over > 1 ? "s" : ""} over received
                            </span>
                            <span className="text-xs text-gray-400">{total} of {item.quantity_to_move}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">{total} of {item.quantity_to_move}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
            {newItems.length > 0 && (
              <p className="text-xs text-emerald-600 flex items-center gap-1.5">
                <AlertCircle size={12} />
                {newItems.length} new product{newItems.length > 1 ? "s" : ""} — pricing required next
              </p>
            )}
            <div className="flex gap-3 ml-auto">
              <button onClick={onClose} className="text-sm text-gray-600 border border-gray-200 px-4 py-2 rounded-lg hover:border-gray-300">Cancel</button>
              <button onClick={handleSubmit} disabled={saving}
                className="flex items-center gap-2 text-white text-sm font-semibold px-5 py-2 rounded-xl disabled:opacity-50 transition-colors"
                style={{ background: "#15803d" }}>
                {saving ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>

      {showPricing && (
        <StorePricingModal
          newItems={newItems}
          toStoreName={transfer.to_store?.storeName}
          fromStoreId={transfer.from_store?._id || transfer.from_store_id}
          fromStoreName={transfer.from_store?.storeName || "Origin store"}
          onConfirm={(pricing) => { setShowPricing(false); doSubmit(pendingSubmit, pricing); }}
          onSkip={() => { setShowPricing(false); doSubmit(pendingSubmit, []); }}
        />
      )}
    </>
  );
};

const TransferDetail = ({ transferId, onBack }) => {
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [destStockMap, setDestStockMap] = useState({});
  const [originStockMap, setOriginStockMap] = useState({});
  const { can } = useRbac();

  const fetchTransfer = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${baseURL}/api/admin/transfers/${transferId}`, {
        headers: { Authorization: getToken() },
      });
      const t = resp.data.data;
      setTransfer(t);

      const fromId = t?.from_store?._id || t?.from_store_id;
      const toId = t?.to_store?._id || t?.to_store_id;

      if (fromId && t?.status === "draft") {
        try {
          const or = await axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${fromId}`,
            { params: { page: 1, limit: 500 }, headers: { Authorization: getToken() } });
          const map = {};
          (or.data.data || []).forEach(s => {
            const pid = (s.product_id?._id || s.product_id || "").toString();
            if (pid) map[pid] = s.number_of_products;
          });
          setOriginStockMap(map);
        } catch { setOriginStockMap({}); }
      }

      if (toId && ["draft", "in_transit"].includes(t?.status)) {
        try {
          const dr = await axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${toId}`,
            { params: { page: 1, limit: 500 }, headers: { Authorization: getToken() } });
          const map = {};
          (dr.data.data || []).forEach(s => {
            const pid = (s.product_id?._id || s.product_id || "").toString();
            if (pid) map[pid] = s.number_of_products;
          });
          setDestStockMap(map);
        } catch { setDestStockMap({}); }
      }
    } catch { setTransfer(null); }
    finally { setLoading(false); }
  }, [transferId]);

  useEffect(() => { fetchTransfer(); }, [fetchTransfer]);

  const handleMoveToTransit = async () => {
    setActionLoading(true);
    try {
      await axios.patch(`${baseURL}/api/admin/transfers/${transferId}/move-to-transit`, {},
        { headers: { Authorization: getToken() } });
      Swal.fire({ icon: "success", title: "Transfer moved to in transit!" });
      fetchTransfer();
    } catch (err) {
      Swal.fire({ icon: "error", title: err?.response?.data?.message || "Failed." });
    } finally { setActionLoading(false); }
  };

  const handleCancel = async () => {
    const result = await Swal.fire({
      icon: "warning", title: "Cancel this transfer?",
      text: "Stock will not be moved. This cannot be undone.",
      showCancelButton: true, confirmButtonColor: "#dc2626", confirmButtonText: "Yes, cancel it",
    });
    if (!result.isConfirmed) return;
    setActionLoading(true);
    try {
      await axios.patch(`${baseURL}/api/admin/transfers/${transferId}/cancel`, {},
        { headers: { Authorization: getToken() } });
      fetchTransfer();
    } finally { setActionLoading(false); }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><RefreshCw size={28} className="animate-spin text-gray-300" /></div>;
  if (!transfer) return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-3">
      <AlertCircle size={40} className="text-gray-200" />
      <p className="text-sm text-gray-400">Transfer not found.</p>
      <button onClick={onBack} className="text-sm" style={{ color: BRAND }}>← Back</button>
    </div>
  );

  const isDraft = transfer.status === "draft";
  const isInTransit = transfer.status === "in_transit";

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={onBack}
            className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center transition-all"
            style={{ color: BRAND }}>
            <ArrowLeftCircle size={18} />
          </button>
          <div>
            <div className="flex items-center gap-2 text-xs text-gray-400 mb-0.5">
              <span className="cursor-pointer hover:underline" onClick={onBack}>Transfers</span>
              <span>›</span>
              <span style={{ color: BRAND }}>{transfer.transfer_number}</span>
            </div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900">{transfer.transfer_number}</h1>
              <StatusBadge status={transfer.status} />
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              Created {moment(transfer.createdAt).format("D MMM YYYY")} · {transfer.items?.length || 0} products
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isDraft && can('stock.update') && (
            <>
              <button onClick={handleCancel}
                className="text-sm text-gray-600 border border-gray-200 px-4 py-2 rounded-lg hover:border-red-300 hover:text-red-500 transition-colors">
                Cancel transfer
              </button>
              <button onClick={handleMoveToTransit} disabled={actionLoading}
                className="flex items-center gap-2 text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
                style={{ background: BRAND }}>
                {actionLoading ? <RefreshCw size={14} className="animate-spin" /> : <Truck size={14} />}
                Move to in transit
              </button>
            </>
          )}
          {isInTransit && can('stock.update') && (
            <button onClick={() => setShowReceiveModal(true)}
              className="flex items-center gap-2 text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors"
              style={{ background: "#15803d" }}>
              <CheckCircle size={14} />Receive items
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-center gap-3 text-sm">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
                <Package size={13} className="text-gray-400" />
                <span className="text-gray-700 font-medium">{transfer.from_store?.storeName}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-8 h-px bg-gray-200" />
                <Truck size={14} className="text-gray-400" />
                <div className="w-8 h-px bg-gray-200" />
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
                <Package size={13} className="text-gray-400" />
                <span className="text-gray-700 font-medium">{transfer.to_store?.storeName}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Products</h2>
              <div className="flex items-center gap-4 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: BRAND }} />
                  {transfer.from_store?.storeName?.split(" ").slice(0, 2).join(" ")}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                  {transfer.to_store?.storeName?.split(" ").slice(0, 2).join(" ")}
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-fixed">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[30%]">Product</th>
                    <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[7%]">Move</th>
                    <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[14%]">
                      <span className="flex items-center justify-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: BRAND }} />Origin
                      </span>
                    </th>
                    <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[14%]">
                      <span className="flex items-center justify-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />Destination
                      </span>
                    </th>
                    <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[11%]">Accepted</th>
                    <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[11%]">Rejected</th>
                    <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[13%]">Received</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {(transfer.items || []).map((item, i) => {
                    const pid = (item.product_id?._id || item.product_id || "").toString();
                    const isDone = ["received", "partially_received"].includes(transfer.status);
                    const originCur = originStockMap[pid] ?? null;
                    const originBef = item.origin_qty_before != null ? item.origin_qty_before : (isDraft && originCur != null ? originCur : null);
                    const originAft = item.origin_qty_after != null ? item.origin_qty_after : (isDraft && originCur != null ? originCur - item.quantity_to_move : null);
                    const destCur = destStockMap[pid] ?? null;
                    const destBef = isDone ? item.destination_qty_before : destCur;
                    const destAft = isDone ? item.destination_qty_after : (destCur != null ? destCur + item.quantity_to_move : null);
                    return (
                      <tr key={i} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            {item.product_info?.variants?.[0]?.variantImage
                              ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`} className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
                              : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><Package size={13} className="text-gray-300" /></div>}
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-800 break-words leading-snug text-sm">{item.product_info?.productName || "—"}</p>
                              <p className="text-xs text-gray-400 mt-0.5">{item.subcategory_info?.sub_collection_name || ""}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center font-semibold text-gray-800">{item.quantity_to_move}</td>
                        <td className="px-4 py-3 text-center">
                          {originBef != null
                            ? <span className="text-sm whitespace-nowrap">
                              <span className="text-gray-500">{originBef}</span>
                              <span className="mx-1 text-gray-300">→</span>
                              <span className="font-semibold" style={{ color: BRAND }}>{originAft}</span>
                            </span>
                            : <span className="text-gray-400 text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {destBef != null
                            ? <span className="text-sm whitespace-nowrap">
                              <span className="text-gray-500">{destBef}</span>
                              <span className="mx-1 text-gray-300">→</span>
                              <span className="font-semibold text-emerald-600">{destAft}</span>
                            </span>
                            : <span className="text-gray-400 text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {item.accepted != null
                            ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: "#15803d", background: "#dcfce7" }}>{item.accepted}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {item.rejected != null
                            ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: "#dc2626", background: "#fee2e2" }}>{item.rejected}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600 whitespace-nowrap">
                          {item.accepted != null ? `${item.accepted} of ${item.quantity_to_move}` : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Details</h3>
            <InfoRow label="Transfer #" value={transfer.transfer_number} />
            <InfoRow label="Status" value={<StatusBadge status={transfer.status} />} />
            <InfoRow label="Created" value={moment(transfer.createdAt).format("D MMM YYYY, h:mm a")} />
            {transfer.expected_arrival && (
              <InfoRow label="Expected" value={moment(transfer.expected_arrival).format("D MMM YYYY, h:mm a")} />
            )}
            {transfer.reference_name && <InfoRow label="Reference" value={transfer.reference_name} />}
            {transfer.notes && <InfoRow label="Notes" value={transfer.notes} />}
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Timeline</h3>
            <TransferTimeline status={transfer.status} transfer={transfer} />
          </div>
        </div>
      </div>

      {showReceiveModal && (
        <ReceiveItemsModal
          transfer={transfer}
          destStockMap={destStockMap}
          onClose={() => setShowReceiveModal(false)}
          onReceived={() => { setShowReceiveModal(false); fetchTransfer(); }}
        />
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// ROOT
// ═════════════════════════════════════════════════════════════════════════════
const StockTransfers = () => {
  const navigate = useNavigate();
  const [view, setView] = useState("list");
  const [detailId, setDetailId] = useState(null);
  return (
    <>
      {view === "list" && (
        <TransferList
          onCreateNew={() => setView("create")}
          onViewDetail={id => { setDetailId(id); setView("detail"); }} />
      )}
      {view === "create" && (
        <CreateTransfer
          onBack={() => setView("list")}
          onCreated={id => { setDetailId(id); setView("detail"); }} />
      )}
      {view === "detail" && (
        <TransferDetail transferId={detailId} onBack={() => setView("list")} />
      )}
    </>
  );
};

export default StockTransfers;


// import React, { useState, useCallback, useEffect } from "react";
// import axios from "axios";
// import { baseURL, imageURL } from "../../../../Services/api/baseURL";
// import { useNavigate } from "react-router-dom";
// import {
//   ArrowLeftCircle, ArrowRight, Package, CheckCircle,
//   Clock, AlertCircle, Plus, PlusCircle, Search, X,
//   Truck, RefreshCw,
// } from "react-feather";
// import moment from "moment";
// import DataTable from "react-data-table-component";
// import Swal from "sweetalert2";
// import { FaRegStickyNote } from "react-icons/fa";

// const BRAND = "#d3178a";
// const BRAND_LIGHT = "#fce7f3";
// const BRAND_HOVER = "#a5116c";

// const getToken = () => JSON.parse(localStorage.getItem("token"));

// const STATUS_CONFIG = {
//   draft: { label: "Draft", color: "#6b7280", bg: "#f3f4f6", icon: Clock },
//   in_transit: { label: "In Transit", color: "#0369a1", bg: "#e0f2fe", icon: Truck },
//   received: { label: "Received", color: "#15803d", bg: "#dcfce7", icon: CheckCircle },
//   partially_received: { label: "Partial", color: "#b45309", bg: "#fef3c7", icon: AlertCircle },
//   cancelled: { label: "Cancelled", color: "#dc2626", bg: "#fee2e2", icon: X },
// };

// const STATUS_DOT = {
//   draft: { dot: "#9ca3af", bg: "#f3f4f6", color: "#6b7280", label: "Draft" },
//   in_transit: { dot: "#60a5fa", bg: "#eff6ff", color: "#1d4ed8", label: "In progress" },
//   received: { dot: "#6b7280", bg: "#f3f4f6", color: "#374151", label: "Transferred" },
//   partially_received: { dot: "#f59e0b", bg: "#fffbeb", color: "#b45309", label: "Partial" },
//   cancelled: { dot: "#ef4444", bg: "#fef2f2", color: "#dc2626", label: "Cancelled" },
// };

// const StatusBadge = ({ status }) => {
//   const cfg = STATUS_DOT[status] || STATUS_DOT.draft;
//   return (
//     <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
//       style={{ color: cfg.color, background: cfg.bg }}>
//       <span style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.dot, flexShrink: 0, display: "inline-block" }} />
//       {cfg.label}
//     </span>
//   );
// };

// const StatCard = ({ label, value, sub, accent }) => (
//   <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-2 px-6 flex flex-col justify-between"
//     style={{ borderBottom: `3px solid ${accent || BRAND}` }}>
//     <div>
//       <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">{label}</p>
//       <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
//     </div>
//     {sub && <p className="text-xs text-gray-500">{sub}</p>}
//   </div>
// );

// const PERIOD_OPTIONS = [
//   { label: "Today", value: "day" },
//   { label: "Week", value: "week" },
//   { label: "Month", value: "month" },
//   { label: "Year", value: "year" },
//   { label: "Custom", value: "custom" },
// ];

// const buildDateParams = (period, startDate, endDate) => {
//   const now = new Date();
//   if (period === "day") {
//     const s = new Date(now); s.setHours(0, 0, 0, 0);
//     const e = new Date(now); e.setHours(23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   if (period === "week") {
//     const day = now.getDay();
//     const s = new Date(now); s.setDate(now.getDate() - ((day + 6) % 7)); s.setHours(0, 0, 0, 0);
//     const e = new Date(s); e.setDate(s.getDate() + 6); e.setHours(23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   if (period === "month") {
//     const s = new Date(now.getFullYear(), now.getMonth(), 1);
//     const e = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   if (period === "year") {
//     const s = new Date(now.getFullYear(), 0, 1);
//     const e = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   if (period === "custom" && startDate && endDate) {
//     const s = new Date(startDate); s.setHours(0, 0, 0, 0);
//     const e = new Date(endDate); e.setHours(23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   return {};
// };

// const DT_CUSTOM_STYLES = {
//   headRow: { style: { backgroundColor: "#f9fafb", borderBottom: "1px solid #f3f4f6" } },
//   headCells: { style: { fontSize: "11px", fontWeight: "600", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" } },
//   rows: { style: { cursor: "pointer", fontSize: "13px" }, highlightOnHoverStyle: { backgroundColor: "#f9fafb", transition: "background 0.15s" } },
//   cells: { style: { paddingTop: "10px", paddingBottom: "10px" } },
// };

// // ═════════════════════════════════════════════════════════════════════════════
// // TRANSFER LIST
// // ═════════════════════════════════════════════════════════════════════════════
// const TransferList = ({ onCreateNew, onViewDetail }) => {
//   const [transfers, setTransfers] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [search, setSearch] = useState("");
//   const [statusFilter, setStatusFilter] = useState("all");
//   const [period, setPeriod] = useState("month");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");

//   const fetchTransfers = useCallback(async () => {
//     if (period === "custom" && (!startDate || !endDate)) return;
//     setLoading(true);
//     try {
//       const params = { ...buildDateParams(period, startDate, endDate) };
//       if (statusFilter !== "all") params.status = statusFilter;
//       if (search) params.search = search;
//       const resp = await axios.get(`${baseURL}/api/admin/transfers`, {
//         params, headers: { Authorization: getToken() },
//       });
//       setTransfers(resp.data.data || []);
//     } catch { setTransfers([]); }
//     finally { setLoading(false); }
//   }, [search, statusFilter, period, startDate, endDate]);

//   useEffect(() => { fetchTransfers(); }, [fetchTransfers]);

//   const stats = {
//     total: transfers.length,
//     inTransit: transfers.filter(t => t.status === "in_transit").length,
//     received: transfers.filter(t => ["received", "partially_received"].includes(t.status)).length,
//     totalItems: transfers.reduce((s, t) => s + (t.items?.reduce((a, i) => a + (i.quantity_to_move || 0), 0) || 0), 0),
//   };

//   const filtered = transfers.filter(t => {
//     if (!search) return true;
//     const q = search.toLowerCase();
//     return (
//       t.transfer_number?.toLowerCase().includes(q) ||
//       t.reference_name?.toLowerCase().includes(q) ||
//       t.from_store?.storeName?.toLowerCase().includes(q) ||
//       t.to_store?.storeName?.toLowerCase().includes(q)
//     );
//   });

//   const todayStr = new Date().toISOString().split("T")[0];

//   const [popupTransfer, setPopupTransfer] = useState(null);
//   const [popupAnchor, setPopupAnchor] = useState({ x: 0, y: 0 });
//   const [popupLoading, setPopupLoading] = useState(false);

//   const handleReceivedClick = async (e, row) => {
//     e.stopPropagation();
//     if (popupTransfer?._id === row._id) { setPopupTransfer(null); return; }
//     const rect = e.currentTarget.getBoundingClientRect();
//     const popupH = 320;
//     const spaceBelow = window.innerHeight - rect.bottom;
//     const y = spaceBelow >= popupH ? rect.bottom + 6 : rect.top - popupH - 6;
//     const x = Math.min(rect.left, window.innerWidth - 300);
//     setPopupAnchor({ x, y });
//     setPopupTransfer(row);
//     setPopupLoading(true);
//     try {
//       const resp = await axios.get(`${baseURL}/api/admin/transfers/${row._id}`, {
//         headers: { Authorization: getToken() },
//       });
//       setPopupTransfer(resp.data.data);
//     } catch { }
//     finally { setPopupLoading(false); }
//   };

//   const columns = [
//     {
//       name: "Transfer #",
//       selector: row => row.transfer_number,
//       sortable: true,
//       width: "120px",
//       cell: row => (
//         <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
//           <span className="font-bold text-gray-900">{row.transfer_number}</span>
//           {row.notes && row.notes.trim() !== "" && (
//             <FaRegStickyNote style={{ color: "orange", fontSize: "15px", flexShrink: 0 }} title={`Note: ${row.notes}`} />
//           )}
//         </div>
//       ),
//     },
//     {
//       name: "Reference Name",
//       selector: row => row.reference_name,
//       sortable: true,
//       width: "200px",
//       cell: row => (
//         <span className="text-gray-600 text-sm truncate" title={row.reference_name || ""}>
//           {row.reference_name || <span className="text-gray-300">—</span>}
//         </span>
//       ),
//     },
//     {
//       name: "Origin",
//       selector: row => row.from_store?.storeName,
//       width: "150px",
//       sortable: true,
//       cell: row => <span className="text-gray-600">{row.from_store?.storeName || "—"}</span>,
//     },
//     {
//       name: "Destination",
//       selector: row => row.to_store?.storeName,
//       width: "150px",
//       sortable: true,
//       cell: row => <span className="text-gray-600">{row.to_store?.storeName || "—"}</span>,
//     },
//     {
//       name: "Status",
//       selector: row => row.status,
//       sortable: true,
//       width: "150px",
//       cell: row => <StatusBadge status={row.status} />,
//     },
//     {
//       name: "Received",
//       selector: row => row.items?.reduce((s, i) => s + (i.accepted || 0), 0),
//       sortable: true,
//       width: "130px",
//       cell: row => {
//         const totalAccepted = row.items?.reduce((s, i) => s + (i.accepted || 0), 0) ?? 0;
//         const totalQty = row.items?.reduce((s, i) => s + (i.quantity_to_move || 0), 0) ?? 0;
//         const isInProgress = ["draft", "in_transit"].includes(row.status);
//         const hasItems = row.items?.length > 0;
//         const isClickable = !isInProgress && hasItems;
//         return (
//           <button onClick={e => isClickable && handleReceivedClick(e, row)}
//             style={{
//               display: "flex", alignItems: "center", gap: "5px",
//               cursor: isClickable ? "pointer" : "default", background: "none", border: "none", padding: 0
//             }}>
//             <span className="text-sm text-gray-700 font-medium">{totalAccepted} of {totalQty}</span>
//             {isClickable && (
//               <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
//                 <polyline points="6 9 12 15 18 9" />
//               </svg>
//             )}
//           </button>
//         );
//       },
//     },
//     {
//       name: "Expected Arrival",
//       selector: row => row.expected_arrival,
//       sortable: true,
//       width: "160px",
//       cell: row => (
//         <span className="text-gray-500 text-xs">
//           {row.expected_arrival ? moment(row.expected_arrival).format("D MMM YYYY, h:mm a") : "—"}
//         </span>
//       ),
//     },
//     {
//       name: "Created",
//       selector: row => row.createdAt,
//       sortable: true,
//       width: "110px",
//       cell: row => <span className="text-gray-400 text-xs">{moment(row.createdAt).format("D MMM YYYY")}</span>,
//     },
//   ];

//   return (
//     <div className="min-h-screen bg-gray-50 p-6">
//       {/* Header */}
//       <div className="flex items-center justify-between mb-6">
//         <div className="flex items-center gap-3">
//           <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: BRAND_LIGHT }}>
//             <Truck size={18} style={{ color: BRAND }} />
//           </div>
//           <h1 className="text-xl font-bold text-gray-900">Transfers</h1>
//         </div>
//         <button onClick={onCreateNew}
//           className="flex items-center gap-2 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
//           style={{ background: BRAND }}
//           onMouseEnter={e => e.currentTarget.style.background = BRAND_HOVER}
//           onMouseLeave={e => e.currentTarget.style.background = BRAND}>
//           <PlusCircle size={15} />Create transfer
//         </button>
//       </div>

//       {/* Period filter */}
//       <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-3">
//         <div className="flex flex-wrap items-center gap-3">
//           <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
//             {PERIOD_OPTIONS.map(o => (
//               <button key={o.value}
//                 onClick={() => { setPeriod(o.value); setStartDate(""); setEndDate(""); }}
//                 className="px-4 py-1.5 text-xs font-semibold rounded-md transition-all whitespace-nowrap"
//                 style={period === o.value ? { background: BRAND, color: "#fff" } : { color: "#6b7280" }}>
//                 {o.label}
//               </button>
//             ))}
//           </div>
//           {period === "custom" && (
//             <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
//               <div className="flex items-center gap-2 px-3 py-2">
//                 <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
//                 </svg>
//                 <input type="date" value={startDate} max={endDate || todayStr}
//                   onChange={e => setStartDate(e.target.value)}
//                   className="bg-transparent text-sm text-gray-700 outline-none w-32 cursor-pointer" />
//               </div>
//               <div className="flex items-center gap-1 px-2 border-x border-gray-200">
//                 <div className="w-3 h-px bg-gray-400" />
//                 <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
//                 </svg>
//               </div>
//               <div className="flex items-center gap-2 px-3 py-2">
//                 <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
//                 </svg>
//                 <input type="date" value={endDate} min={startDate} max={todayStr}
//                   onChange={e => setEndDate(e.target.value)}
//                   className="bg-transparent text-sm text-gray-700 outline-none w-32 cursor-pointer" />
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Stats */}
//       <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-0">
//         <StatCard label="Total Transfers" value={stats.total} accent={BRAND} />
//         <StatCard label="In Transit" value={stats.inTransit} sub="Active shipments" accent="#0369a1" />
//         <StatCard label="Received" value={stats.received} sub="Completed" accent="#15803d" />
//         <StatCard label="Total Items Moved" value={stats.totalItems} sub="Across all transfers" accent="#b45309" />
//       </div>

//       {/* Received items popup */}
//       {popupTransfer && (
//         <>
//           <div className="fixed inset-0 z-40" onClick={() => setPopupTransfer(null)} />
//           <div className="fixed z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 w-80"
//             style={{ top: popupAnchor.y, left: popupAnchor.x, maxHeight: "320px", display: "flex", flexDirection: "column" }}>
//             <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
//               <div className="flex items-center gap-2">
//                 <span className="w-4 h-4 rounded flex items-center justify-center bg-gray-100">
//                   <CheckCircle size={10} className="text-gray-500" />
//                 </span>
//                 <span className="text-xs font-semibold text-gray-600">
//                   Received ({popupTransfer.items?.filter(i => i.accepted > 0).length || 0})
//                 </span>
//               </div>
//               <span className="text-xs text-gray-400">{popupTransfer.transfer_number}</span>
//             </div>
//             <div className="p-3 space-y-3 max-h-64 overflow-y-auto">
//               {popupLoading ? (
//                 <div className="flex justify-center py-4">
//                   <RefreshCw size={18} className="animate-spin text-gray-300" />
//                 </div>
//               ) : (popupTransfer.items || []).map((item, i) => (
//                 <div key={i} className="flex items-center gap-3">
//                   {item.product_info?.variants?.[0]?.variantImage
//                     ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`}
//                       className="w-10 h-10 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
//                     : <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
//                       <Package size={13} className="text-gray-300" />
//                     </div>}
//                   <div className="flex-1 min-w-0">
//                     <p className="text-sm font-medium text-gray-800 truncate" title={item.product_info?.productName || ""}>
//                       {item.product_info?.productName || "—"}
//                     </p>
//                     <p className="text-xs text-gray-400 mt-0.5">{item.accepted ?? 0} of {item.quantity_to_move}</p>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </>
//       )}

//       {/* Table */}
//       <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
//         <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
//           <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
//             {["all", "draft", "in_transit", "received", "cancelled"].map(s => (
//               <button key={s} onClick={() => setStatusFilter(s)}
//                 className="px-3 py-1.5 text-xs font-semibold rounded-md transition-all capitalize"
//                 style={statusFilter === s ? { background: BRAND, color: "#fff" } : { color: "#6b7280" }}>
//                 {s === "in_transit" ? "In Transit" : s.charAt(0).toUpperCase() + s.slice(1)}
//               </button>
//             ))}
//           </div>
//           <div className="mb-0 file-content form-group position-relative search_outer d-flex align-items-center">
//             <i className="fa fa-search"></i>
//             <input className="form-control border-0" value={search}
//               onChange={e => setSearch(e.target.value)} type="text" placeholder="Search..." />
//           </div>
//         </div>
//         <DataTable
//           data={filtered}
//           columns={columns}
//           pagination
//           paginationPerPage={10}
//           paginationRowsPerPageOptions={[10, 25, 50, 100]}
//           onRowClicked={row => onViewDetail(row._id)}
//           progressPending={loading}
//           progressComponent={
//             <div className="flex justify-center items-center py-20">
//               <RefreshCw size={24} className="animate-spin text-gray-300" />
//             </div>
//           }
//           noDataComponent={
//             <div className="flex flex-col items-center justify-center py-20 gap-3">
//               <Truck size={40} className="text-gray-200" />
//               <p className="text-sm text-gray-400">No transfers found</p>
//               <button onClick={onCreateNew} className="text-sm font-semibold" style={{ color: BRAND }}>
//                 Create your first transfer →
//               </button>
//             </div>
//           }
//           customStyles={DT_CUSTOM_STYLES}
//           highlightOnHover
//           pointerOnHover
//         />
//       </div>
//     </div>
//   );
// };

// // ═════════════════════════════════════════════════════════════════════════════
// // CREATE TRANSFER
// // ═════════════════════════════════════════════════════════════════════════════
// const StoreStatusPill = ({ label, filled, destination }) => (
//   <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
//     style={filled
//       ? { background: destination ? "#dcfce7" : BRAND_LIGHT, color: destination ? "#15803d" : BRAND }
//       : { background: "#f3f4f6", color: "#9ca3af" }}>
//     {filled ? label : (destination ? "Select destination" : "Select origin")}
//   </div>
// );

// const ProductSearchModal = ({
//   products, search, onSearchChange, onSelect, onClose,
//   selectedIds, loading, destStockMap = {}, fromStoreName = "", toStoreName = ""
// }) => (
//   <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
//     <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden">
//       <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
//         <h3 className="text-base font-semibold text-gray-900">Add products</h3>
//         <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400">
//           <X size={15} />
//         </button>
//       </div>
//       <div className="px-5 py-3 border-b border-gray-100">
//         <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus-within:border-[#d3178a]">
//           <Search size={14} className="text-gray-400 flex-shrink-0" />
//           <input value={search} onChange={e => onSearchChange(e.target.value)} placeholder="Search products..." autoFocus
//             className="bg-transparent text-sm text-gray-700 outline-none flex-1" />
//         </div>
//       </div>
//       <div className="overflow-y-auto flex-1">
//         {loading
//           ? <div className="flex justify-center py-10"><RefreshCw size={20} className="animate-spin text-gray-300" /></div>
//           : products.length === 0
//             ? <p className="text-sm text-gray-400 text-center py-10">No products found</p>
//             : (
//               <table className="w-full text-sm table-fixed">
//                 <colgroup>
//                   <col className="w-[55%]" />
//                   <col className="w-[15%]" />
//                   <col className="w-[15%]" />
//                   <col className="w-[15%]" />
//                 </colgroup>
//                 <thead>
//                   <tr className="bg-gray-50 border-b border-gray-100">
//                     <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase">Product</th>
//                     <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: BRAND }} />
//                         {fromStoreName ? fromStoreName.split(" ").slice(0, 2).join(" ") : "At origin"}
//                       </span>
//                     </th>
//                     <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
//                         {toStoreName ? toStoreName.split(" ").slice(0, 2).join(" ") : "At dest."}
//                       </span>
//                     </th>
//                     <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase"></th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-50">
//                   {products.map(p => {
//                     const isSelected = selectedIds.includes(p._id);
//                     const isOutOfStock = p.number_of_products === 0;
//                     const pid = (p.product_id?._id || p.product_id || "").toString();
//                     const destQty = destStockMap[pid] ?? null;
//                     return (
//                       <tr key={p._id} className={`${isOutOfStock && !isSelected ? "opacity-50" : ""} ${isSelected ? "bg-gray-50" : ""}`}>
//                         <td className="px-5 py-3">
//                           <div className="flex items-center gap-3">
//                             {p.product_info?.[0]?.variants?.[0]?.variantImage
//                               ? <img src={`${imageURL}${p.product_info[0].variants[0].variantImage}`}
//                                 className={`w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0 ${isSelected ? "opacity-50" : ""}`} alt="" />
//                               : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
//                                 <Package size={13} className="text-gray-300" />
//                               </div>}
//                             <div className="min-w-0 flex-1">
//                               <p className={`font-medium text-sm truncate ${isSelected ? "text-gray-400" : "text-gray-800"}`}
//                                 title={p.product_info?.[0]?.productName || ""}>
//                                 {p.product_info?.[0]?.productName || "—"}
//                               </p>
//                               {isSelected && (
//                                 <p className="text-xs mt-0.5 truncate" style={{ color: BRAND }}>Item already selected</p>
//                               )}
//                             </div>
//                           </div>
//                         </td>
//                         <td className="px-5 py-3 text-center">
//                           <span className={`text-sm font-semibold ${isSelected ? "text-gray-300" : "text-gray-700"}`}>
//                             {p.number_of_products}
//                           </span>
//                         </td>
//                         <td className="px-5 py-3 text-center">
//                           <span className={`text-sm font-semibold ${isSelected ? "text-gray-300" : "text-gray-700"}`}>
//                             {destQty != null ? destQty : <span className="text-gray-300 text-xs">new</span>}
//                           </span>
//                         </td>
//                         <td className="px-5 py-3 text-right">
//                           {!isSelected && (
//                             <button onClick={() => !isOutOfStock && onSelect(p)} disabled={isOutOfStock}
//                               className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
//                               style={{ color: BRAND, borderColor: BRAND, background: "white" }}>
//                               Add
//                             </button>
//                           )}
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             )}
//       </div>
//       <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end">
//         <button onClick={onClose} className="text-sm font-semibold px-5 py-2 rounded-xl text-white" style={{ background: BRAND }}>
//           Done ({selectedIds.length} added)
//         </button>
//       </div>
//     </div>
//   </div>
// );

// const CreateTransfer = ({ onBack, onCreated }) => {
//   const [stores, setStores] = useState([]);
//   const [fromStore, setFromStore] = useState(null);
//   const [toStore, setToStore] = useState(null);
//   const [availableProducts, setAvailableProducts] = useState([]);
//   const [selectedItems, setSelectedItems] = useState([]);
//   const [productSearch, setProductSearch] = useState("");
//   const [showProductModal, setShowProductModal] = useState(false);
//   const [loadingProducts, setLoadingProducts] = useState(false);
//   const [destStockMap, setDestStockMap] = useState({});
//   const [expectedArrival, setExpectedArrival] = useState("");
//   const [referenceName, setReferenceName] = useState("");
//   const [notes, setNotes] = useState("");
//   const [saving, setSaving] = useState(false);

//   useEffect(() => {
//     axios.get(`${baseURL}/api/store/get-all-stores`, { headers: { Authorization: getToken() } })
//       .then(r => setStores((r.data.data || []).filter(s => s.status === "active")))
//       .catch(() => { });
//   }, []);

//   useEffect(() => {
//     if (!fromStore) { setAvailableProducts([]); return; }
//     setLoadingProducts(true);
//     axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${fromStore._id}`, {
//       params: { page: 1, limit: 10000 }, headers: { Authorization: getToken() },
//     })
//       .then(r => setAvailableProducts(r.data.data || []))
//       .finally(() => setLoadingProducts(false));
//   }, [fromStore]);

//   useEffect(() => {
//     if (!toStore) { setDestStockMap({}); return; }
//     axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${toStore._id}`, {
//       params: { page: 1, limit: 10000 }, headers: { Authorization: getToken() },
//     })
//       .then(r => {
//         const map = {};
//         (r.data.data || []).forEach(s => {
//           const pid = (s.product_id?._id || s.product_id || "").toString();
//           if (pid) map[pid] = s.number_of_products;
//         });
//         setDestStockMap(map);
//       })
//       .catch(() => setDestStockMap({}));
//   }, [toStore]);

//   const filteredProducts = availableProducts.filter(p =>
//     !productSearch || p?.product_info?.[0]?.productName?.toLowerCase().includes(productSearch.toLowerCase())
//   );

//   const handleAddProduct = (stockItem) => {
//     if (selectedItems.find(i => i.stock_id === stockItem._id)) return;
//     setSelectedItems(prev => [...prev, {
//       stock_id: stockItem._id,
//       product_id: stockItem.product_id,
//       category_id: stockItem.category_id,
//       sub_category_id: stockItem.sub_category_id,
//       product_info: stockItem.product_info?.[0],
//       quantity_to_move: 1,
//       available_at_origin: stockItem.number_of_products,
//     }]);
//   };

//   const handleRemoveItem = id => setSelectedItems(p => p.filter(i => i.stock_id !== id));
//   const handleQtyChange = (id, qty) => {
//     setSelectedItems(p => p.map(i =>
//       i.stock_id === id
//         ? {
//           ...i,
//           quantity_to_move: qty === '' ? '' : Math.max(1, Math.min(parseInt(qty) || 1, i.available_at_origin))
//         }
//         : i
//     ));
//   };

//   const handleSaveDraft = async () => {
//     if (!fromStore || !toStore) return Swal.fire({ icon: "warning", title: "Please select origin and destination stores." });
//     if (!selectedItems.length) return Swal.fire({ icon: "warning", title: "Add at least one product to transfer." });
//     setSaving(true);
//     try {
//       const resp = await axios.post(`${baseURL}/api/admin/transfers`, {
//         from_store_id: fromStore._id,
//         to_store_id: toStore._id,
//         items: selectedItems.map(i => ({
//           stock_id: i.stock_id, product_id: i.product_id,
//           category_id: i.category_id, sub_category_id: i.sub_category_id,
//           quantity_to_move: i.quantity_to_move,
//         })),
//         expected_arrival: expectedArrival || null,
//         reference_name: referenceName, notes,
//       }, { headers: { Authorization: getToken() } });
//       Swal.fire({ icon: "success", title: "Transfer created!", text: `Draft ${resp.data.data.transfer_number} saved.` });
//       onCreated(resp.data.data._id);
//     } catch (err) {
//       Swal.fire({ icon: "error", title: err?.response?.data?.message || "Failed to create transfer." });
//     } finally { setSaving(false); }
//   };

//   const canProceed = fromStore && toStore && fromStore._id !== toStore?._id;

//   return (
//     <div className="min-h-screen bg-gray-50 p-6">
//       <div className="flex items-center gap-3 mb-6">
//         <button onClick={onBack}
//           className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center transition-all hover:border-[#d3178a]"
//           style={{ color: BRAND }}>
//           <ArrowLeftCircle size={18} />
//         </button>
//         <div>
//           <div className="flex items-center gap-2 text-xs text-gray-400 mb-0.5">
//             <span className="cursor-pointer hover:underline" onClick={onBack}>Transfers</span>
//             <span>›</span>
//             <span style={{ color: BRAND }}>Create transfer</span>
//           </div>
//           <h1 className="text-xl font-bold text-gray-900">Create transfer</h1>
//         </div>
//       </div>

//       <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
//         <div className="xl:col-span-2 space-y-4">
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
//             <div className="flex items-center gap-4 flex-wrap">
//               <div className="flex-1 min-w-[160px]">
//                 <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Origin store</label>
//                 <select value={fromStore?._id || ""}
//                   onChange={e => { setFromStore(stores.find(s => s._id === e.target.value) || null); setSelectedItems([]); }}
//                   className="w-full h-10 border border-gray-200 rounded-xl px-3 text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-[#d3178a]">
//                   <option value="">Select origin</option>
//                   {stores.map(s => <option key={s._id} value={s._id} disabled={s._id === toStore?._id}>{s.storeName}</option>)}
//                 </select>
//               </div>
//               <div className="flex items-center justify-center mt-5">
//                 <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: BRAND_LIGHT }}>
//                   <ArrowRight size={14} style={{ color: BRAND }} />
//                 </div>
//               </div>
//               <div className="flex-1 min-w-[160px]">
//                 <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Destination store</label>
//                 <select value={toStore?._id || ""}
//                   onChange={e => setToStore(stores.find(s => s._id === e.target.value) || null)}
//                   className="w-full h-10 border border-gray-200 rounded-xl px-3 text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-[#d3178a]">
//                   <option value="">Select destination</option>
//                   {stores.map(s => <option key={s._id} value={s._id} disabled={s._id === fromStore?._id}>{s.storeName}</option>)}
//                 </select>
//               </div>
//             </div>
//             <div className="mt-4 flex items-center gap-3 text-xs">
//               <StoreStatusPill label={fromStore?.storeName} filled={!!fromStore} />
//               <div className="flex-1 h-px border-t border-dashed border-gray-200" />
//               <div className="w-7 h-7 rounded-full border-2 border-gray-200 flex items-center justify-center">
//                 <Truck size={12} className="text-gray-400" />
//               </div>
//               <div className="flex-1 h-px border-t border-dashed border-gray-200" />
//               <StoreStatusPill label={toStore?.storeName} filled={!!toStore} destination />
//             </div>
//           </div>

//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
//             <div className="px-5 py-2 border-b border-gray-100 flex items-center justify-between">
//               <h2 className="text-sm font-semibold text-gray-900">Products to transfer</h2>
//               <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{selectedItems.length} added</span>
//             </div>
//             {selectedItems.length > 0 && (
//               <table className="w-full text-sm">
//                 <thead>
//                   <tr className="bg-gray-50 border-b border-gray-100">
//                     <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Product</th>
//                     <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-20">Move</th>
//                     <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-32">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full inline-block flex-shrink-0" style={{ background: BRAND }} />
//                         origin
//                       </span>
//                     </th>
//                     <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-36">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block flex-shrink-0" />
//                         destination
//                       </span>
//                     </th>
//                     <th className="px-4 py-2.5 w-10"></th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-50">
//                   {selectedItems.map(item => {
//                     const pid = (item.product_id?._id || item.product_id || "").toString();
//                     const destQty = destStockMap[pid] ?? null;
//                     return (
//                       <tr key={item.stock_id}>
//                         <td className="px-5 py-3">
//                           <div className="flex items-center gap-2.5">
//                             {item.product_info?.variants?.[0]?.variantImage
//                               ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`} className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
//                               : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><Package size={14} className="text-gray-300" /></div>}
//                             <span className="font-medium text-gray-800 text-sm" title={item.product_info?.productName || ""}>
//                               {item.product_info?.productName || "—"}
//                             </span>
//                           </div>
//                         </td>

//                         {/* <td className="px-4 py-3 text-center w-40">
//                           <div className="flex flex-col items-center">
//                             <input
//                               type="number"
//                               min={1}
//                               max={item.available_at_origin}
//                               value={item.quantity_to_move}
//                               onChange={e =>
//                                 handleQtyChange(
//                                   item.stock_id,
//                                   parseInt(e.target.value) || 1
//                                 )
//                               }
//                               className="w-28 h-10 border border-gray-200 rounded-lg px-2 text-base text-center focus:outline-none focus:border-[#d3178a]"
//                             />

//                             <span className="text-[11px] text-gray-400 mt-1">
//                               Min 1 • Max {item.available_at_origin}
//                             </span>
//                           </div>
//                         </td> */}
//                         <td className="px-4 py-3 text-center w-40">
//                           <div className="flex flex-col items-center">
//                             <input
//                               type="text"
//                               inputMode="numeric"
//                               value={item.quantity_to_move}
//                               onChange={e => {
//                                 const val = e.target.value;
//                                 // Allow empty string (for backspacing)
//                                 if (val === '') {
//                                   handleQtyChange(item.stock_id, '');
//                                   return;
//                                 }
//                                 // Only allow numbers
//                                 if (!/^\d+$/.test(val)) return;
//                                 const num = parseInt(val);
//                                 // Validate against min/max
//                                 if (num >= 1 && num <= item.available_at_origin) {
//                                   handleQtyChange(item.stock_id, num);
//                                 }
//                               }}
//                               onBlur={e => {
//                                 // If empty or invalid on blur, reset to 1
//                                 if (!e.target.value || parseInt(e.target.value) < 1) {
//                                   handleQtyChange(item.stock_id, 1);
//                                 }
//                               }}
//                               className="w-28 h-10 border border-gray-200 rounded-lg px-2 text-base text-center focus:outline-none focus:border-[#d3178a]"
//                             />

//                             <span className="text-[11px] text-gray-400 mt-1">
//                               Min 1 • Max {item.available_at_origin}
//                             </span>
//                           </div>
//                         </td>
//                         <td className="px-4 py-3 text-center whitespace-nowrap">
//                           <span className="text-gray-500 text-sm">{item.available_at_origin}</span>
//                           <span className="mx-1 text-gray-300 text-sm">→</span>
//                           <span className={`font-semibold text-sm ${item.available_at_origin - item.quantity_to_move < 0 ? "text-red-500" : ""}`}
//                             style={item.available_at_origin - item.quantity_to_move >= 0 ? { color: BRAND } : {}}>
//                             {item.available_at_origin - item.quantity_to_move}
//                           </span>
//                         </td>
//                         <td className="px-4 py-3 text-center whitespace-nowrap">
//                           {destQty != null
//                             ? <>
//                               <span className="text-gray-500 text-sm">{destQty}</span>
//                               <span className="mx-1 text-gray-300 text-sm">→</span>
//                               <span className="font-semibold text-sm text-emerald-600">{destQty + item.quantity_to_move}</span>
//                             </>
//                             : toStore
//                               ? <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">New</span>
//                               : <span className="text-gray-300 text-xs">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-right">
//                           <button onClick={() => handleRemoveItem(item.stock_id)}
//                             className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
//                             <X size={13} />
//                           </button>
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             )}
//             <div className="p-4">
//               <button disabled={!fromStore} onClick={() => setShowProductModal(true)}
//                 className="w-full flex items-center gap-2 text-sm text-gray-400 bg-gray-50 border border-dashed border-gray-200 rounded-xl px-4 py-3 hover:border-[#d3178a] hover:text-[#d3178a] transition-colors disabled:cursor-not-allowed disabled:opacity-50">
//                 <Search size={14} />
//                 {fromStore ? "Search products to add" : "Select an origin store first"}
//               </button>
//             </div>
//           </div>
//         </div>

//         <div className="space-y-4">
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
//             <h3 className="text-sm font-semibold text-gray-900">Transfer details</h3>
//             <div>
//               <label className="text-xs text-gray-500 font-medium mb-1.5 block">Expected arrival</label>
//               <input type="datetime-local" value={expectedArrival} min={new Date().toISOString().slice(0, 16)}
//                 onChange={e => setExpectedArrival(e.target.value)}
//                 className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-gray-50 focus:outline-none focus:border-[#d3178a]" />
//             </div>
//             <div>
//               <label className="text-xs text-gray-500 font-medium mb-1.5 block">Reference name</label>
//               <input type="text" value={referenceName} onChange={e => setReferenceName(e.target.value)}
//                 placeholder="e.g. Weekly restock"
//                 className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-gray-50 focus:outline-none focus:border-[#d3178a]" />
//             </div>
//             <div>
//               <label className="text-xs text-gray-500 font-medium mb-1.5 block">Notes</label>
//               <textarea value={notes} onChange={e => setNotes(e.target.value)}
//                 placeholder="Add notes about this transfer..." rows={3}
//                 className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#d3178a] resize-none" />
//             </div>
//           </div>

//           {selectedItems.length > 0 && (
//             <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
//               <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Summary</h3>
//               <div className="space-y-2 text-sm">
//                 <div className="flex justify-between text-gray-600">
//                   <span>Products</span><span className="font-semibold text-gray-900">{selectedItems.length}</span>
//                 </div>
//                 <div className="flex justify-between text-gray-600">
//                   <span>Total units</span>
//                   <span className="font-semibold text-gray-900">{selectedItems.reduce((s, i) => s + i.quantity_to_move, 0)}</span>
//                 </div>
//                 {fromStore && (
//                   <div className="flex justify-between text-gray-600 pt-2 border-t border-gray-100">
//                     <span>From</span>
//                     <span className="font-medium text-gray-800 text-right max-w-[130px] truncate">{fromStore.storeName}</span>
//                   </div>
//                 )}
//                 {toStore && (
//                   <div className="flex justify-between text-gray-600">
//                     <span>To</span>
//                     <span className="font-medium text-gray-800 text-right max-w-[130px] truncate">{toStore.storeName}</span>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}

//           <button onClick={handleSaveDraft} disabled={!canProceed || !selectedItems.length || saving}
//             className="w-full flex items-center justify-center gap-2 text-white text-sm font-semibold py-3 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
//             style={{ background: BRAND }}
//             onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = BRAND_HOVER; }}
//             onMouseLeave={e => e.currentTarget.style.background = BRAND}>
//             {saving ? <RefreshCw size={15} className="animate-spin" /> : null}
//             Save as draft
//           </button>
//           <button onClick={onBack}
//             className="w-full text-gray-500 text-sm font-medium py-2.5 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
//             Cancel
//           </button>
//         </div>
//       </div>

//       {showProductModal && (
//         <ProductSearchModal
//           products={filteredProducts} search={productSearch} onSearchChange={setProductSearch}
//           onSelect={handleAddProduct} onClose={() => { setShowProductModal(false); setProductSearch(""); }}
//           selectedIds={selectedItems.map(i => i.stock_id)} loading={loadingProducts}
//           destStockMap={destStockMap} fromStoreName={fromStore?.storeName || ""} toStoreName={toStore?.storeName || ""}
//         />
//       )}
//     </div>
//   );
// };

// // ═════════════════════════════════════════════════════════════════════════════
// // TRANSFER DETAIL
// // ═════════════════════════════════════════════════════════════════════════════
// const InfoRow = ({ label, value }) => (
//   <div className="flex items-start justify-between gap-3">
//     <span className="text-xs text-gray-400 flex-shrink-0">{label}</span>
//     <span className="text-xs font-medium text-gray-700 text-right">{value}</span>
//   </div>
// );

// const TransferTimeline = ({ status, transfer }) => {
//   const steps = [
//     { key: "draft", label: "Draft created", date: transfer.createdAt },
//     { key: "in_transit", label: "Moved to transit", date: transfer.transit_at },
//     { key: "received", label: "Items received", date: transfer.received_at },
//   ];
//   const ORDER = ["draft", "in_transit", "received"];
//   const currentIndex = ORDER.indexOf(status === "partially_received" ? "received" : status);
//   return (
//     <div className="space-y-3">
//       {steps.map((step, i) => {
//         const done = i <= currentIndex;
//         return (
//           <div key={step.key} className="flex items-start gap-3">
//             <div className="flex flex-col items-center">
//               <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
//                 style={done ? { background: BRAND } : { background: "#f3f4f6" }}>
//                 {done ? <CheckCircle size={11} color="white" /> : <div className="w-2 h-2 rounded-full bg-gray-300" />}
//               </div>
//               {i < steps.length - 1 && (
//                 <div className="w-px flex-1 min-h-[16px]" style={{ background: done && i < currentIndex ? BRAND : "#e5e7eb" }} />
//               )}
//             </div>
//             <div className="pb-3">
//               <p className="text-xs font-medium text-gray-700">{step.label}</p>
//               {step.date && done && (
//                 <p className="text-[10px] text-gray-400">{moment(step.date).format("D MMM YYYY, h:mm a")}</p>
//               )}
//             </div>
//           </div>
//         );
//       })}
//     </div>
//   );
// };

// // ── Store Pricing Modal ───────────────────────────────────────────────────────
// const StorePricingModal = ({ newItems, toStoreName, fromStoreId, fromStoreName = "Origin store", onConfirm, onSkip }) => {
//   const [prices, setPrices] = useState(
//     newItems.map(item => ({
//       stock_id: item.stock_id,
//       product_id: item.product_id,
//       product_info: item.product_info,
//       sellingPrice: "",
//       discount: "0",
//       discountType: "percentage",
//       finalSellingPrice: "",
//       memberPrice: "",
//     }))
//   );
//   // Origin store pricing fetched for reference display
//   const [originPricing, setOriginPricing] = useState({});

//   useEffect(() => {
//     if (!fromStoreId) return;
//     newItems.forEach(async (item) => {
//       const pid = (item.product_id?._id || item.product_id || "").toString();
//       if (!pid) return;
//       try {
//         const resp = await axios.get(`${baseURL}/api/admin/get-variant-pricing`, {
//           params: { product_id: pid, store_id: fromStoreId },
//           headers: { Authorization: getToken() },
//         });
//         if (resp.data.data) {
//           setOriginPricing(prev => ({ ...prev, [pid]: resp.data.data }));
//         }
//       } catch { }
//     });
//   }, [fromStoreId]);

//   const handleChange = (idx, field, val) => {
//     const updated = [...prices];
//     updated[idx][field] = val;
//     const sp = parseFloat(updated[idx].sellingPrice) || 0;
//     const disc = parseFloat(updated[idx].discount) || 0;
//     const type = updated[idx].discountType;
//     if (["sellingPrice", "discount", "discountType"].includes(field)) {
//       const final = type === "percentage" ? sp - (sp * disc / 100) : sp - disc;
//       updated[idx].finalSellingPrice = final > 0 ? parseFloat(final.toFixed(4)) : 0;
//     }
//     setPrices(updated);
//   };

//   const isValid = prices.every(p => parseFloat(p.sellingPrice) > 0);

//   return (
//     <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }}>
//       <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
//         <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
//           <div>
//             <h3 className="text-base font-semibold text-gray-900">Store Pricing</h3>
//             <p className="text-xs text-gray-400 mt-0.5">
//               New products at <span className="font-medium text-gray-600">{toStoreName}</span> — set prices before saving
//             </p>
//           </div>
//         </div>

//         <div className="overflow-y-auto flex-1 p-5 space-y-4">
//           {prices.map((p, i) => {
//             const pid = (p.product_id?._id || p.product_id || "").toString();
//             const op = originPricing[pid];
//             return (
//               <div key={p.stock_id} className="border border-gray-100 rounded-xl p-4 bg-gray-50">
//                 {/* Product header */}
//                 <div className="flex items-center gap-2.5 mb-3">
//                   {p.product_info?.variants?.[0]?.variantImage
//                     ? <img src={`${imageURL}${p.product_info.variants[0].variantImage}`} className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
//                     : <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0"><Package size={13} className="text-gray-400" /></div>}
//                   <span className="font-semibold text-gray-800 text-sm">{p.product_info?.productName || "—"}</span>
//                 </div>

//                 {/* Origin store price reference — table style matching Store Pricing */}
//                 {op && (
//                   <div className="mb-3 rounded-lg border border-gray-200 overflow-hidden">
//                     <table className="w-full text-xs">
//                       <thead>
//                         <tr className="bg-gray-50 border-b border-gray-200">
//                           <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase tracking-wider">Store Name</th>
//                           <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase tracking-wider">Selling Price</th>
//                           <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase tracking-wider">Discount</th>
//                           <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase tracking-wider">Final Price</th>
//                           {op.memberPrice > 0 && (
//                             <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase tracking-wider">Member Price</th>
//                           )}
//                         </tr>
//                       </thead>
//                       <tbody>
//                         <tr className="bg-white">
//                           <td className="px-3 py-2.5 text-gray-700 font-medium">{fromStoreName}</td>
//                           <td className="px-3 py-2.5 text-gray-700">{op.sellingPrice}</td>
//                           <td className="px-3 py-2.5 text-gray-700">
//                             {op.discount || 0} {op.discountType === "percentage" ? "%" : "$"}
//                           </td>
//                           <td className="px-3 py-2.5 text-gray-700">{op.finalSellingPrice}</td>
//                           {op.memberPrice > 0 && (
//                             <td className="px-3 py-2.5 text-gray-700">{op.memberPrice}</td>
//                           )}
//                         </tr>
//                       </tbody>
//                     </table>
//                   </div>
//                 )}

//                 <div className="grid grid-cols-4 gap-3">
//                   <div>
//                     <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1.5">Selling Price</label>
//                     <input type="number" min="0" step="0.01" value={p.sellingPrice}
//                       onChange={e => handleChange(i, "sellingPrice", e.target.value)} placeholder="0"
//                       className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-[#d3178a]" />
//                   </div>

//                   <div>
//                     <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1.5">Discount</label>
//                     <div className="flex gap-1">
//                       <input type="number" min="0" value={p.discount}
//                         onChange={e => handleChange(i, "discount", e.target.value)} placeholder="0"
//                         // className="flex-1 h-9 border border-gray-200 rounded-lg px-2 text-sm bg-white focus:outline-none focus:border-[#d3178a] w-2"/>
//                         className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-[#d3178a]" />
//                       <select value={p.discountType} onChange={e => handleChange(i, "discountType", e.target.value)}
//                         className="h-9 border border-gray-200 rounded-lg px-1 text-xs bg-white focus:outline-none cursor-pointer">
//                         <option value="percentage">%</option>
//                         <option value="amount">$</option>
//                       </select>
//                     </div>
//                   </div>
//                   <div>
//                     <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1.5">Final Price</label>
//                     <input type="number" readOnly value={p.finalSellingPrice} placeholder="0"
//                       className="w-full h-9 border border-gray-100 rounded-lg px-3 text-sm bg-gray-100 text-gray-600 outline-none cursor-default" />
//                   </div>
//                   <div>
//                     <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1.5">Member Price</label>
//                     <input type="number" min="0" step="0.01" value={p.memberPrice}
//                       onChange={e => handleChange(i, "memberPrice", e.target.value)} placeholder="0"
//                       className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-[#d3178a]" />
//                   </div>
//                 </div>
//               </div>
//             );
//           })}
//         </div>

//         <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
//           <button onClick={onSkip}
//             className="text-sm text-gray-500 border border-gray-200 px-4 py-2 rounded-lg hover:border-gray-300 transition-colors">
//             Skip (set prices later)
//           </button>
//           <button onClick={() => onConfirm(prices)} disabled={!isValid}
//             className="flex items-center gap-2 text-white text-sm font-semibold px-5 py-2 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
//             style={{ background: BRAND }}>
//             <CheckCircle size={14} />
//             Save Details
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// // ── Receive Items Modal ───────────────────────────────────────────────────────
// const ReceiveItemsModal = ({ transfer, destStockMap = {}, onClose, onReceived }) => {
//   const [receiveData, setReceiveData] = useState(
//     (transfer.items || []).map(item => ({
//       stock_id: item.stock_id, product_id: item.product_id,
//       category_id: item.category_id, sub_category_id: item.sub_category_id,
//       product_info: item.product_info, quantity_to_move: item.quantity_to_move,
//       accepted: item.quantity_to_move, rejected: 0,
//     }))
//   );
//   const [saving, setSaving] = useState(false);
//   const [showPricing, setShowPricing] = useState(false);
//   const [pendingSubmit, setPendingSubmit] = useState(null);

//   const handleChange = (index, field, val) => {
//     const updated = [...receiveData];
//     updated[index][field] = Math.max(0, parseInt(val) || 0);
//     setReceiveData(updated);
//   };

//   const isNewAtDest = (item) => {
//     const pid = (item.product_id?._id || item.product_id || "").toString();
//     return destStockMap[pid] == null;
//   };

//   const newItems = receiveData.filter(item => item.accepted > 0 && isNewAtDest(item));

//   const doSubmit = async (items, pricing = []) => {
//     setSaving(true);
//     try {
//       await axios.patch(`${baseURL}/api/admin/transfers/${transfer._id}/receive`,
//         { items, pricing }, { headers: { Authorization: getToken() } });
//       Swal.fire({ icon: "success", title: "Items received!", text: "Stock has been updated." });
//       onReceived();
//     } catch (err) {
//       Swal.fire({ icon: "error", title: err?.response?.data?.message || "Failed to receive items." });
//     } finally { setSaving(false); }
//   };

//   const handleSubmit = async () => {
//     const overItems = receiveData.filter(i => (i.accepted + i.rejected) > i.quantity_to_move);
//     if (overItems.length > 0) {
//       const confirm = await Swal.fire({
//         icon: "warning", title: "Over-received items",
//         text: "Some items exceed quantity to move. Continue anyway?",
//         showCancelButton: true, confirmButtonColor: BRAND, confirmButtonText: "Yes, continue",
//       });
//       if (!confirm.isConfirmed) return;
//     }
//     if (newItems.length > 0) {
//       setPendingSubmit(receiveData);
//       setShowPricing(true);
//     } else {
//       doSubmit(receiveData, []);
//     }
//   };

//   return (
//     <>
//       <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
//         <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
//           <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
//             <div>
//               <h3 className="text-base font-semibold text-gray-900">Receive items</h3>
//               <p className="text-xs text-gray-400 mt-0.5">at {transfer.to_store?.storeName}</p>
//             </div>
//             <div className="flex items-center gap-2">
//               <button onClick={() => setReceiveData(d => d.map(i => ({ ...i, accepted: i.quantity_to_move, rejected: 0 })))}
//                 className="text-xs font-semibold px-3 py-1.5 rounded-lg border"
//                 style={{ color: "#15803d", borderColor: "#bbf7d0", background: "#dcfce7" }}>Accept all</button>
//               <button onClick={() => setReceiveData(d => d.map(i => ({ ...i, accepted: 0, rejected: i.quantity_to_move })))}
//                 className="text-xs font-semibold px-3 py-1.5 rounded-lg border"
//                 style={{ color: "#dc2626", borderColor: "#fecaca", background: "#fee2e2" }}>Reject all</button>
//               <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 ml-1">
//                 <X size={15} />
//               </button>
//             </div>
//           </div>

//           <div className="overflow-y-auto flex-1">
//             <table className="w-full text-sm">
//               <thead>
//                 <tr className="bg-gray-50 border-b border-gray-100">
//                   <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Product</th>
//                   <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Accept</th>
//                   <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Reject</th>
//                   <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Received</th>
//                 </tr>
//               </thead>
//               <tbody className="divide-y divide-gray-50">
//                 {receiveData.map((item, i) => {
//                   const total = item.accepted + item.rejected;
//                   const over = total - item.quantity_to_move;
//                   const isOver = over > 0;
//                   const isNew = item.accepted > 0 && isNewAtDest(item);
//                   return (
//                     <tr key={i} className={isOver ? "bg-amber-50" : ""}>
//                       <td className="px-5 py-3">
//                         <div className="flex items-center gap-2.5">
//                           {item.product_info?.variants?.[0]?.variantImage
//                             ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`}
//                               className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
//                             : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
//                               <Package size={13} className="text-gray-300" />
//                             </div>}
//                           <div>
//                             <p className="font-medium text-gray-800 text-sm">{item.product_info?.productName || "—"}</p>
//                             {isNew && (
//                               <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
//                                 New — price required
//                               </span>
//                             )}
//                           </div>
//                         </div>
//                       </td>
//                       <td className="px-5 py-3 text-center">
//                         <input type="number" min={0} value={item.accepted}
//                           onChange={e => handleChange(i, "accepted", e.target.value)}
//                           className="w-20 border rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none"
//                           style={{ borderColor: isOver ? "#fbbf24" : "#bbf7d0", background: isOver ? "#fffbeb" : "#f0fdf4" }} />
//                       </td>
//                       <td className="px-5 py-3 text-center">
//                         <input type="number" min={0} value={item.rejected}
//                           onChange={e => handleChange(i, "rejected", e.target.value)}
//                           className="w-20 border rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none"
//                           style={{ borderColor: isOver ? "#fbbf24" : "#fecaca", background: isOver ? "#fffbeb" : "#fff5f5" }} />
//                       </td>
//                       <td className="px-5 py-3 text-center">
//                         {isOver ? (
//                           <div className="flex flex-col items-center gap-0.5">
//                             <span className="text-xs font-semibold text-amber-600 flex items-center gap-1">
//                               <AlertCircle size={11} />{over} unit{over > 1 ? "s" : ""} over received
//                             </span>
//                             <span className="text-xs text-gray-400">{total} of {item.quantity_to_move}</span>
//                           </div>
//                         ) : (
//                           <span className="text-sm text-gray-500">{total} of {item.quantity_to_move}</span>
//                         )}
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>

//           <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
//             {newItems.length > 0 && (
//               <p className="text-xs text-emerald-600 flex items-center gap-1.5">
//                 <AlertCircle size={12} />
//                 {newItems.length} new product{newItems.length > 1 ? "s" : ""} — pricing required next
//               </p>
//             )}
//             <div className="flex gap-3 ml-auto">
//               <button onClick={onClose} className="text-sm text-gray-600 border border-gray-200 px-4 py-2 rounded-lg hover:border-gray-300">Cancel</button>
//               <button onClick={handleSubmit} disabled={saving}
//                 className="flex items-center gap-2 text-white text-sm font-semibold px-5 py-2 rounded-xl disabled:opacity-50 transition-colors"
//                 style={{ background: "#15803d" }}>
//                 {saving ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
//                 Confirm
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>

//       {showPricing && (
//         <StorePricingModal
//           newItems={newItems}
//           toStoreName={transfer.to_store?.storeName}
//           fromStoreId={transfer.from_store?._id || transfer.from_store_id}
//           fromStoreName={transfer.from_store?.storeName || "Origin store"}
//           onConfirm={(pricing) => { setShowPricing(false); doSubmit(pendingSubmit, pricing); }}
//           onSkip={() => { setShowPricing(false); doSubmit(pendingSubmit, []); }}
//         />
//       )}
//     </>
//   );
// };

// const TransferDetail = ({ transferId, onBack }) => {
//   const [transfer, setTransfer] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [showReceiveModal, setShowReceiveModal] = useState(false);
//   const [actionLoading, setActionLoading] = useState(false);
//   const [destStockMap, setDestStockMap] = useState({});
//   const [originStockMap, setOriginStockMap] = useState({});

//   const fetchTransfer = useCallback(async () => {
//     setLoading(true);
//     try {
//       const resp = await axios.get(`${baseURL}/api/admin/transfers/${transferId}`, {
//         headers: { Authorization: getToken() },
//       });
//       const t = resp.data.data;
//       setTransfer(t);

//       const fromId = t?.from_store?._id || t?.from_store_id;
//       const toId = t?.to_store?._id || t?.to_store_id;

//       if (fromId && t?.status === "draft") {
//         try {
//           const or = await axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${fromId}`,
//             { params: { page: 1, limit: 500 }, headers: { Authorization: getToken() } });
//           const map = {};
//           (or.data.data || []).forEach(s => {
//             const pid = (s.product_id?._id || s.product_id || "").toString();
//             if (pid) map[pid] = s.number_of_products;
//           });
//           setOriginStockMap(map);
//         } catch { setOriginStockMap({}); }
//       }

//       if (toId && ["draft", "in_transit"].includes(t?.status)) {
//         try {
//           const dr = await axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${toId}`,
//             { params: { page: 1, limit: 500 }, headers: { Authorization: getToken() } });
//           const map = {};
//           (dr.data.data || []).forEach(s => {
//             const pid = (s.product_id?._id || s.product_id || "").toString();
//             if (pid) map[pid] = s.number_of_products;
//           });
//           setDestStockMap(map);
//         } catch { setDestStockMap({}); }
//       }
//     } catch { setTransfer(null); }
//     finally { setLoading(false); }
//   }, [transferId]);

//   useEffect(() => { fetchTransfer(); }, [fetchTransfer]);

//   const handleMoveToTransit = async () => {
//     setActionLoading(true);
//     try {
//       await axios.patch(`${baseURL}/api/admin/transfers/${transferId}/move-to-transit`, {},
//         { headers: { Authorization: getToken() } });
//       Swal.fire({ icon: "success", title: "Transfer moved to in transit!" });
//       fetchTransfer();
//     } catch (err) {
//       Swal.fire({ icon: "error", title: err?.response?.data?.message || "Failed." });
//     } finally { setActionLoading(false); }
//   };

//   const handleCancel = async () => {
//     const result = await Swal.fire({
//       icon: "warning", title: "Cancel this transfer?",
//       text: "Stock will not be moved. This cannot be undone.",
//       showCancelButton: true, confirmButtonColor: "#dc2626", confirmButtonText: "Yes, cancel it",
//     });
//     if (!result.isConfirmed) return;
//     setActionLoading(true);
//     try {
//       await axios.patch(`${baseURL}/api/admin/transfers/${transferId}/cancel`, {},
//         { headers: { Authorization: getToken() } });
//       fetchTransfer();
//     } finally { setActionLoading(false); }
//   };

//   if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><RefreshCw size={28} className="animate-spin text-gray-300" /></div>;
//   if (!transfer) return (
//     <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-3">
//       <AlertCircle size={40} className="text-gray-200" />
//       <p className="text-sm text-gray-400">Transfer not found.</p>
//       <button onClick={onBack} className="text-sm" style={{ color: BRAND }}>← Back</button>
//     </div>
//   );

//   const isDraft = transfer.status === "draft";
//   const isInTransit = transfer.status === "in_transit";

//   return (
//     <div className="min-h-screen bg-gray-50 p-6">
//       <div className="flex items-center justify-between mb-6">
//         <div className="flex items-center gap-3">
//           <button onClick={onBack}
//             className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center transition-all"
//             style={{ color: BRAND }}>
//             <ArrowLeftCircle size={18} />
//           </button>
//           <div>
//             <div className="flex items-center gap-2 text-xs text-gray-400 mb-0.5">
//               <span className="cursor-pointer hover:underline" onClick={onBack}>Transfers</span>
//               <span>›</span>
//               <span style={{ color: BRAND }}>{transfer.transfer_number}</span>
//             </div>
//             <div className="flex items-center gap-2">
//               <h1 className="text-xl font-bold text-gray-900">{transfer.transfer_number}</h1>
//               <StatusBadge status={transfer.status} />
//             </div>
//             <p className="text-xs text-gray-400 mt-0.5">
//               Created {moment(transfer.createdAt).format("D MMM YYYY")} · {transfer.items?.length || 0} products
//             </p>
//           </div>
//         </div>
//         <div className="flex items-center gap-2">
//           {isDraft && (
//             <>
//               <button onClick={handleCancel}
//                 className="text-sm text-gray-600 border border-gray-200 px-4 py-2 rounded-lg hover:border-red-300 hover:text-red-500 transition-colors">
//                 Cancel transfer
//               </button>
//               <button onClick={handleMoveToTransit} disabled={actionLoading}
//                 className="flex items-center gap-2 text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
//                 style={{ background: BRAND }}>
//                 {actionLoading ? <RefreshCw size={14} className="animate-spin" /> : <Truck size={14} />}
//                 Move to in transit
//               </button>
//             </>
//           )}
//           {isInTransit && (
//             <button onClick={() => setShowReceiveModal(true)}
//               className="flex items-center gap-2 text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors"
//               style={{ background: "#15803d" }}>
//               <CheckCircle size={14} />Receive items
//             </button>
//           )}
//         </div>
//       </div>

//       <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
//         <div className="xl:col-span-2 space-y-4">
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
//             <div className="flex items-center gap-3 text-sm">
//               <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
//                 <Package size={13} className="text-gray-400" />
//                 <span className="text-gray-700 font-medium">{transfer.from_store?.storeName}</span>
//               </div>
//               <div className="flex items-center gap-1">
//                 <div className="w-8 h-px bg-gray-200" />
//                 <Truck size={14} className="text-gray-400" />
//                 <div className="w-8 h-px bg-gray-200" />
//               </div>
//               <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
//                 <Package size={13} className="text-gray-400" />
//                 <span className="text-gray-700 font-medium">{transfer.to_store?.storeName}</span>
//               </div>
//             </div>
//           </div>

//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
//             <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
//               <h2 className="text-sm font-semibold text-gray-900">Products</h2>
//               <div className="flex items-center gap-4 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
//                 <span className="flex items-center gap-1.5">
//                   <span className="w-2 h-2 rounded-full inline-block" style={{ background: BRAND }} />
//                   {transfer.from_store?.storeName?.split(" ").slice(0, 2).join(" ")}
//                 </span>
//                 <span className="flex items-center gap-1.5">
//                   <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
//                   {transfer.to_store?.storeName?.split(" ").slice(0, 2).join(" ")}
//                 </span>
//               </div>
//             </div>
//             <div className="overflow-x-auto">
//               <table className="w-full text-sm table-fixed">
//                 <thead>
//                   <tr className="bg-gray-50 border-b border-gray-100">
//                     <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[30%]">Product</th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[7%]">Move</th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[14%]">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: BRAND }} />Origin
//                       </span>
//                     </th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[14%]">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />Destination
//                       </span>
//                     </th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[11%]">Accepted</th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[11%]">Rejected</th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[13%]">Received</th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-50">
//                   {(transfer.items || []).map((item, i) => {
//                     const pid = (item.product_id?._id || item.product_id || "").toString();
//                     const isDone = ["received", "partially_received"].includes(transfer.status);
//                     const originCur = originStockMap[pid] ?? null;
//                     const originBef = item.origin_qty_before != null ? item.origin_qty_before : (isDraft && originCur != null ? originCur : null);
//                     const originAft = item.origin_qty_after != null ? item.origin_qty_after : (isDraft && originCur != null ? originCur - item.quantity_to_move : null);
//                     const destCur = destStockMap[pid] ?? null;
//                     const destBef = isDone ? item.destination_qty_before : destCur;
//                     const destAft = isDone ? item.destination_qty_after : (destCur != null ? destCur + item.quantity_to_move : null);
//                     return (
//                       <tr key={i} className="hover:bg-gray-50 transition-colors">
//                         <td className="px-4 py-3">
//                           <div className="flex items-center gap-2.5">
//                             {item.product_info?.variants?.[0]?.variantImage
//                               ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`} className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
//                               : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><Package size={13} className="text-gray-300" /></div>}
//                             <div className="min-w-0 flex-1">
//                               <p className="font-medium text-gray-800 break-words leading-snug text-sm">{item.product_info?.productName || "—"}</p>
//                               <p className="text-xs text-gray-400 mt-0.5">{item.subcategory_info?.sub_collection_name || ""}</p>
//                             </div>
//                           </div>
//                         </td>
//                         <td className="px-4 py-3 text-center font-semibold text-gray-800">{item.quantity_to_move}</td>
//                         <td className="px-4 py-3 text-center">
//                           {originBef != null
//                             ? <span className="text-sm whitespace-nowrap">
//                               <span className="text-gray-500">{originBef}</span>
//                               <span className="mx-1 text-gray-300">→</span>
//                               <span className="font-semibold" style={{ color: BRAND }}>{originAft}</span>
//                             </span>
//                             : <span className="text-gray-400 text-xs">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-center">
//                           {destBef != null
//                             ? <span className="text-sm whitespace-nowrap">
//                               <span className="text-gray-500">{destBef}</span>
//                               <span className="mx-1 text-gray-300">→</span>
//                               <span className="font-semibold text-emerald-600">{destAft}</span>
//                             </span>
//                             : <span className="text-gray-400 text-xs">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-center">
//                           {item.accepted != null
//                             ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: "#15803d", background: "#dcfce7" }}>{item.accepted}</span>
//                             : <span className="text-gray-300">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-center">
//                           {item.rejected != null
//                             ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: "#dc2626", background: "#fee2e2" }}>{item.rejected}</span>
//                             : <span className="text-gray-300">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-center text-sm text-gray-600 whitespace-nowrap">
//                           {item.accepted != null ? `${item.accepted} of ${item.quantity_to_move}` : "—"}
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         </div>

//         <div className="space-y-4">
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
//             <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Details</h3>
//             <InfoRow label="Transfer #" value={transfer.transfer_number} />
//             <InfoRow label="Status" value={<StatusBadge status={transfer.status} />} />
//             <InfoRow label="Created" value={moment(transfer.createdAt).format("D MMM YYYY, h:mm a")} />
//             {transfer.expected_arrival && (
//               <InfoRow label="Expected" value={moment(transfer.expected_arrival).format("D MMM YYYY, h:mm a")} />
//             )}
//             {transfer.reference_name && <InfoRow label="Reference" value={transfer.reference_name} />}
//             {transfer.notes && <InfoRow label="Notes" value={transfer.notes} />}
//           </div>
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
//             <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Timeline</h3>
//             <TransferTimeline status={transfer.status} transfer={transfer} />
//           </div>
//         </div>
//       </div>

//       {showReceiveModal && (
//         <ReceiveItemsModal
//           transfer={transfer}
//           destStockMap={destStockMap}
//           onClose={() => setShowReceiveModal(false)}
//           onReceived={() => { setShowReceiveModal(false); fetchTransfer(); }}
//         />
//       )}
//     </div>
//   );
// };

// // ═════════════════════════════════════════════════════════════════════════════
// // ROOT
// // ═════════════════════════════════════════════════════════════════════════════
// const StockTransfers = () => {
//   const navigate = useNavigate();
//   const [view, setView] = useState("list");
//   const [detailId, setDetailId] = useState(null);
//   return (
//     <>
//       {view === "list" && (
//         <TransferList
//           onCreateNew={() => setView("create")}
//           onViewDetail={id => { setDetailId(id); setView("detail"); }} />
//       )}
//       {view === "create" && (
//         <CreateTransfer
//           onBack={() => setView("list")}
//           onCreated={id => { setDetailId(id); setView("detail"); }} />
//       )}
//       {view === "detail" && (
//         <TransferDetail transferId={detailId} onBack={() => setView("list")} />
//       )}
//     </>
//   );
// };

// export default StockTransfers;





// import React, { useState, useCallback, useEffect } from "react";
// import axios from "axios";
// import { baseURL, imageURL } from "../../../../Services/api/baseURL";
// import { useNavigate } from "react-router-dom";
// import {
//   ArrowLeftCircle, ArrowRight, Package, CheckCircle,
//   Clock, AlertCircle, Plus, PlusCircle, Search, X,
//   Truck, RefreshCw,
// } from "react-feather";
// import moment from "moment";
// import DataTable from "react-data-table-component";
// import Swal from "sweetalert2";
// import { FaRegStickyNote } from "react-icons/fa";

// const BRAND = "#d3178a";
// const BRAND_LIGHT = "#fce7f3";
// const BRAND_HOVER = "#a5116c";

// const getToken = () => JSON.parse(localStorage.getItem("token"));

// const STATUS_CONFIG = {
//   draft: { label: "Draft", color: "#6b7280", bg: "#f3f4f6", icon: Clock },
//   in_transit: { label: "In Transit", color: "#0369a1", bg: "#e0f2fe", icon: Truck },
//   received: { label: "Received", color: "#15803d", bg: "#dcfce7", icon: CheckCircle },
//   partially_received: { label: "Partial", color: "#b45309", bg: "#fef3c7", icon: AlertCircle },
//   cancelled: { label: "Cancelled", color: "#dc2626", bg: "#fee2e2", icon: X },
// };

// const StatusBadge = ({ status }) => {
//   const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
//   const Icon = cfg.icon;
//   return (
//     <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
//       style={{ color: cfg.color, background: cfg.bg }}>
//       <Icon size={11} />{cfg.label}
//     </span>
//   );
// };

// const StatCard = ({ label, value, sub, accent }) => (
//   <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-2 px-6 flex flex-col justify-between"
//     style={{ borderBottom: `3px solid ${accent || BRAND}` }}>
//     <div>
//       <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">{label}</p>
//       <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
//     </div>
//     {sub && <p className="text-xs text-gray-500">{sub}</p>}
//   </div>
// );

// const PERIOD_OPTIONS = [
//   { label: "Today", value: "day" },
//   { label: "Week", value: "week" },
//   { label: "Month", value: "month" },
//   { label: "Year", value: "year" },
//   { label: "Custom", value: "custom" },
// ];

// const buildDateParams = (period, startDate, endDate) => {
//   const now = new Date();
//   if (period === "day") {
//     const s = new Date(now); s.setHours(0, 0, 0, 0);
//     const e = new Date(now); e.setHours(23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   if (period === "week") {
//     const day = now.getDay();
//     const s = new Date(now); s.setDate(now.getDate() - ((day + 6) % 7)); s.setHours(0, 0, 0, 0);
//     const e = new Date(s); e.setDate(s.getDate() + 6); e.setHours(23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   if (period === "month") {
//     const s = new Date(now.getFullYear(), now.getMonth(), 1);
//     const e = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   if (period === "year") {
//     const s = new Date(now.getFullYear(), 0, 1);
//     const e = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   if (period === "custom" && startDate && endDate) {
//     const s = new Date(startDate); s.setHours(0, 0, 0, 0);
//     const e = new Date(endDate); e.setHours(23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   return {};
// };

// // ═════════════════════════════════════════════════════════════════════════════
// // TRANSFER LIST
// // ═════════════════════════════════════════════════════════════════════════════
// // ─── custom DataTable styles ──────────────────────────────────────────────────
// const DT_CUSTOM_STYLES = {
//   headRow: { style: { backgroundColor: "#f9fafb", borderBottom: "1px solid #f3f4f6" } },
//   headCells: { style: { fontSize: "11px", fontWeight: "600", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" } },
//   rows: { style: { cursor: "pointer", fontSize: "13px" }, highlightOnHoverStyle: { backgroundColor: "#f9fafb", transition: "background 0.15s" } },
//   cells: { style: { paddingTop: "10px", paddingBottom: "10px" } },
// };


// // ═════════════════════════════════════════════════════════════════════════════
// // TRANSFER LIST
// // ═════════════════════════════════════════════════════════════════════════════
// const TransferList = ({ onCreateNew, onViewDetail }) => {
//   const [transfers, setTransfers] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [search, setSearch] = useState("");
//   const [statusFilter, setStatusFilter] = useState("all");
//   const [period, setPeriod] = useState("month");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");

//   const fetchTransfers = useCallback(async () => {
//     if (period === "custom" && (!startDate || !endDate)) return;
//     setLoading(true);
//     try {
//       const params = { ...buildDateParams(period, startDate, endDate) };
//       if (statusFilter !== "all") params.status = statusFilter;
//       if (search) params.search = search;
//       const resp = await axios.get(`${baseURL}/api/admin/transfers`, {
//         params, headers: { Authorization: getToken() },
//       });
//       setTransfers(resp.data.data || []);
//     } catch { setTransfers([]); }
//     finally { setLoading(false); }
//   }, [search, statusFilter, period, startDate, endDate]);

//   useEffect(() => { fetchTransfers(); }, [fetchTransfers]);

//   const stats = {
//     total: transfers.length,
//     inTransit: transfers.filter(t => t.status === "in_transit").length,
//     received: transfers.filter(t => ["received", "partially_received"].includes(t.status)).length,
//     totalItems: transfers.reduce((s, t) => s + (t.items?.reduce((a, i) => a + (i.quantity_to_move || 0), 0) || 0), 0),
//   };

//   const filtered = transfers.filter(t => {
//     if (!search) return true;
//     const q = search.toLowerCase();
//     return (
//       t.transfer_number?.toLowerCase().includes(q) ||
//       t.from_store?.storeName?.toLowerCase().includes(q) ||
//       t.to_store?.storeName?.toLowerCase().includes(q)
//     );
//   });

//   const todayStr = new Date().toISOString().split("T")[0];

//   const columns = [
//     {
//       name: "Transfer #",
//       selector: row => row.transfer_number,
//       sortable: true,
//       width: "160px",
//       cell: row => (
//         <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
//           <span className="font-bold text-gray-900">{row.transfer_number}</span>
//           {row.notes && row.notes.trim() !== "" && (
//             <FaRegStickyNote
//               style={{ color: "orange", fontSize: "15px", flexShrink: 0 }}
//               title={`Note: ${row.notes}`}
//             />
//           )}
//         </div>
//       ),
//     },
//     {
//       name: "Origin",
//       selector: row => row.from_store?.storeName,
//       sortable: true,
//       cell: row => <span className="text-gray-600">{row.from_store?.storeName || "—"}</span>,
//     },
//     {
//       name: "Destination",
//       selector: row => row.to_store?.storeName,
//       sortable: true,
//       cell: row => <span className="text-gray-600">{row.to_store?.storeName || "—"}</span>,
//     },
//     {
//       name: "Status",
//       selector: row => row.status,
//       sortable: true,
//       width: "160px",
//       cell: row => <StatusBadge status={row.status} />,
//     },
//     {
//       name: "Items",
//       selector: row => row.items?.length || 0,
//       sortable: true,
//       width: "90px",
//       center: true,
//       cell: row => <span className="text-gray-700">{row.items?.length || 0}</span>,
//     },
//     {
//       name: "Expected Arrival",
//       selector: row => row.expected_arrival,
//       sortable: true,
//       width: "170px",
//       cell: row => (
//         <span className="text-gray-500 text-xs">
//           {row.expected_arrival ? moment(row.expected_arrival).format("D MMM YYYY, h:mm a") : "—"}
//         </span>
//       ),
//     },
//     {
//       name: "Created",
//       selector: row => row.createdAt,
//       sortable: true,
//       width: "110px",
//       cell: row => <span className="text-gray-400 text-xs">{moment(row.createdAt).format("D MMM YYYY")}</span>,
//     },
//   ];

//   return (
//     <div className="min-h-screen bg-gray-50 p-6">
//       {/* Header */}
//       <div className="flex items-center justify-between mb-6">
//         <div className="flex items-center gap-3">
//           <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: BRAND_LIGHT }}>
//             <Truck size={18} style={{ color: BRAND }} />
//           </div>
//           <h1 className="text-xl font-bold text-gray-900">Transfers</h1>
//         </div>
//         <button onClick={onCreateNew}
//           className="flex items-center gap-2 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
//           style={{ background: BRAND }}
//           onMouseEnter={e => e.currentTarget.style.background = BRAND_HOVER}
//           onMouseLeave={e => e.currentTarget.style.background = BRAND}>
//           <PlusCircle size={15} />Create transfer
//         </button>
//       </div>

//       {/* Period filter */}
//       <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-3">
//         <div className="flex flex-wrap items-center gap-3">
//           <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
//             {PERIOD_OPTIONS.map(o => (
//               <button key={o.value}
//                 onClick={() => { setPeriod(o.value); setStartDate(""); setEndDate(""); }}
//                 className="px-4 py-1.5 text-xs font-semibold rounded-md transition-all whitespace-nowrap"
//                 style={period === o.value ? { background: BRAND, color: "#fff" } : { color: "#6b7280" }}>
//                 {o.label}
//               </button>
//             ))}
//           </div>
//           {period === "custom" && (
//             <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
//               <div className="flex items-center gap-2 px-3 py-2">
//                 <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
//                 </svg>
//                 <input type="date" value={startDate} max={endDate || todayStr}
//                   onChange={e => setStartDate(e.target.value)}
//                   className="bg-transparent text-sm text-gray-700 outline-none w-32 cursor-pointer" />
//               </div>
//               <div className="flex items-center gap-1 px-2 border-x border-gray-200">
//                 <div className="w-3 h-px bg-gray-400" />
//                 <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
//                 </svg>
//               </div>
//               <div className="flex items-center gap-2 px-3 py-2">
//                 <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
//                 </svg>
//                 <input type="date" value={endDate} min={startDate} max={todayStr}
//                   onChange={e => setEndDate(e.target.value)}
//                   className="bg-transparent text-sm text-gray-700 outline-none w-32 cursor-pointer" />
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//      {/* Stats */}
//       <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-0">
//         <StatCard label="Total Transfers" value={stats.total} accent={BRAND} />
//         <StatCard label="In Transit" value={stats.inTransit} sub="Active shipments" accent="#0369a1" />
//         <StatCard label="Received" value={stats.received} sub="Completed" accent="#15803d" />
//         <StatCard label="Total Items Moved" value={stats.totalItems} sub="Across all transfers" accent="#b45309" />
//       </div>

//       {/* Table */}
//       <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
//         <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
//           <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
//             {["all", "draft", "in_transit", "received", "cancelled"].map((s) => (
//               <button
//                 key={s}
//                 onClick={() => setStatusFilter(s)}
//                 className="px-3 py-1.5 text-xs font-semibold rounded-md transition-all capitalize"
//                 style={
//                   statusFilter === s
//                     ? { background: BRAND, color: "#fff" }
//                     : { color: "#6b7280" }
//                 }
//               >
//                 {s === "in_transit"
//                   ? "In Transit"
//                   : s.charAt(0).toUpperCase() + s.slice(1)}
//               </button>
//             ))}
//           </div>

//           <div className="mb-0 file-content form-group position-relative search_outer d-flex align-items-center">
//             <i className="fa fa-search"></i>
//             <input
//               className="form-control border-0"
//               value={search}
//               onChange={(e) => setSearch(e.target.value)}
//               type="text"
//               placeholder="Search..."
//             />
//           </div>
//         </div>

//         <DataTable
//           data={filtered}
//           columns={columns}
//           pagination
//           paginationPerPage={10}
//           paginationRowsPerPageOptions={[10, 25, 50, 100]}
//           onRowClicked={row => onViewDetail(row._id)}
//           progressPending={loading}
//           progressComponent={
//             <div className="flex justify-center items-center py-20">
//               <RefreshCw size={24} className="animate-spin text-gray-300" />
//             </div>
//           }
//           noDataComponent={
//             <div className="flex flex-col items-center justify-center py-20 gap-3">
//               <Truck size={40} className="text-gray-200" />
//               <p className="text-sm text-gray-400">No transfers found</p>
//               <button onClick={onCreateNew} className="text-sm font-semibold" style={{ color: BRAND }}>
//                 Create your first transfer →
//               </button>
//             </div>
//           }
//           customStyles={DT_CUSTOM_STYLES}
//           highlightOnHover
//           pointerOnHover
//         />
//       </div>
//     </div>
//   );
// };

// // ═════════════════════════════════════════════════════════════════════════════
// // CREATE TRANSFER
// // ═════════════════════════════════════════════════════════════════════════════
// const StoreStatusPill = ({ label, filled, destination }) => (
//   <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
//     style={filled
//       ? { background: destination ? "#dcfce7" : BRAND_LIGHT, color: destination ? "#15803d" : BRAND }
//       : { background: "#f3f4f6", color: "#9ca3af" }}>
//     {filled ? label : (destination ? "Select destination" : "Select origin")}
//   </div>
// );

// // ── Product Search Modal ──────────────────────────────────────────────────────
// // Now shows both "Available at origin" and "Available at destination"
// const ProductSearchModal = ({
//   products, search, onSearchChange, onSelect, onClose,
//   selectedIds, loading, destStockMap = {}, fromStoreName = "", toStoreName = ""
// }) => (
//   <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
//     <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden">
//       <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
//         <h3 className="text-base font-semibold text-gray-900">Add products</h3>
//         <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400">
//           <X size={15} />
//         </button>
//       </div>
//       <div className="px-5 py-3 border-b border-gray-100">
//         <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus-within:border-[#d3178a]">
//           <Search size={14} className="text-gray-400 flex-shrink-0" />
//           <input value={search} onChange={e => onSearchChange(e.target.value)} placeholder="Search products..." autoFocus
//             className="bg-transparent text-sm text-gray-700 outline-none flex-1" />
//         </div>
//       </div>
//       <div className="overflow-y-auto flex-1">
//         {loading
//           ? <div className="flex justify-center py-10"><RefreshCw size={20} className="animate-spin text-gray-300" /></div>
//           : products.length === 0
//             ? <p className="text-sm text-gray-400 text-center py-10">No products found</p>
//             : (
//               <table className="w-full text-sm table-fixed">
//                 <colgroup>
//                   <col className="w-[55%]" />
//                   <col className="w-[15%]" />
//                   <col className="w-[15%]" />
//                   <col className="w-[15%]" />
//                 </colgroup>
//                 <thead>
//                   <tr className="bg-gray-50 border-b border-gray-100">
//                     <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase">Product</th>
//                     <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: BRAND }} />
//                         {fromStoreName ? fromStoreName.split(" ").slice(0, 2).join(" ") : "At origin"}
//                       </span>
//                     </th>
//                     <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
//                         {toStoreName ? toStoreName.split(" ").slice(0, 2).join(" ") : "At dest."}
//                       </span>
//                     </th>
//                     <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase"></th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-50">
//                   {products.map(p => {
//                     const isSelected = selectedIds.includes(p._id);
//                     const isOutOfStock = p.number_of_products === 0;
//                     // Find destination qty: match by product_id
//                     const pid = (p.product_id?._id || p.product_id || "").toString();
//                     const destQty = destStockMap[pid] ?? null;

//                     return (
//                       <tr key={p._id} className={`${isOutOfStock && !isSelected ? "opacity-50" : ""} ${isSelected ? "bg-gray-50" : ""}`}>
//                         <td className="px-5 py-3">
//                           <div className="flex items-center gap-3">
//                             {p.product_info?.[0]?.variants?.[0]?.variantImage
//                               ? <img src={`${imageURL}${p.product_info[0].variants[0].variantImage}`}
//                                 className={`w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0 ${isSelected ? "opacity-50" : ""}`} alt="" />
//                               : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
//                                 <Package size={13} className="text-gray-300" />
//                               </div>}
//                             <div className="min-w-0 flex-1">
//                               <p className={`font-medium text-sm truncate ${isSelected ? "text-gray-400" : "text-gray-800"}`}
//                                 title={p.product_info?.[0]?.productName || ""}>
//                                 {p.product_info?.[0]?.productName || "—"}
//                               </p>
//                               {isSelected && (
//                                 <p className="text-xs mt-0.5 truncate" style={{ color: BRAND }}>Item already selected</p>
//                               )}
//                             </div>
//                           </div>
//                         </td>
//                         {/* Origin qty */}
//                         <td className="px-5 py-3 text-center">
//                           <span className={`text-sm font-semibold ${isSelected ? "text-gray-300" : "text-gray-700"}`}>
//                             {p.number_of_products}
//                           </span>
//                         </td>
//                         {/* Destination qty */}
//                         <td className="px-5 py-3 text-center">
//                           <span className={`text-sm font-semibold ${isSelected ? "text-gray-300" : "text-gray-700"}`}>
//                             {destQty != null ? destQty : <span className="text-gray-300 text-xs">new</span>}
//                           </span>
//                         </td>
//                         <td className="px-5 py-3 text-right">
//                           {!isSelected && (
//                             <button onClick={() => !isOutOfStock && onSelect(p)} disabled={isOutOfStock}
//                               className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
//                               style={{ color: BRAND, borderColor: BRAND, background: "white" }}>
//                               Add
//                             </button>
//                           )}
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             )}
//       </div>
//       <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end">
//         <button onClick={onClose} className="text-sm font-semibold px-5 py-2 rounded-xl text-white" style={{ background: BRAND }}>
//           Done ({selectedIds.length} added)
//         </button>
//       </div>
//     </div>
//   </div>
// );

// const CreateTransfer = ({ onBack, onCreated }) => {
//   const [stores, setStores] = useState([]);
//   const [fromStore, setFromStore] = useState(null);
//   const [toStore, setToStore] = useState(null);
//   const [availableProducts, setAvailableProducts] = useState([]);
//   const [selectedItems, setSelectedItems] = useState([]);
//   const [productSearch, setProductSearch] = useState("");
//   const [showProductModal, setShowProductModal] = useState(false);
//   const [loadingProducts, setLoadingProducts] = useState(false);
//   const [destStockMap, setDestStockMap] = useState({});  // ← destination stock map
//   const [expectedArrival, setExpectedArrival] = useState("");
//   const [referenceName, setReferenceName] = useState("");
//   const [notes, setNotes] = useState("");
//   const [saving, setSaving] = useState(false);

//   useEffect(() => {
//     axios.get(`${baseURL}/api/store/get-all-stores`, { headers: { Authorization: getToken() } })
//       .then(r => setStores((r.data.data || []).filter(s => s.status === "active")))
//       .catch(() => { });
//   }, []);

//   // Fetch origin store products
//   useEffect(() => {
//     if (!fromStore) { setAvailableProducts([]); return; }
//     setLoadingProducts(true);
//     axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${fromStore._id}`, {
//       params: { page: 1, limit: 200 }, headers: { Authorization: getToken() },
//     })
//       .then(r => setAvailableProducts(r.data.data || []))
//       .finally(() => setLoadingProducts(false));
//   }, [fromStore]);

//   // Fetch destination store stock when toStore changes
//   useEffect(() => {
//     if (!toStore) { setDestStockMap({}); return; }
//     axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${toStore._id}`, {
//       params: { page: 1, limit: 500 }, headers: { Authorization: getToken() },
//     })
//       .then(r => {
//         const map = {};
//         (r.data.data || []).forEach(s => {
//           const pid = (s.product_id?._id || s.product_id || "").toString();
//           if (pid) map[pid] = s.number_of_products;
//         });
//         setDestStockMap(map);
//       })
//       .catch(() => setDestStockMap({}));
//   }, [toStore]);

//   const filteredProducts = availableProducts.filter(p =>
//     !productSearch || p?.product_info?.[0]?.productName?.toLowerCase().includes(productSearch.toLowerCase())
//   );

//   const handleAddProduct = (stockItem) => {
//     if (selectedItems.find(i => i.stock_id === stockItem._id)) return;
//     setSelectedItems(prev => [...prev, {
//       stock_id: stockItem._id,
//       product_id: stockItem.product_id,
//       category_id: stockItem.category_id,
//       sub_category_id: stockItem.sub_category_id,
//       product_info: stockItem.product_info?.[0],
//       quantity_to_move: 1,
//       available_at_origin: stockItem.number_of_products,
//     }]);
//   };

//   const handleRemoveItem = id => setSelectedItems(p => p.filter(i => i.stock_id !== id));
//   const handleQtyChange = (id, qty) => setSelectedItems(p => p.map(i =>
//     i.stock_id === id ? { ...i, quantity_to_move: Math.max(1, Math.min(qty, i.available_at_origin)) } : i
//   ));

//   const handleSaveDraft = async () => {
//     if (!fromStore || !toStore) return Swal.fire({ icon: "warning", title: "Please select origin and destination stores." });
//     if (!selectedItems.length) return Swal.fire({ icon: "warning", title: "Add at least one product to transfer." });
//     setSaving(true);
//     try {
//       const resp = await axios.post(`${baseURL}/api/admin/transfers`, {
//         from_store_id: fromStore._id,
//         to_store_id: toStore._id,
//         items: selectedItems.map(i => ({
//           stock_id: i.stock_id, product_id: i.product_id,
//           category_id: i.category_id, sub_category_id: i.sub_category_id,
//           quantity_to_move: i.quantity_to_move,
//         })),
//         expected_arrival: expectedArrival || null,
//         reference_name: referenceName, notes,
//       }, { headers: { Authorization: getToken() } });
//       Swal.fire({ icon: "success", title: "Transfer created!", text: `Draft ${resp.data.data.transfer_number} saved.` });
//       onCreated(resp.data.data._id);
//     } catch (err) {
//       Swal.fire({ icon: "error", title: err?.response?.data?.message || "Failed to create transfer." });
//     } finally { setSaving(false); }
//   };

//   const canProceed = fromStore && toStore && fromStore._id !== toStore?._id;

//   return (
//     <div className="min-h-screen bg-gray-50 p-6">
//       <div className="flex items-center gap-3 mb-6">
//         <button onClick={onBack}
//           className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center transition-all hover:border-[#d3178a]"
//           style={{ color: BRAND }}>
//           <ArrowLeftCircle size={18} />
//         </button>
//         <div>
//           <div className="flex items-center gap-2 text-xs text-gray-400 mb-0.5">
//             <span className="cursor-pointer hover:underline" onClick={onBack}>Transfers</span>
//             <span>›</span>
//             <span style={{ color: BRAND }}>Create transfer</span>
//           </div>
//           <h1 className="text-xl font-bold text-gray-900">Create transfer</h1>
//         </div>
//       </div>

//       <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
//         <div className="xl:col-span-2 space-y-4">
//           {/* Store picker */}
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
//             <div className="flex items-center gap-4 flex-wrap">
//               <div className="flex-1 min-w-[160px]">
//                 <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Origin store</label>
//                 <select value={fromStore?._id || ""}
//                   onChange={e => { setFromStore(stores.find(s => s._id === e.target.value) || null); setSelectedItems([]); }}
//                   className="w-full h-10 border border-gray-200 rounded-xl px-3 text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-[#d3178a]">
//                   <option value="">Select origin</option>
//                   {stores.map(s => <option key={s._id} value={s._id} disabled={s._id === toStore?._id}>{s.storeName}</option>)}
//                 </select>
//               </div>
//               <div className="flex items-center justify-center mt-5">
//                 <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: BRAND_LIGHT }}>
//                   <ArrowRight size={14} style={{ color: BRAND }} />
//                 </div>
//               </div>
//               <div className="flex-1 min-w-[160px]">
//                 <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Destination store</label>
//                 <select value={toStore?._id || ""}
//                   onChange={e => setToStore(stores.find(s => s._id === e.target.value) || null)}
//                   className="w-full h-10 border border-gray-200 rounded-xl px-3 text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-[#d3178a]">
//                   <option value="">Select destination</option>
//                   {stores.map(s => <option key={s._id} value={s._id} disabled={s._id === fromStore?._id}>{s.storeName}</option>)}
//                 </select>
//               </div>
//             </div>
//             <div className="mt-4 flex items-center gap-3 text-xs">
//               <StoreStatusPill label={fromStore?.storeName} filled={!!fromStore} />
//               <div className="flex-1 h-px border-t border-dashed border-gray-200" />
//               <div className="w-7 h-7 rounded-full border-2 border-gray-200 flex items-center justify-center">
//                 <Truck size={12} className="text-gray-400" />
//               </div>
//               <div className="flex-1 h-px border-t border-dashed border-gray-200" />
//               <StoreStatusPill label={toStore?.storeName} filled={!!toStore} destination />
//             </div>
//           </div>

//           {/* Products */}
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
//             <div className="px-5 py-2 border-b border-gray-100 flex items-center justify-between">
//               <h2 className="text-sm font-semibold text-gray-900">Products to transfer</h2>
//               <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{selectedItems.length} added</span>
//             </div>
//             {selectedItems.length > 0 && (
//               <table className="w-full text-sm">
//                 <thead>
//                   <tr className="bg-gray-50 border-b border-gray-100">
//                     <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Product</th>
//                     <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-20">Move</th>
//                     <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-32">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full inline-block flex-shrink-0" style={{ background: BRAND }} />
//                         At origin
//                       </span>
//                     </th>
//                     <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-36">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block flex-shrink-0" />
//                         At destination
//                       </span>
//                     </th>
//                     <th className="px-4 py-2.5 w-10"></th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-50">
//                   {selectedItems.map(item => {
//                     const pid = (item.product_id?._id || item.product_id || "").toString();
//                     const destQty = destStockMap[pid] ?? null;
//                     return (
//                       <tr key={item.stock_id}>
//                         <td className="px-5 py-3">
//                           <div className="flex items-center gap-2.5">
//                             {item.product_info?.variants?.[0]?.variantImage
//                               ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`} className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
//                               : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><Package size={14} className="text-gray-300" /></div>}
//                             <span className="font-medium text-gray-800 text-sm" title={item.product_info?.productName || ""}>
//                               {item.product_info?.productName || "—"}
//                             </span>
//                           </div>
//                         </td>
//                         <td className="px-4 py-3 text-center w-20">
//                           <input type="number" min={1} max={item.available_at_origin} value={item.quantity_to_move}
//                             onChange={e => handleQtyChange(item.stock_id, parseInt(e.target.value) || 1)}
//                             className="w-14 h-8 border border-gray-200 rounded-lg px-1 text-sm text-center focus:outline-none focus:border-[#d3178a]" />
//                         </td>
//                         <td className="px-4 py-3 text-center whitespace-nowrap">
//                           <span className="text-gray-500 text-sm">{item.available_at_origin}</span>
//                           <span className="mx-1 text-gray-300 text-sm">→</span>
//                           <span className={`font-semibold text-sm ${item.available_at_origin - item.quantity_to_move < 0 ? "text-red-500" : ""}`}
//                             style={item.available_at_origin - item.quantity_to_move >= 0 ? { color: BRAND } : {}}>
//                             {item.available_at_origin - item.quantity_to_move}
//                           </span>
//                         </td>
//                         <td className="px-4 py-3 text-center whitespace-nowrap">
//                           {destQty != null
//                             ? <>
//                               <span className="text-gray-500 text-sm">{destQty}</span>
//                               <span className="mx-1 text-gray-300 text-sm">→</span>
//                               <span className="font-semibold text-sm text-emerald-600">{destQty + item.quantity_to_move}</span>
//                             </>
//                             : toStore
//                               ? <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">New</span>
//                               : <span className="text-gray-300 text-xs">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-right">
//                           <button onClick={() => handleRemoveItem(item.stock_id)}
//                             className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
//                             <X size={13} />
//                           </button>
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             )}
//             <div className="p-4">
//               <button disabled={!fromStore} onClick={() => setShowProductModal(true)}
//                 className="w-full flex items-center gap-2 text-sm text-gray-400 bg-gray-50 border border-dashed border-gray-200 rounded-xl px-4 py-3 hover:border-[#d3178a] hover:text-[#d3178a] transition-colors disabled:cursor-not-allowed disabled:opacity-50">
//                 <Search size={14} />
//                 {fromStore ? "Search products to add" : "Select an origin store first"}
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* Sidebar */}
//         <div className="space-y-4">
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
//             <h3 className="text-sm font-semibold text-gray-900">Transfer details</h3>
//             <div>
//               <label className="text-xs text-gray-500 font-medium mb-1.5 block">Expected arrival</label>
//               <input type="datetime-local" value={expectedArrival} min={new Date().toISOString().slice(0, 16)}
//                 onChange={e => setExpectedArrival(e.target.value)}
//                 className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-gray-50 focus:outline-none focus:border-[#d3178a]" />
//             </div>
//             <div>
//               <label className="text-xs text-gray-500 font-medium mb-1.5 block">Reference name</label>
//               <input type="text" value={referenceName} onChange={e => setReferenceName(e.target.value)}
//                 placeholder="e.g. Weekly restock"
//                 className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-gray-50 focus:outline-none focus:border-[#d3178a]" />
//             </div>
//             <div>
//               <label className="text-xs text-gray-500 font-medium mb-1.5 block">Notes</label>
//               <textarea value={notes} onChange={e => setNotes(e.target.value)}
//                 placeholder="Add notes about this transfer..." rows={3}
//                 className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#d3178a] resize-none" />
//             </div>
//           </div>

//           {selectedItems.length > 0 && (
//             <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
//               <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Summary</h3>
//               <div className="space-y-2 text-sm">
//                 <div className="flex justify-between text-gray-600">
//                   <span>Products</span><span className="font-semibold text-gray-900">{selectedItems.length}</span>
//                 </div>
//                 <div className="flex justify-between text-gray-600">
//                   <span>Total units</span>
//                   <span className="font-semibold text-gray-900">{selectedItems.reduce((s, i) => s + i.quantity_to_move, 0)}</span>
//                 </div>
//                 {fromStore && (
//                   <div className="flex justify-between text-gray-600 pt-2 border-t border-gray-100">
//                     <span>From</span>
//                     <span className="font-medium text-gray-800 text-right max-w-[130px] truncate">{fromStore.storeName}</span>
//                   </div>
//                 )}
//                 {toStore && (
//                   <div className="flex justify-between text-gray-600">
//                     <span>To</span>
//                     <span className="font-medium text-gray-800 text-right max-w-[130px] truncate">{toStore.storeName}</span>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}

//           <button onClick={handleSaveDraft} disabled={!canProceed || !selectedItems.length || saving}
//             className="w-full flex items-center justify-center gap-2 text-white text-sm font-semibold py-3 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
//             style={{ background: BRAND }}
//             onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = BRAND_HOVER; }}
//             onMouseLeave={e => e.currentTarget.style.background = BRAND}>
//             {saving ? <RefreshCw size={15} className="animate-spin" /> : null}
//             Save as draft
//           </button>
//           <button onClick={onBack}
//             className="w-full text-gray-500 text-sm font-medium py-2.5 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
//             Cancel
//           </button>
//         </div>
//       </div>

//       {showProductModal && (
//         <ProductSearchModal
//           products={filteredProducts}
//           search={productSearch}
//           onSearchChange={setProductSearch}
//           onSelect={handleAddProduct}
//           onClose={() => { setShowProductModal(false); setProductSearch(""); }}
//           selectedIds={selectedItems.map(i => i.stock_id)}
//           loading={loadingProducts}
//           destStockMap={destStockMap}
//           fromStoreName={fromStore?.storeName || ""}
//           toStoreName={toStore?.storeName || ""}
//         />
//       )}
//     </div>
//   );
// };

// // ═════════════════════════════════════════════════════════════════════════════
// // TRANSFER DETAIL
// // ═════════════════════════════════════════════════════════════════════════════
// const InfoRow = ({ label, value }) => (
//   <div className="flex items-start justify-between gap-3">
//     <span className="text-xs text-gray-400 flex-shrink-0">{label}</span>
//     <span className="text-xs font-medium text-gray-700 text-right">{value}</span>
//   </div>
// );

// const TransferTimeline = ({ status, transfer }) => {
//   const steps = [
//     { key: "draft", label: "Draft created", date: transfer.createdAt },
//     { key: "in_transit", label: "Moved to transit", date: transfer.transit_at },
//     { key: "received", label: "Items received", date: transfer.received_at },
//   ];
//   const ORDER = ["draft", "in_transit", "received"];
//   const currentIndex = ORDER.indexOf(status === "partially_received" ? "received" : status);
//   return (
//     <div className="space-y-3">
//       {steps.map((step, i) => {
//         const done = i <= currentIndex;
//         return (
//           <div key={step.key} className="flex items-start gap-3">
//             <div className="flex flex-col items-center">
//               <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
//                 style={done ? { background: BRAND } : { background: "#f3f4f6" }}>
//                 {done ? <CheckCircle size={11} color="white" /> : <div className="w-2 h-2 rounded-full bg-gray-300" />}
//               </div>
//               {i < steps.length - 1 && (
//                 <div className="w-px flex-1 min-h-[16px]" style={{ background: done && i < currentIndex ? BRAND : "#e5e7eb" }} />
//               )}
//             </div>
//             <div className="pb-3">
//               <p className="text-xs font-medium text-gray-700">{step.label}</p>
//               {step.date && done && (
//                 <p className="text-[10px] text-gray-400">{moment(step.date).format("D MMM YYYY, h:mm a")}</p>
//               )}
//             </div>
//           </div>
//         );
//       })}
//     </div>
//   );
// };

// // ── Receive Items Modal ───────────────────────────────────────────────────────
// // Allows over-entry, shows "X units over received" warning instead of blocking
// const ReceiveItemsModal = ({ transfer, onClose, onReceived }) => {
//   const [receiveData, setReceiveData] = useState(
//     (transfer.items || []).map(item => ({
//       stock_id: item.stock_id, product_id: item.product_id,
//       category_id: item.category_id, sub_category_id: item.sub_category_id,
//       product_info: item.product_info, quantity_to_move: item.quantity_to_move,
//       accepted: item.quantity_to_move, rejected: 0,
//     }))
//   );
//   const [saving, setSaving] = useState(false);

//   const handleChange = (index, field, val) => {
//     const updated = [...receiveData];
//     const n = Math.max(0, parseInt(val) || 0);
//     updated[index][field] = n;
//     setReceiveData(updated);
//   };

//   const handleSubmit = async () => {
//     // Check for over-received items before submitting
//     const overItems = receiveData.filter(i => (i.accepted + i.rejected) > i.quantity_to_move);
//     if (overItems.length > 0) {
//       const confirm = await Swal.fire({
//         icon: "warning",
//         title: "Over-received items",
//         text: "Some items have accept + reject exceeding quantity to move. Continue anyway?",
//         showCancelButton: true,
//         confirmButtonColor: BRAND,
//         confirmButtonText: "Yes, continue",
//       });
//       if (!confirm.isConfirmed) return;
//     }

//     setSaving(true);
//     try {
//       await axios.patch(`${baseURL}/api/admin/transfers/${transfer._id}/receive`,
//         { items: receiveData }, { headers: { Authorization: getToken() } });
//       Swal.fire({ icon: "success", title: "Items received!", text: "Stock has been updated." });
//       onReceived();
//     } catch (err) {
//       Swal.fire({ icon: "error", title: err?.response?.data?.message || "Failed to receive items." });
//     } finally { setSaving(false); }
//   };

//   return (
//     <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
//       <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
//         <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
//           <div>
//             <h3 className="text-base font-semibold text-gray-900">Receive items</h3>
//             <p className="text-xs text-gray-400 mt-0.5">at {transfer.to_store?.storeName}</p>
//           </div>
//           <div className="flex items-center gap-2">
//             <button onClick={() => setReceiveData(d => d.map(i => ({ ...i, accepted: i.quantity_to_move, rejected: 0 })))}
//               className="text-xs font-semibold px-3 py-1.5 rounded-lg border"
//               style={{ color: "#15803d", borderColor: "#bbf7d0", background: "#dcfce7" }}>Accept all</button>
//             <button onClick={() => setReceiveData(d => d.map(i => ({ ...i, accepted: 0, rejected: i.quantity_to_move })))}
//               className="text-xs font-semibold px-3 py-1.5 rounded-lg border"
//               style={{ color: "#dc2626", borderColor: "#fecaca", background: "#fee2e2" }}>Reject all</button>
//             <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 ml-1">
//               <X size={15} />
//             </button>
//           </div>
//         </div>

//         <div className="overflow-y-auto flex-1">
//           <table className="w-full text-sm">
//             <thead>
//               <tr className="bg-gray-50 border-b border-gray-100">
//                 <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Product</th>
//                 <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Accept</th>
//                 <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Reject</th>
//                 <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Received</th>
//               </tr>
//             </thead>
//             <tbody className="divide-y divide-gray-50">
//               {receiveData.map((item, i) => {
//                 const total = item.accepted + item.rejected;
//                 const over = total - item.quantity_to_move;
//                 const isOver = over > 0;
//                 return (
//                   <tr key={i} className={isOver ? "bg-amber-50" : ""}>
//                     <td className="px-5 py-3">
//                       <div className="flex items-center gap-2.5">
//                         {item.product_info?.variants?.[0]?.variantImage
//                           ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`}
//                             className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
//                           : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
//                             <Package size={13} className="text-gray-300" />
//                           </div>}
//                         <span className="font-medium text-gray-800">{item.product_info?.productName || "—"}</span>
//                       </div>
//                     </td>
//                     <td className="px-5 py-3 text-center">
//                       <input type="number" min={0} value={item.accepted}
//                         onChange={e => handleChange(i, "accepted", e.target.value)}
//                         className="w-20 border rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none"
//                         style={{
//                           borderColor: isOver ? "#fbbf24" : "#bbf7d0",
//                           background: isOver ? "#fffbeb" : "#f0fdf4"
//                         }} />
//                     </td>
//                     <td className="px-5 py-3 text-center">
//                       <input type="number" min={0} value={item.rejected}
//                         onChange={e => handleChange(i, "rejected", e.target.value)}
//                         className="w-20 border rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none"
//                         style={{
//                           borderColor: isOver ? "#fbbf24" : "#fecaca",
//                           background: isOver ? "#fffbeb" : "#fff5f5"
//                         }} />
//                     </td>
//                     <td className="px-5 py-3 text-center">
//                       {isOver ? (
//                         <div className="flex flex-col items-center gap-0.5">
//                           <span className="text-xs font-semibold text-amber-600 flex items-center gap-1">
//                             <AlertCircle size={11} />
//                             {over} unit{over > 1 ? "s" : ""} over received
//                           </span>
//                           <span className="text-xs text-gray-400">{total} of {item.quantity_to_move}</span>
//                         </div>
//                       ) : (
//                         <span className="text-sm text-gray-500">{total} of {item.quantity_to_move}</span>
//                       )}
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         </div>

//         <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-3">
//           <button onClick={onClose} className="text-sm text-gray-600 border border-gray-200 px-4 py-2 rounded-lg hover:border-gray-300">Cancel</button>
//           <button onClick={handleSubmit} disabled={saving}
//             className="flex items-center gap-2 text-white text-sm font-semibold px-5 py-2 rounded-xl disabled:opacity-50 transition-colors"
//             style={{ background: "#15803d" }}>
//             {saving ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
//             Confirm
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// const TransferDetail = ({ transferId, onBack }) => {
//   const [transfer, setTransfer] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [showReceiveModal, setShowReceiveModal] = useState(false);
//   const [actionLoading, setActionLoading] = useState(false);
//   const [destStockMap, setDestStockMap] = useState({});
//   const [originStockMap, setOriginStockMap] = useState({});

//   const fetchTransfer = useCallback(async () => {
//     setLoading(true);
//     try {
//       const resp = await axios.get(`${baseURL}/api/admin/transfers/${transferId}`, {
//         headers: { Authorization: getToken() },
//       });
//       const t = resp.data.data;
//       setTransfer(t);

//       const fromId = t?.from_store?._id || t?.from_store_id;
//       const toId = t?.to_store?._id || t?.to_store_id;

//       if (fromId && t?.status === "draft") {
//         try {
//           const or = await axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${fromId}`,
//             { params: { page: 1, limit: 500 }, headers: { Authorization: getToken() } });
//           const map = {};
//           (or.data.data || []).forEach(s => {
//             const pid = (s.product_id?._id || s.product_id || "").toString();
//             if (pid) map[pid] = s.number_of_products;
//           });
//           setOriginStockMap(map);
//         } catch { setOriginStockMap({}); }
//       }

//       if (toId && ["draft", "in_transit"].includes(t?.status)) {
//         try {
//           const dr = await axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${toId}`,
//             { params: { page: 1, limit: 500 }, headers: { Authorization: getToken() } });
//           const map = {};
//           (dr.data.data || []).forEach(s => {
//             const pid = (s.product_id?._id || s.product_id || "").toString();
//             if (pid) map[pid] = s.number_of_products;
//           });
//           setDestStockMap(map);
//         } catch { setDestStockMap({}); }
//       }
//     } catch { setTransfer(null); }
//     finally { setLoading(false); }
//   }, [transferId]);

//   useEffect(() => { fetchTransfer(); }, [fetchTransfer]);

//   const handleMoveToTransit = async () => {
//     setActionLoading(true);
//     try {
//       await axios.patch(`${baseURL}/api/admin/transfers/${transferId}/move-to-transit`, {},
//         { headers: { Authorization: getToken() } });
//       Swal.fire({ icon: "success", title: "Transfer moved to in transit!" });
//       fetchTransfer();
//     } catch (err) {
//       Swal.fire({ icon: "error", title: err?.response?.data?.message || "Failed." });
//     } finally { setActionLoading(false); }
//   };

//   const handleCancel = async () => {
//     const result = await Swal.fire({
//       icon: "warning", title: "Cancel this transfer?",
//       text: "Stock will not be moved. This cannot be undone.",
//       showCancelButton: true, confirmButtonColor: "#dc2626", confirmButtonText: "Yes, cancel it",
//     });
//     if (!result.isConfirmed) return;
//     setActionLoading(true);
//     try {
//       await axios.patch(`${baseURL}/api/admin/transfers/${transferId}/cancel`, {},
//         { headers: { Authorization: getToken() } });
//       fetchTransfer();
//     } finally { setActionLoading(false); }
//   };

//   if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><RefreshCw size={28} className="animate-spin text-gray-300" /></div>;
//   if (!transfer) return (
//     <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-3">
//       <AlertCircle size={40} className="text-gray-200" />
//       <p className="text-sm text-gray-400">Transfer not found.</p>
//       <button onClick={onBack} className="text-sm" style={{ color: BRAND }}>← Back</button>
//     </div>
//   );

//   const isDraft = transfer.status === "draft";
//   const isInTransit = transfer.status === "in_transit";

//   return (
//     <div className="min-h-screen bg-gray-50 p-6">
//       <div className="flex items-center justify-between mb-6">
//         <div className="flex items-center gap-3">
//           <button onClick={onBack}
//             className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center transition-all"
//             style={{ color: BRAND }}>
//             <ArrowLeftCircle size={18} />
//           </button>
//           <div>
//             <div className="flex items-center gap-2 text-xs text-gray-400 mb-0.5">
//               <span className="cursor-pointer hover:underline" onClick={onBack}>Transfers</span>
//               <span>›</span>
//               <span style={{ color: BRAND }}>{transfer.transfer_number}</span>
//             </div>
//             <div className="flex items-center gap-2">
//               <h1 className="text-xl font-bold text-gray-900">{transfer.transfer_number}</h1>
//               <StatusBadge status={transfer.status} />
//             </div>
//             <p className="text-xs text-gray-400 mt-0.5">
//               Created {moment(transfer.createdAt).format("D MMM YYYY")} · {transfer.items?.length || 0} products
//             </p>
//           </div>
//         </div>
//         <div className="flex items-center gap-2">
//           {isDraft && (
//             <>
//               <button onClick={handleCancel}
//                 className="text-sm text-gray-600 border border-gray-200 px-4 py-2 rounded-lg hover:border-red-300 hover:text-red-500 transition-colors">
//                 Cancel transfer
//               </button>
//               <button onClick={handleMoveToTransit} disabled={actionLoading}
//                 className="flex items-center gap-2 text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
//                 style={{ background: BRAND }}>
//                 {actionLoading ? <RefreshCw size={14} className="animate-spin" /> : <Truck size={14} />}
//                 Move to in transit
//               </button>
//             </>
//           )}
//           {isInTransit && (
//             <button onClick={() => setShowReceiveModal(true)}
//               className="flex items-center gap-2 text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors"
//               style={{ background: "#15803d" }}>
//               <CheckCircle size={14} />Receive items
//             </button>
//           )}
//         </div>
//       </div>

//       <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
//         <div className="xl:col-span-2 space-y-4">
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
//             <div className="flex items-center gap-3 text-sm">
//               <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
//                 <Package size={13} className="text-gray-400" />
//                 <span className="text-gray-700 font-medium">{transfer.from_store?.storeName}</span>
//               </div>
//               <div className="flex items-center gap-1">
//                 <div className="w-8 h-px bg-gray-200" />
//                 <Truck size={14} className="text-gray-400" />
//                 <div className="w-8 h-px bg-gray-200" />
//               </div>
//               <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
//                 <Package size={13} className="text-gray-400" />
//                 <span className="text-gray-700 font-medium">{transfer.to_store?.storeName}</span>
//               </div>
//             </div>
//           </div>

//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
//             <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
//               <h2 className="text-sm font-semibold text-gray-900">Products</h2>
//               <div className="flex items-center gap-4 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
//                 <span className="flex items-center gap-1.5">
//                   <span className="w-2 h-2 rounded-full inline-block" style={{ background: BRAND }} />
//                   {transfer.from_store?.storeName?.split(" ").slice(0, 2).join(" ")}
//                 </span>
//                 <span className="flex items-center gap-1.5">
//                   <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
//                   {transfer.to_store?.storeName?.split(" ").slice(0, 2).join(" ")}
//                 </span>
//               </div>
//             </div>
//             <div className="overflow-x-auto">
//               <table className="w-full text-sm table-fixed">
//                 <thead>
//                   <tr className="bg-gray-50 border-b border-gray-100">
//                     <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[30%]">Product</th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[7%]">Move</th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[14%]">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: BRAND }} />Origin
//                       </span>
//                     </th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[14%]">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />Destination
//                       </span>
//                     </th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[11%]">Accepted</th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[11%]">Rejected</th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[13%]">Received</th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-50">
//                   {(transfer.items || []).map((item, i) => {
//                     const pid = (item.product_id?._id || item.product_id || "").toString();
//                     const isDone = ["received", "partially_received"].includes(transfer.status);
//                     const originCur = originStockMap[pid] ?? null;
//                     const originBef = item.origin_qty_before != null ? item.origin_qty_before : (isDraft && originCur != null ? originCur : null);
//                     const originAft = item.origin_qty_after != null ? item.origin_qty_after : (isDraft && originCur != null ? originCur - item.quantity_to_move : null);
//                     const destCur = destStockMap[pid] ?? null;
//                     const destBef = isDone ? item.destination_qty_before : destCur;
//                     const destAft = isDone ? item.destination_qty_after : (destCur != null ? destCur + item.quantity_to_move : null);
//                     return (
//                       <tr key={i} className="hover:bg-gray-50 transition-colors">
//                         <td className="px-4 py-3">
//                           <div className="flex items-center gap-2.5">
//                             {item.product_info?.variants?.[0]?.variantImage
//                               ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`} className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
//                               : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><Package size={13} className="text-gray-300" /></div>}
//                             <div className="min-w-0 flex-1">
//                               <p className="font-medium text-gray-800 break-words leading-snug text-sm">{item.product_info?.productName || "—"}</p>
//                               <p className="text-xs text-gray-400 mt-0.5">{item.subcategory_info?.sub_collection_name || ""}</p>
//                             </div>
//                           </div>
//                         </td>
//                         <td className="px-4 py-3 text-center font-semibold text-gray-800">{item.quantity_to_move}</td>
//                         <td className="px-4 py-3 text-center">
//                           {originBef != null
//                             ? <span className="text-sm whitespace-nowrap">
//                               <span className="text-gray-500">{originBef}</span>
//                               <span className="mx-1 text-gray-300">→</span>
//                               <span className="font-semibold" style={{ color: BRAND }}>{originAft}</span>
//                             </span>
//                             : <span className="text-gray-400 text-xs">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-center">
//                           {destBef != null
//                             ? <span className="text-sm whitespace-nowrap">
//                               <span className="text-gray-500">{destBef}</span>
//                               <span className="mx-1 text-gray-300">→</span>
//                               <span className="font-semibold text-emerald-600">{destAft}</span>
//                             </span>
//                             : <span className="text-gray-400 text-xs">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-center">
//                           {item.accepted != null
//                             ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: "#15803d", background: "#dcfce7" }}>{item.accepted}</span>
//                             : <span className="text-gray-300">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-center">
//                           {item.rejected != null
//                             ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: "#dc2626", background: "#fee2e2" }}>{item.rejected}</span>
//                             : <span className="text-gray-300">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-center text-sm text-gray-600 whitespace-nowrap">
//                           {item.accepted != null ? `${item.accepted} of ${item.quantity_to_move}` : "—"}
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         </div>

//         <div className="space-y-4">
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
//             <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Details</h3>
//             <InfoRow label="Transfer #" value={transfer.transfer_number} />
//             <InfoRow label="Status" value={<StatusBadge status={transfer.status} />} />
//             <InfoRow label="Created" value={moment(transfer.createdAt).format("D MMM YYYY, h:mm a")} />
//             {transfer.expected_arrival && (
//               <InfoRow label="Expected" value={moment(transfer.expected_arrival).format("D MMM YYYY, h:mm a")} />
//             )}
//             {transfer.reference_name && <InfoRow label="Reference" value={transfer.reference_name} />}
//             {transfer.notes && <InfoRow label="Notes" value={transfer.notes} />}
//           </div>
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
//             <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Timeline</h3>
//             <TransferTimeline status={transfer.status} transfer={transfer} />
//           </div>
//         </div>
//       </div>

//       {showReceiveModal && (
//         <ReceiveItemsModal
//           transfer={transfer}
//           onClose={() => setShowReceiveModal(false)}
//           onReceived={() => { setShowReceiveModal(false); fetchTransfer(); }}
//         />
//       )}
//     </div>
//   );
// };

// // ═════════════════════════════════════════════════════════════════════════════
// // ROOT
// // ═════════════════════════════════════════════════════════════════════════════
// const StockTransfers = () => {
//   const navigate = useNavigate();
//   const [view, setView] = useState("list");
//   const [detailId, setDetailId] = useState(null);
//   return (
//     <>
//       {view === "list" && (
//         <TransferList
//           onCreateNew={() => setView("create")}
//           onViewDetail={id => { setDetailId(id); setView("detail"); }} />
//       )}
//       {view === "create" && (
//         <CreateTransfer
//           onBack={() => setView("list")}
//           onCreated={id => { setDetailId(id); setView("detail"); }} />
//       )}
//       {view === "detail" && (
//         <TransferDetail transferId={detailId} onBack={() => setView("list")} />
//       )}
//     </>
//   );
// };

// export default StockTransfers;




// import React, { useState, useCallback, useEffect } from "react";
// import axios from "axios";
// import { baseURL, imageURL } from "../../../../Services/api/baseURL";
// import { useNavigate } from "react-router-dom";
// import {
//   ArrowLeftCircle, ArrowRight, Package, CheckCircle,
//   Clock, AlertCircle, Plus, PlusCircle, Search, X,
//   Truck, RefreshCw,
// } from "react-feather";
// import moment from "moment";
// import Swal from "sweetalert2";

// const BRAND = "#d3178a";
// const BRAND_LIGHT = "#fce7f3";
// const BRAND_HOVER = "#a5116c";

// const getToken = () => JSON.parse(localStorage.getItem("token"));

// const STATUS_CONFIG = {
//   draft: { label: "Draft", color: "#6b7280", bg: "#f3f4f6", icon: Clock },
//   in_transit: { label: "In Transit", color: "#0369a1", bg: "#e0f2fe", icon: Truck },
//   received: { label: "Received", color: "#15803d", bg: "#dcfce7", icon: CheckCircle },
//   partially_received: { label: "Partial", color: "#b45309", bg: "#fef3c7", icon: AlertCircle },
//   cancelled: { label: "Cancelled", color: "#dc2626", bg: "#fee2e2", icon: X },
// };

// const StatusBadge = ({ status }) => {
//   const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
//   const Icon = cfg.icon;
//   return (
//     <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
//       style={{ color: cfg.color, background: cfg.bg }}>
//       <Icon size={11} />{cfg.label}
//     </span>
//   );
// };

// const StatCard = ({ label, value, sub, accent }) => (
//   <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-2 px-6 flex flex-col justify-between"
//     style={{ borderBottom: `3px solid ${accent || BRAND}` }}>
//     <div>
//       <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">{label}</p>
//       <p className="text-3xl font-bold text-gray-900 mb-2">{value}</p>
//     </div>
//     {sub && <p className="text-xs text-gray-500">{sub}</p>}
//   </div>
// );

// const PERIOD_OPTIONS = [
//   { label: "Today", value: "day" },
//   { label: "Week", value: "week" },
//   { label: "Month", value: "month" },
//   { label: "Year", value: "year" },
//   { label: "Custom", value: "custom" },
// ];

// const buildDateParams = (period, startDate, endDate) => {
//   const now = new Date();
//   if (period === "day") {
//     const s = new Date(now); s.setHours(0, 0, 0, 0);
//     const e = new Date(now); e.setHours(23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   if (period === "week") {
//     const day = now.getDay();
//     const s = new Date(now); s.setDate(now.getDate() - ((day + 6) % 7)); s.setHours(0, 0, 0, 0);
//     const e = new Date(s); e.setDate(s.getDate() + 6); e.setHours(23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   if (period === "month") {
//     const s = new Date(now.getFullYear(), now.getMonth(), 1);
//     const e = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   if (period === "year") {
//     const s = new Date(now.getFullYear(), 0, 1);
//     const e = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   if (period === "custom" && startDate && endDate) {
//     const s = new Date(startDate); s.setHours(0, 0, 0, 0);
//     const e = new Date(endDate); e.setHours(23, 59, 59, 999);
//     return { start_date: s.toISOString(), end_date: e.toISOString() };
//   }
//   return {};
// };

// // ═════════════════════════════════════════════════════════════════════════════
// // TRANSFER LIST
// // ═════════════════════════════════════════════════════════════════════════════
// const TransferList = ({ onCreateNew, onViewDetail }) => {
//   const [transfers, setTransfers] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [search, setSearch] = useState("");
//   const [statusFilter, setStatusFilter] = useState("all");
//   const [period, setPeriod] = useState("month");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");

//   const fetchTransfers = useCallback(async () => {
//     if (period === "custom" && (!startDate || !endDate)) return;
//     setLoading(true);
//     try {
//       const params = { ...buildDateParams(period, startDate, endDate) };
//       if (statusFilter !== "all") params.status = statusFilter;
//       if (search) params.search = search;
//       const resp = await axios.get(`${baseURL}/api/admin/transfers`, {
//         params, headers: { Authorization: getToken() },
//       });
//       setTransfers(resp.data.data || []);
//     } catch { setTransfers([]); }
//     finally { setLoading(false); }
//   }, [search, statusFilter, period, startDate, endDate]);

//   useEffect(() => { fetchTransfers(); }, [fetchTransfers]);

//   const stats = {
//     total: transfers.length,
//     inTransit: transfers.filter(t => t.status === "in_transit").length,
//     received: transfers.filter(t => ["received", "partially_received"].includes(t.status)).length,
//     totalItems: transfers.reduce((s, t) => s + (t.items?.reduce((a, i) => a + (i.quantity_to_move || 0), 0) || 0), 0),
//   };

//   const filtered = transfers.filter(t => {
//     if (!search) return true;
//     const q = search.toLowerCase();
//     return (
//       t.transfer_number?.toLowerCase().includes(q) ||
//       t.from_store?.storeName?.toLowerCase().includes(q) ||
//       t.to_store?.storeName?.toLowerCase().includes(q)
//     );
//   });

//   const todayStr = new Date().toISOString().split("T")[0];

//   return (
//     <div className="min-h-screen bg-gray-50 p-6">
//       <div className="flex items-center justify-between mb-6">
//         <div className="flex items-center gap-3">
//           <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: BRAND_LIGHT }}>
//             <Truck size={18} style={{ color: BRAND }} />
//           </div>
//           <h1 className="text-xl font-bold text-gray-900">Transfers</h1>
//         </div>
//         <button onClick={onCreateNew}
//           className="flex items-center gap-2 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
//           style={{ background: BRAND }}
//           onMouseEnter={e => e.currentTarget.style.background = BRAND_HOVER}
//           onMouseLeave={e => e.currentTarget.style.background = BRAND}>
//           <PlusCircle size={15} />Create transfer
//         </button>
//       </div>

//       {/* Period filter */}
//       <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-3">
//         <div className="flex flex-wrap items-center gap-3">
//           <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
//             {PERIOD_OPTIONS.map(o => (
//               <button key={o.value}
//                 onClick={() => { setPeriod(o.value); setStartDate(""); setEndDate(""); }}
//                 className="px-4 py-1.5 text-xs font-semibold rounded-md transition-all whitespace-nowrap"
//                 style={period === o.value ? { background: BRAND, color: "#fff" } : { color: "#6b7280" }}>
//                 {o.label}
//               </button>
//             ))}
//           </div>
//           {period === "custom" && (
//             <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl overflow-hidden focus-within:border-[#d3178a] transition-colors">
//               <div className="flex items-center gap-2 px-3 py-2">
//                 <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
//                 </svg>
//                 <input type="date" value={startDate} max={endDate || todayStr}
//                   onChange={e => setStartDate(e.target.value)}
//                   className="bg-transparent text-sm text-gray-700 outline-none w-32 cursor-pointer" />
//               </div>
//               <div className="flex items-center gap-1 px-2 border-x border-gray-200">
//                 <div className="w-3 h-px bg-gray-400" />
//                 <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
//                 </svg>
//               </div>
//               <div className="flex items-center gap-2 px-3 py-2">
//                 <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
//                 </svg>
//                 <input type="date" value={endDate} min={startDate} max={todayStr}
//                   onChange={e => setEndDate(e.target.value)}
//                   className="bg-transparent text-sm text-gray-700 outline-none w-32 cursor-pointer" />
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//       <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-1">
//         <StatCard label="Total Transfers" value={stats.total} accent={BRAND} />
//         <StatCard label="In Transit" value={stats.inTransit} sub="Active shipments" accent="#0369a1" />
//         <StatCard label="Received" value={stats.received} sub="Completed" accent="#15803d" />
//         <StatCard label="Total Items Moved" value={stats.totalItems} sub="Across all transfers" accent="#b45309" />
//       </div>

//       <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
//         <div className="p-4 border-b border-gray-100 flex flex-wrap gap-3 items-center">
//           <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
//             {["all", "draft", "in_transit", "received", "cancelled"].map(s => (
//               <button key={s} onClick={() => setStatusFilter(s)}
//                 className="px-3 py-1.5 text-xs font-semibold rounded-md transition-all capitalize"
//                 style={statusFilter === s ? { background: BRAND, color: "#fff" } : { color: "#6b7280" }}>
//                 {s === "in_transit" ? "In Transit" : s.charAt(0).toUpperCase() + s.slice(1)}
//               </button>
//             ))}
//           </div>
//           <div className="flex items-center gap-2 flex-1 min-w-[200px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
//             <Search size={14} className="text-gray-400 flex-shrink-0" />
//             <input value={search} onChange={e => setSearch(e.target.value)}
//               placeholder="Search by transfer # or store..."
//               className="bg-transparent text-sm text-gray-700 outline-none w-full" />
//           </div>
//         </div>

//         {loading ? (
//           <div className="flex justify-center items-center py-20">
//             <RefreshCw size={24} className="animate-spin text-gray-300" />
//           </div>
//         ) : filtered.length === 0 ? (
//           <div className="flex flex-col items-center justify-center py-20 gap-3">
//             <Truck size={40} className="text-gray-200" />
//             <p className="text-sm text-gray-400">No transfers found</p>
//             <button onClick={onCreateNew} className="text-sm font-semibold" style={{ color: BRAND }}>Create your first transfer →</button>
//           </div>
//         ) : (
//           <div className="overflow-x-auto">
//             <table className="w-full text-sm">
//               <thead>
//                 <tr className="bg-gray-50 border-b border-gray-100">
//                   {["Transfer","Origin","Destination","Status","Items","Created","Expected Arrival"].map(h => (
//                     <th key={h} className="px-3 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{h}</th>
//                   ))}
//                 </tr>
//               </thead>
//               <tbody className="divide-y divide-gray-50">
//                 {filtered.map(t => (
//                   <tr key={t._id} onClick={() => onViewDetail(t._id)}
//                     className="hover:bg-gray-50 cursor-pointer transition-colors">
//                     <td className="px-3 py-3.5 font-bold text-gray-900">{t.transfer_number}</td>
//                     <td className="px-3 py-3.5 text-gray-600 max-w-[160px] truncate">{t.from_store?.storeName}</td>
//                     <td className="px-3 py-3.5 text-gray-600 max-w-[160px] truncate">{t.to_store?.storeName}</td>
//                     <td className="px-3 py-3.5"><StatusBadge status={t.status} /></td>
//                     <td className="px-3 py-3.5 text-gray-700">{t.items?.length || 0} products</td>
//                     <td className="px-3 py-3.5 text-gray-400 text-xs">{moment(t.createdAt).format("D MMM YYYY")}</td>
//                      {/* <td className="px-5 py-3.5 text-gray-500 text-xs">
//                       {t.expected_arrival ? moment(t.expected_arrival).format("D MMM YYYY, h:mm a") : "—"}
//                     </td> */}
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// // ═════════════════════════════════════════════════════════════════════════════
// // CREATE TRANSFER
// // ═════════════════════════════════════════════════════════════════════════════
// const StoreStatusPill = ({ label, filled, destination }) => (
//   <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
//     style={filled
//       ? { background: destination ? "#dcfce7" : BRAND_LIGHT, color: destination ? "#15803d" : BRAND }
//       : { background: "#f3f4f6", color: "#9ca3af" }}>
//     {filled ? label : (destination ? "Select destination" : "Select origin")}
//   </div>
// );

// // ── Product Search Modal ──────────────────────────────────────────────────────
// // Now shows both "Available at origin" and "Available at destination"
// const ProductSearchModal = ({
//   products, search, onSearchChange, onSelect, onClose,
//   selectedIds, loading, destStockMap = {}, fromStoreName = "", toStoreName = ""
// }) => (
//   <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
//     <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden">
//       <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
//         <h3 className="text-base font-semibold text-gray-900">Add products</h3>
//         <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400">
//           <X size={15} />
//         </button>
//       </div>
//       <div className="px-5 py-3 border-b border-gray-100">
//         <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus-within:border-[#d3178a]">
//           <Search size={14} className="text-gray-400 flex-shrink-0" />
//           <input value={search} onChange={e => onSearchChange(e.target.value)} placeholder="Search products..." autoFocus
//             className="bg-transparent text-sm text-gray-700 outline-none flex-1" />
//         </div>
//       </div>
//       <div className="overflow-y-auto flex-1">
//         {loading
//           ? <div className="flex justify-center py-10"><RefreshCw size={20} className="animate-spin text-gray-300" /></div>
//           : products.length === 0
//             ? <p className="text-sm text-gray-400 text-center py-10">No products found</p>
//             : (
//               <table className="w-full text-sm table-fixed">
//                 <colgroup>
//                   <col className="w-[55%]" />
//                   <col className="w-[15%]" />
//                   <col className="w-[15%]" />
//                   <col className="w-[15%]" />
//                 </colgroup>
//                 <thead>
//                   <tr className="bg-gray-50 border-b border-gray-100">
//                     <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase">Product</th>
//                     <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: BRAND }} />
//                         {fromStoreName ? fromStoreName.split(" ").slice(0, 2).join(" ") : "At origin"}
//                       </span>
//                     </th>
//                     <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
//                         {toStoreName ? toStoreName.split(" ").slice(0, 2).join(" ") : "At dest."}
//                       </span>
//                     </th>
//                     <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase"></th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-50">
//                   {products.map(p => {
//                     const isSelected = selectedIds.includes(p._id);
//                     const isOutOfStock = p.number_of_products === 0;
//                     // Find destination qty: match by product_id
//                     const pid = (p.product_id?._id || p.product_id || "").toString();
//                     const destQty = destStockMap[pid] ?? null;

//                     return (
//                       <tr key={p._id} className={`${isOutOfStock && !isSelected ? "opacity-50" : ""} ${isSelected ? "bg-gray-50" : ""}`}>
//                         <td className="px-5 py-3">
//                           <div className="flex items-center gap-3">
//                             {p.product_info?.[0]?.variants?.[0]?.variantImage
//                               ? <img src={`${imageURL}${p.product_info[0].variants[0].variantImage}`}
//                                 className={`w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0 ${isSelected ? "opacity-50" : ""}`} alt="" />
//                               : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
//                                 <Package size={13} className="text-gray-300" />
//                               </div>}
//                             <div className="min-w-0 flex-1">
//                               <p className={`font-medium text-sm truncate ${isSelected ? "text-gray-400" : "text-gray-800"}`}
//                                 title={p.product_info?.[0]?.productName || ""}>
//                                 {p.product_info?.[0]?.productName || "—"}
//                               </p>
//                               {isSelected && (
//                                 <p className="text-xs mt-0.5 truncate" style={{ color: BRAND }}>Item already selected</p>
//                               )}
//                             </div>
//                           </div>
//                         </td>
//                         {/* Origin qty */}
//                         <td className="px-5 py-3 text-center">
//                           <span className={`text-sm font-semibold ${isSelected ? "text-gray-300" : "text-gray-700"}`}>
//                             {p.number_of_products}
//                           </span>
//                         </td>
//                         {/* Destination qty */}
//                         <td className="px-5 py-3 text-center">
//                           <span className={`text-sm font-semibold ${isSelected ? "text-gray-300" : "text-gray-700"}`}>
//                             {destQty != null ? destQty : <span className="text-gray-300 text-xs">new</span>}
//                           </span>
//                         </td>
//                         <td className="px-5 py-3 text-right">
//                           {!isSelected && (
//                             <button onClick={() => !isOutOfStock && onSelect(p)} disabled={isOutOfStock}
//                               className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
//                               style={{ color: BRAND, borderColor: BRAND, background: "white" }}>
//                               Add
//                             </button>
//                           )}
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             )}
//       </div>
//       <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end">
//         <button onClick={onClose} className="text-sm font-semibold px-5 py-2 rounded-xl text-white" style={{ background: BRAND }}>
//           Done ({selectedIds.length} added)
//         </button>
//       </div>
//     </div>
//   </div>
// );

// const CreateTransfer = ({ onBack, onCreated }) => {
//   const [stores, setStores] = useState([]);
//   const [fromStore, setFromStore] = useState(null);
//   const [toStore, setToStore] = useState(null);
//   const [availableProducts, setAvailableProducts] = useState([]);
//   const [selectedItems, setSelectedItems] = useState([]);
//   const [productSearch, setProductSearch] = useState("");
//   const [showProductModal, setShowProductModal] = useState(false);
//   const [loadingProducts, setLoadingProducts] = useState(false);
//   const [destStockMap, setDestStockMap] = useState({});  // ← destination stock map
//   const [expectedArrival, setExpectedArrival] = useState("");
//   const [referenceName, setReferenceName] = useState("");
//   const [notes, setNotes] = useState("");
//   const [saving, setSaving] = useState(false);

//   useEffect(() => {
//     axios.get(`${baseURL}/api/store/get-all-stores`, { headers: { Authorization: getToken() } })
//       .then(r => setStores((r.data.data || []).filter(s => s.status === "active")))
//       .catch(() => { });
//   }, []);

//   // Fetch origin store products
//   useEffect(() => {
//     if (!fromStore) { setAvailableProducts([]); return; }
//     setLoadingProducts(true);
//     axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${fromStore._id}`, {
//       params: { page: 1, limit: 200 }, headers: { Authorization: getToken() },
//     })
//       .then(r => setAvailableProducts(r.data.data || []))
//       .finally(() => setLoadingProducts(false));
//   }, [fromStore]);

//   // Fetch destination store stock when toStore changes
//   useEffect(() => {
//     if (!toStore) { setDestStockMap({}); return; }
//     axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${toStore._id}`, {
//       params: { page: 1, limit: 500 }, headers: { Authorization: getToken() },
//     })
//       .then(r => {
//         const map = {};
//         (r.data.data || []).forEach(s => {
//           const pid = (s.product_id?._id || s.product_id || "").toString();
//           if (pid) map[pid] = s.number_of_products;
//         });
//         setDestStockMap(map);
//       })
//       .catch(() => setDestStockMap({}));
//   }, [toStore]);

//   const filteredProducts = availableProducts.filter(p =>
//     !productSearch || p?.product_info?.[0]?.productName?.toLowerCase().includes(productSearch.toLowerCase())
//   );

//   const handleAddProduct = (stockItem) => {
//     if (selectedItems.find(i => i.stock_id === stockItem._id)) return;
//     setSelectedItems(prev => [...prev, {
//       stock_id: stockItem._id,
//       product_id: stockItem.product_id,
//       category_id: stockItem.category_id,
//       sub_category_id: stockItem.sub_category_id,
//       product_info: stockItem.product_info?.[0],
//       quantity_to_move: 1,
//       available_at_origin: stockItem.number_of_products,
//     }]);
//   };

//   const handleRemoveItem = id => setSelectedItems(p => p.filter(i => i.stock_id !== id));
//   const handleQtyChange = (id, qty) => setSelectedItems(p => p.map(i =>
//     i.stock_id === id ? { ...i, quantity_to_move: Math.max(1, Math.min(qty, i.available_at_origin)) } : i
//   ));

//   const handleSaveDraft = async () => {
//     if (!fromStore || !toStore) return Swal.fire({ icon: "warning", title: "Please select origin and destination stores." });
//     if (!selectedItems.length) return Swal.fire({ icon: "warning", title: "Add at least one product to transfer." });
//     setSaving(true);
//     try {
//       const resp = await axios.post(`${baseURL}/api/admin/transfers`, {
//         from_store_id: fromStore._id,
//         to_store_id: toStore._id,
//         items: selectedItems.map(i => ({
//           stock_id: i.stock_id, product_id: i.product_id,
//           category_id: i.category_id, sub_category_id: i.sub_category_id,
//           quantity_to_move: i.quantity_to_move,
//         })),
//         expected_arrival: expectedArrival || null,
//         reference_name: referenceName, notes,
//       }, { headers: { Authorization: getToken() } });
//       Swal.fire({ icon: "success", title: "Transfer created!", text: `Draft ${resp.data.data.transfer_number} saved.` });
//       onCreated(resp.data.data._id);
//     } catch (err) {
//       Swal.fire({ icon: "error", title: err?.response?.data?.message || "Failed to create transfer." });
//     } finally { setSaving(false); }
//   };

//   const canProceed = fromStore && toStore && fromStore._id !== toStore?._id;

//   return (
//     <div className="min-h-screen bg-gray-50 p-6">
//       <div className="flex items-center gap-3 mb-6">
//         <button onClick={onBack}
//           className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center transition-all hover:border-[#d3178a]"
//           style={{ color: BRAND }}>
//           <ArrowLeftCircle size={18} />
//         </button>
//         <div>
//           <div className="flex items-center gap-2 text-xs text-gray-400 mb-0.5">
//             <span className="cursor-pointer hover:underline" onClick={onBack}>Transfers</span>
//             <span>›</span>
//             <span style={{ color: BRAND }}>Create transfer</span>
//           </div>
//           <h1 className="text-xl font-bold text-gray-900">Create transfer</h1>
//         </div>
//       </div>

//       <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
//         <div className="xl:col-span-2 space-y-4">
//           {/* Store picker */}
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
//             <div className="flex items-center gap-4 flex-wrap">
//               <div className="flex-1 min-w-[160px]">
//                 <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Origin store</label>
//                 <select value={fromStore?._id || ""}
//                   onChange={e => { setFromStore(stores.find(s => s._id === e.target.value) || null); setSelectedItems([]); }}
//                   className="w-full h-10 border border-gray-200 rounded-xl px-3 text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-[#d3178a]">
//                   <option value="">Select origin</option>
//                   {stores.map(s => <option key={s._id} value={s._id} disabled={s._id === toStore?._id}>{s.storeName}</option>)}
//                 </select>
//               </div>
//               <div className="flex items-center justify-center mt-5">
//                 <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: BRAND_LIGHT }}>
//                   <ArrowRight size={14} style={{ color: BRAND }} />
//                 </div>
//               </div>
//               <div className="flex-1 min-w-[160px]">
//                 <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Destination store</label>
//                 <select value={toStore?._id || ""}
//                   onChange={e => setToStore(stores.find(s => s._id === e.target.value) || null)}
//                   className="w-full h-10 border border-gray-200 rounded-xl px-3 text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-[#d3178a]">
//                   <option value="">Select destination</option>
//                   {stores.map(s => <option key={s._id} value={s._id} disabled={s._id === fromStore?._id}>{s.storeName}</option>)}
//                 </select>
//               </div>
//             </div>
//             <div className="mt-4 flex items-center gap-3 text-xs">
//               <StoreStatusPill label={fromStore?.storeName} filled={!!fromStore} />
//               <div className="flex-1 h-px border-t border-dashed border-gray-200" />
//               <div className="w-7 h-7 rounded-full border-2 border-gray-200 flex items-center justify-center">
//                 <Truck size={12} className="text-gray-400" />
//               </div>
//               <div className="flex-1 h-px border-t border-dashed border-gray-200" />
//               <StoreStatusPill label={toStore?.storeName} filled={!!toStore} destination />
//             </div>
//           </div>

//           {/* Products */}
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
//             <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
//               <h2 className="text-sm font-semibold text-gray-900">Products to transfer</h2>
//               <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{selectedItems.length} added</span>
//             </div>
//             {selectedItems.length > 0 && (
//               <table className="w-full text-sm">
//                 <thead>
//                   <tr className="bg-gray-50 border-b border-gray-100">
//                     <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Product</th>
//                     <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-20">Move</th>
//                     <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-32">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full inline-block flex-shrink-0" style={{ background: BRAND }} />
//                         At origin
//                       </span>
//                     </th>
//                     <th className="px-2 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-36">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block flex-shrink-0" />
//                         At destination
//                       </span>
//                     </th>
//                     <th className="px-4 py-2.5 w-10"></th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-50">
//                   {selectedItems.map(item => {
//                     const pid = (item.product_id?._id || item.product_id || "").toString();
//                     const destQty = destStockMap[pid] ?? null;
//                     return (
//                       <tr key={item.stock_id}>
//                         <td className="px-3 py-3">
//                           <div className="flex items-center gap-2.5">
//                             {item.product_info?.variants?.[0]?.variantImage
//                               ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`} className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
//                               : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><Package size={14} className="text-gray-300" /></div>}
//                             <span className="font-medium text-gray-800 text-sm" title={item.product_info?.productName || ""}>
//                               {item.product_info?.productName || "—"}
//                             </span>
//                           </div>
//                         </td>
//                         <td className="px-4 py-3 text-center w-20">
//                           <input type="number" min={1} max={item.available_at_origin} value={item.quantity_to_move}
//                             onChange={e => handleQtyChange(item.stock_id, parseInt(e.target.value) || 1)}
//                             className="w-14 h-8 border border-gray-200 rounded-lg px-1 text-sm text-center focus:outline-none focus:border-[#d3178a]" />
//                         </td>
//                         <td className="px-4 py-3 text-center whitespace-nowrap">
//                           <span className="text-gray-500 text-sm">{item.available_at_origin}</span>
//                           <span className="mx-1 text-gray-300 text-sm">→</span>
//                           <span className={`font-semibold text-sm ${item.available_at_origin - item.quantity_to_move < 0 ? "text-red-500" : ""}`}
//                             style={item.available_at_origin - item.quantity_to_move >= 0 ? { color: BRAND } : {}}>
//                             {item.available_at_origin - item.quantity_to_move}
//                           </span>
//                         </td>
//                         <td className="px-4 py-3 text-center whitespace-nowrap">
//                           {destQty != null
//                             ? <>
//                               <span className="text-gray-500 text-sm">{destQty}</span>
//                               <span className="mx-1 text-gray-300 text-sm">→</span>
//                               <span className="font-semibold text-sm text-emerald-600">{destQty + item.quantity_to_move}</span>
//                             </>
//                             : toStore
//                               ? <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">New</span>
//                               : <span className="text-gray-300 text-xs">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-right">
//                           <button onClick={() => handleRemoveItem(item.stock_id)}
//                             className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
//                             <X size={13} />
//                           </button>
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             )}
//             <div className="p-4">
//               <button disabled={!fromStore} onClick={() => setShowProductModal(true)}
//                 className="w-full flex items-center gap-2 text-sm text-gray-400 bg-gray-50 border border-dashed border-gray-200 rounded-xl px-4 py-3 hover:border-[#d3178a] hover:text-[#d3178a] transition-colors disabled:cursor-not-allowed disabled:opacity-50">
//                 <Search size={14} />
//                 {fromStore ? "Search products to add" : "Select an origin store first"}
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* Sidebar */}
//         <div className="space-y-4">
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
//             <h3 className="text-sm font-semibold text-gray-900">Transfer details</h3>
//             <div>
//               <label className="text-xs text-gray-500 font-medium mb-1.5 block">Expected arrival</label>
//               <input type="datetime-local" value={expectedArrival} min={new Date().toISOString().slice(0, 16)}
//                 onChange={e => setExpectedArrival(e.target.value)}
//                 className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-gray-50 focus:outline-none focus:border-[#d3178a]" />
//             </div>
//             <div>
//               <label className="text-xs text-gray-500 font-medium mb-1.5 block">Reference name</label>
//               <input type="text" value={referenceName} onChange={e => setReferenceName(e.target.value)}
//                 placeholder="e.g. Weekly restock"
//                 className="w-full h-9 border border-gray-200 rounded-lg px-3 text-sm bg-gray-50 focus:outline-none focus:border-[#d3178a]" />
//             </div>
//             <div>
//               <label className="text-xs text-gray-500 font-medium mb-1.5 block">Notes</label>
//               <textarea value={notes} onChange={e => setNotes(e.target.value)}
//                 placeholder="Add notes about this transfer..." rows={3}
//                 className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#d3178a] resize-none" />
//             </div>
//           </div>

//           {selectedItems.length > 0 && (
//             <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
//               <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Summary</h3>
//               <div className="space-y-2 text-sm">
//                 <div className="flex justify-between text-gray-600">
//                   <span>Products</span><span className="font-semibold text-gray-900">{selectedItems.length}</span>
//                 </div>
//                 <div className="flex justify-between text-gray-600">
//                   <span>Total units</span>
//                   <span className="font-semibold text-gray-900">{selectedItems.reduce((s, i) => s + i.quantity_to_move, 0)}</span>
//                 </div>
//                 {fromStore && (
//                   <div className="flex justify-between text-gray-600 pt-2 border-t border-gray-100">
//                     <span>From</span>
//                     <span className="font-medium text-gray-800 text-right max-w-[130px] truncate">{fromStore.storeName}</span>
//                   </div>
//                 )}
//                 {toStore && (
//                   <div className="flex justify-between text-gray-600">
//                     <span>To</span>
//                     <span className="font-medium text-gray-800 text-right max-w-[130px] truncate">{toStore.storeName}</span>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}

//           <button onClick={handleSaveDraft} disabled={!canProceed || !selectedItems.length || saving}
//             className="w-full flex items-center justify-center gap-2 text-white text-sm font-semibold py-3 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
//             style={{ background: BRAND }}
//             onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = BRAND_HOVER; }}
//             onMouseLeave={e => e.currentTarget.style.background = BRAND}>
//             {saving ? <RefreshCw size={15} className="animate-spin" /> : null}
//             Save as draft
//           </button>
//           <button onClick={onBack}
//             className="w-full text-gray-500 text-sm font-medium py-2.5 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
//             Cancel
//           </button>
//         </div>
//       </div>

//       {showProductModal && (
//         <ProductSearchModal
//           products={filteredProducts}
//           search={productSearch}
//           onSearchChange={setProductSearch}
//           onSelect={handleAddProduct}
//           onClose={() => { setShowProductModal(false); setProductSearch(""); }}
//           selectedIds={selectedItems.map(i => i.stock_id)}
//           loading={loadingProducts}
//           destStockMap={destStockMap}
//           fromStoreName={fromStore?.storeName || ""}
//           toStoreName={toStore?.storeName || ""}
//         />
//       )}
//     </div>
//   );
// };

// // ═════════════════════════════════════════════════════════════════════════════
// // TRANSFER DETAIL
// // ═════════════════════════════════════════════════════════════════════════════
// const InfoRow = ({ label, value }) => (
//   <div className="flex items-start justify-between gap-3">
//     <span className="text-xs text-gray-400 flex-shrink-0">{label}</span>
//     <span className="text-xs font-medium text-gray-700 text-right">{value}</span>
//   </div>
// );

// const TransferTimeline = ({ status, transfer }) => {
//   const steps = [
//     { key: "draft", label: "Draft created", date: transfer.createdAt },
//     { key: "in_transit", label: "Moved to transit", date: transfer.transit_at },
//     { key: "received", label: "Items received", date: transfer.received_at },
//   ];
//   const ORDER = ["draft", "in_transit", "received"];
//   const currentIndex = ORDER.indexOf(status === "partially_received" ? "received" : status);
//   return (
//     <div className="space-y-3">
//       {steps.map((step, i) => {
//         const done = i <= currentIndex;
//         return (
//           <div key={step.key} className="flex items-start gap-3">
//             <div className="flex flex-col items-center">
//               <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
//                 style={done ? { background: BRAND } : { background: "#f3f4f6" }}>
//                 {done ? <CheckCircle size={11} color="white" /> : <div className="w-2 h-2 rounded-full bg-gray-300" />}
//               </div>
//               {i < steps.length - 1 && (
//                 <div className="w-px flex-1 min-h-[16px]" style={{ background: done && i < currentIndex ? BRAND : "#e5e7eb" }} />
//               )}
//             </div>
//             <div className="pb-3">
//               <p className="text-xs font-medium text-gray-700">{step.label}</p>
//               {step.date && done && (
//                 <p className="text-[10px] text-gray-400">{moment(step.date).format("D MMM YYYY, h:mm a")}</p>
//               )}
//             </div>
//           </div>
//         );
//       })}
//     </div>
//   );
// };

// // ── Receive Items Modal ───────────────────────────────────────────────────────
// // Allows over-entry, shows "X units over received" warning instead of blocking
// const ReceiveItemsModal = ({ transfer, onClose, onReceived }) => {
//   const [receiveData, setReceiveData] = useState(
//     (transfer.items || []).map(item => ({
//       stock_id: item.stock_id, product_id: item.product_id,
//       category_id: item.category_id, sub_category_id: item.sub_category_id,
//       product_info: item.product_info, quantity_to_move: item.quantity_to_move,
//       accepted: item.quantity_to_move, rejected: 0,
//     }))
//   );
//   const [saving, setSaving] = useState(false);

//   const handleChange = (index, field, val) => {
//     const updated = [...receiveData];
//     const n = Math.max(0, parseInt(val) || 0);
//     updated[index][field] = n;
//     setReceiveData(updated);
//   };

//   const handleSubmit = async () => {
//     // Check for over-received items before submitting
//     const overItems = receiveData.filter(i => (i.accepted + i.rejected) > i.quantity_to_move);
//     if (overItems.length > 0) {
//       const confirm = await Swal.fire({
//         icon: "warning",
//         title: "Over-received items",
//         text: "Some items have accept + reject exceeding quantity to move. Continue anyway?",
//         showCancelButton: true,
//         confirmButtonColor: BRAND,
//         confirmButtonText: "Yes, continue",
//       });
//       if (!confirm.isConfirmed) return;
//     }

//     setSaving(true);
//     try {
//       await axios.patch(`${baseURL}/api/admin/transfers/${transfer._id}/receive`,
//         { items: receiveData }, { headers: { Authorization: getToken() } });
//       Swal.fire({ icon: "success", title: "Items received!", text: "Stock has been updated." });
//       onReceived();
//     } catch (err) {
//       Swal.fire({ icon: "error", title: err?.response?.data?.message || "Failed to receive items." });
//     } finally { setSaving(false); }
//   };

//   return (
//     <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
//       <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
//         <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
//           <div>
//             <h3 className="text-base font-semibold text-gray-900">Receive items</h3>
//             <p className="text-xs text-gray-400 mt-0.5">at {transfer.to_store?.storeName}</p>
//           </div>
//           <div className="flex items-center gap-2">
//             <button onClick={() => setReceiveData(d => d.map(i => ({ ...i, accepted: i.quantity_to_move, rejected: 0 })))}
//               className="text-xs font-semibold px-3 py-1.5 rounded-lg border"
//               style={{ color: "#15803d", borderColor: "#bbf7d0", background: "#dcfce7" }}>Accept all</button>
//             <button onClick={() => setReceiveData(d => d.map(i => ({ ...i, accepted: 0, rejected: i.quantity_to_move })))}
//               className="text-xs font-semibold px-3 py-1.5 rounded-lg border"
//               style={{ color: "#dc2626", borderColor: "#fecaca", background: "#fee2e2" }}>Reject all</button>
//             <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 ml-1">
//               <X size={15} />
//             </button>
//           </div>
//         </div>

//         <div className="overflow-y-auto flex-1">
//           <table className="w-full text-sm">
//             <thead>
//               <tr className="bg-gray-50 border-b border-gray-100">
//                 <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Product</th>
//                 <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Accept</th>
//                 <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Reject</th>
//                 <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Received</th>
//               </tr>
//             </thead>
//             <tbody className="divide-y divide-gray-50">
//               {receiveData.map((item, i) => {
//                 const total = item.accepted + item.rejected;
//                 const over = total - item.quantity_to_move;
//                 const isOver = over > 0;
//                 return (
//                   <tr key={i} className={isOver ? "bg-amber-50" : ""}>
//                     <td className="px-5 py-3">
//                       <div className="flex items-center gap-2.5">
//                         {item.product_info?.variants?.[0]?.variantImage
//                           ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`}
//                             className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
//                           : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
//                             <Package size={13} className="text-gray-300" />
//                           </div>}
//                         <span className="font-medium text-gray-800">{item.product_info?.productName || "—"}</span>
//                       </div>
//                     </td>
//                     <td className="px-5 py-3 text-center">
//                       <input type="number" min={0} value={item.accepted}
//                         onChange={e => handleChange(i, "accepted", e.target.value)}
//                         className="w-20 border rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none"
//                         style={{
//                           borderColor: isOver ? "#fbbf24" : "#bbf7d0",
//                           background: isOver ? "#fffbeb" : "#f0fdf4"
//                         }} />
//                     </td>
//                     <td className="px-5 py-3 text-center">
//                       <input type="number" min={0} value={item.rejected}
//                         onChange={e => handleChange(i, "rejected", e.target.value)}
//                         className="w-20 border rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none"
//                         style={{
//                           borderColor: isOver ? "#fbbf24" : "#fecaca",
//                           background: isOver ? "#fffbeb" : "#fff5f5"
//                         }} />
//                     </td>
//                     <td className="px-5 py-3 text-center">
//                       {isOver ? (
//                         <div className="flex flex-col items-center gap-0.5">
//                           <span className="text-xs font-semibold text-amber-600 flex items-center gap-1">
//                             <AlertCircle size={11} />
//                             {over} unit{over > 1 ? "s" : ""} over received
//                           </span>
//                           <span className="text-xs text-gray-400">{total} of {item.quantity_to_move}</span>
//                         </div>
//                       ) : (
//                         <span className="text-sm text-gray-500">{total} of {item.quantity_to_move}</span>
//                       )}
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         </div>

//         <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-3">
//           <button onClick={onClose} className="text-sm text-gray-600 border border-gray-200 px-4 py-2 rounded-lg hover:border-gray-300">Cancel</button>
//           <button onClick={handleSubmit} disabled={saving}
//             className="flex items-center gap-2 text-white text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-50 transition-colors"
//             style={{ background: "#15803d" }}>
//             {saving ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
//             Confirm
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// const TransferDetail = ({ transferId, onBack }) => {
//   const [transfer, setTransfer] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [showReceiveModal, setShowReceiveModal] = useState(false);
//   const [actionLoading, setActionLoading] = useState(false);
//   const [destStockMap, setDestStockMap] = useState({});
//   const [originStockMap, setOriginStockMap] = useState({});

//   const fetchTransfer = useCallback(async () => {
//     setLoading(true);
//     try {
//       const resp = await axios.get(`${baseURL}/api/admin/transfers/${transferId}`, {
//         headers: { Authorization: getToken() },
//       });
//       const t = resp.data.data;
//       setTransfer(t);

//       const fromId = t?.from_store?._id || t?.from_store_id;
//       const toId = t?.to_store?._id || t?.to_store_id;

//       if (fromId && t?.status === "draft") {
//         try {
//           const or = await axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${fromId}`,
//             { params: { page: 1, limit: 500 }, headers: { Authorization: getToken() } });
//           const map = {};
//           (or.data.data || []).forEach(s => {
//             const pid = (s.product_id?._id || s.product_id || "").toString();
//             if (pid) map[pid] = s.number_of_products;
//           });
//           setOriginStockMap(map);
//         } catch { setOriginStockMap({}); }
//       }

//       if (toId && ["draft", "in_transit"].includes(t?.status)) {
//         try {
//           const dr = await axios.get(`${baseURL}/api/admin/get-stocks-by-storeId/${toId}`,
//             { params: { page: 1, limit: 500 }, headers: { Authorization: getToken() } });
//           const map = {};
//           (dr.data.data || []).forEach(s => {
//             const pid = (s.product_id?._id || s.product_id || "").toString();
//             if (pid) map[pid] = s.number_of_products;
//           });
//           setDestStockMap(map);
//         } catch { setDestStockMap({}); }
//       }
//     } catch { setTransfer(null); }
//     finally { setLoading(false); }
//   }, [transferId]);

//   useEffect(() => { fetchTransfer(); }, [fetchTransfer]);

//   const handleMoveToTransit = async () => {
//     setActionLoading(true);
//     try {
//       await axios.patch(`${baseURL}/api/admin/transfers/${transferId}/move-to-transit`, {},
//         { headers: { Authorization: getToken() } });
//       Swal.fire({ icon: "success", title: "Transfer moved to in transit!" });
//       fetchTransfer();
//     } catch (err) {
//       Swal.fire({ icon: "error", title: err?.response?.data?.message || "Failed." });
//     } finally { setActionLoading(false); }
//   };

//   const handleCancel = async () => {
//     const result = await Swal.fire({
//       icon: "warning", title: "Cancel this transfer?",
//       text: "Stock will not be moved. This cannot be undone.",
//       showCancelButton: true, confirmButtonColor: "#dc2626", confirmButtonText: "Yes, cancel it",
//     });
//     if (!result.isConfirmed) return;
//     setActionLoading(true);
//     try {
//       await axios.patch(`${baseURL}/api/admin/transfers/${transferId}/cancel`, {},
//         { headers: { Authorization: getToken() } });
//       fetchTransfer();
//     } finally { setActionLoading(false); }
//   };

//   if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><RefreshCw size={28} className="animate-spin text-gray-300" /></div>;
//   if (!transfer) return (
//     <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-3">
//       <AlertCircle size={40} className="text-gray-200" />
//       <p className="text-sm text-gray-400">Transfer not found.</p>
//       <button onClick={onBack} className="text-sm" style={{ color: BRAND }}>← Back</button>
//     </div>
//   );

//   const isDraft = transfer.status === "draft";
//   const isInTransit = transfer.status === "in_transit";

//   return (
//     <div className="min-h-screen bg-gray-50 p-6">
//       <div className="flex items-center justify-between mb-6">
//         <div className="flex items-center gap-3">
//           <button onClick={onBack}
//             className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center transition-all"
//             style={{ color: BRAND }}>
//             <ArrowLeftCircle size={18} />
//           </button>
//           <div>
//             <div className="flex items-center gap-2 text-xs text-gray-400 mb-0.5">
//               <span className="cursor-pointer hover:underline" onClick={onBack}>Transfers</span>
//               <span>›</span>
//               <span style={{ color: BRAND }}>{transfer.transfer_number}</span>
//             </div>
//             <div className="flex items-center gap-2">
//               <h1 className="text-xl font-bold text-gray-900">{transfer.transfer_number}</h1>
//               <StatusBadge status={transfer.status} />
//             </div>
//             <p className="text-xs text-gray-400 mt-0.5">
//               Created {moment(transfer.createdAt).format("D MMM YYYY")} · {transfer.items?.length || 0} products
//             </p>
//           </div>
//         </div>
//         <div className="flex items-center gap-2">
//           {isDraft && (
//             <>
//               <button onClick={handleCancel}
//                 className="text-sm text-gray-600 border border-gray-200 px-4 py-2 rounded-lg hover:border-red-300 hover:text-red-500 transition-colors">
//                 Cancel transfer
//               </button>
//               <button onClick={handleMoveToTransit} disabled={actionLoading}
//                 className="flex items-center gap-2 text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
//                 style={{ background: BRAND }}>
//                 {actionLoading ? <RefreshCw size={14} className="animate-spin" /> : <Truck size={14} />}
//                 Move to in transit
//               </button>
//             </>
//           )}
//           {isInTransit && (
//             <button onClick={() => setShowReceiveModal(true)}
//               className="flex items-center gap-2 text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors"
//               style={{ background: "#15803d" }}>
//               <CheckCircle size={14} />Receive items
//             </button>
//           )}
//         </div>
//       </div>

//       <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
//         <div className="xl:col-span-2 space-y-4">
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
//             <div className="flex items-center gap-3 text-sm">
//               <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
//                 <Package size={13} className="text-gray-400" />
//                 <span className="text-gray-700 font-medium">{transfer.from_store?.storeName}</span>
//               </div>
//               <div className="flex items-center gap-1">
//                 <div className="w-8 h-px bg-gray-200" />
//                 <Truck size={14} className="text-gray-400" />
//                 <div className="w-8 h-px bg-gray-200" />
//               </div>
//               <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
//                 <Package size={13} className="text-gray-400" />
//                 <span className="text-gray-700 font-medium">{transfer.to_store?.storeName}</span>
//               </div>
//             </div>
//           </div>

//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
//             <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
//               <h2 className="text-sm font-semibold text-gray-900">Products</h2>
//               <div className="flex items-center gap-4 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
//                 <span className="flex items-center gap-1.5">
//                   <span className="w-2 h-2 rounded-full inline-block" style={{ background: BRAND }} />
//                   {transfer.from_store?.storeName?.split(" ").slice(0, 2).join(" ")}
//                 </span>
//                 <span className="flex items-center gap-1.5">
//                   <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
//                   {transfer.to_store?.storeName?.split(" ").slice(0, 2).join(" ")}
//                 </span>
//               </div>
//             </div>
//             <div className="overflow-x-auto">
//               <table className="w-full text-sm table-fixed">
//                 <thead>
//                   <tr className="bg-gray-50 border-b border-gray-100">
//                     <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[30%]">Product</th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[7%]">Move</th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[14%]">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: BRAND }} />Origin
//                       </span>
//                     </th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[14%]">
//                       <span className="flex items-center justify-center gap-1">
//                         <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />Destination
//                       </span>
//                     </th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[11%]">Accepted</th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[11%]">Rejected</th>
//                     <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-[13%]">Received</th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-50">
//                   {(transfer.items || []).map((item, i) => {
//                     const pid = (item.product_id?._id || item.product_id || "").toString();
//                     const isDone = ["received", "partially_received"].includes(transfer.status);
//                     const originCur = originStockMap[pid] ?? null;
//                     const originBef = item.origin_qty_before != null ? item.origin_qty_before : (isDraft && originCur != null ? originCur : null);
//                     const originAft = item.origin_qty_after != null ? item.origin_qty_after : (isDraft && originCur != null ? originCur - item.quantity_to_move : null);
//                     const destCur = destStockMap[pid] ?? null;
//                     const destBef = isDone ? item.destination_qty_before : destCur;
//                     const destAft = isDone ? item.destination_qty_after : (destCur != null ? destCur + item.quantity_to_move : null);
//                     return (
//                       <tr key={i} className="hover:bg-gray-50 transition-colors">
//                         <td className="px-4 py-3">
//                           <div className="flex items-center gap-2.5">
//                             {item.product_info?.variants?.[0]?.variantImage
//                               ? <img src={`${imageURL}${item.product_info.variants[0].variantImage}`} className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" alt="" />
//                               : <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><Package size={13} className="text-gray-300" /></div>}
//                             <div className="min-w-0 flex-1">
//                               <p className="font-medium text-gray-800 break-words leading-snug text-sm">{item.product_info?.productName || "—"}</p>
//                               <p className="text-xs text-gray-400 mt-0.5">{item.subcategory_info?.sub_collection_name || ""}</p>
//                             </div>
//                           </div>
//                         </td>
//                         <td className="px-4 py-3 text-center font-semibold text-gray-800">{item.quantity_to_move}</td>
//                         <td className="px-4 py-3 text-center">
//                           {originBef != null
//                             ? <span className="text-sm whitespace-nowrap">
//                               <span className="text-gray-500">{originBef}</span>
//                               <span className="mx-1 text-gray-300">→</span>
//                               <span className="font-semibold" style={{ color: BRAND }}>{originAft}</span>
//                             </span>
//                             : <span className="text-gray-400 text-xs">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-center">
//                           {destBef != null
//                             ? <span className="text-sm whitespace-nowrap">
//                               <span className="text-gray-500">{destBef}</span>
//                               <span className="mx-1 text-gray-300">→</span>
//                               <span className="font-semibold text-emerald-600">{destAft}</span>
//                             </span>
//                             : <span className="text-gray-400 text-xs">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-center">
//                           {item.accepted != null
//                             ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: "#15803d", background: "#dcfce7" }}>{item.accepted}</span>
//                             : <span className="text-gray-300">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-center">
//                           {item.rejected != null
//                             ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: "#dc2626", background: "#fee2e2" }}>{item.rejected}</span>
//                             : <span className="text-gray-300">—</span>}
//                         </td>
//                         <td className="px-4 py-3 text-center text-sm text-gray-600 whitespace-nowrap">
//                           {item.accepted != null ? `${item.accepted} of ${item.quantity_to_move}` : "—"}
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         </div>

//         <div className="space-y-4">
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
//             <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Details</h3>
//             <InfoRow label="Transfer #" value={transfer.transfer_number} />
//             <InfoRow label="Status" value={<StatusBadge status={transfer.status} />} />
//             <InfoRow label="Created" value={moment(transfer.createdAt).format("D MMM YYYY, h:mm a")} />
//             {transfer.expected_arrival && (
//               <InfoRow label="Expected" value={moment(transfer.expected_arrival).format("D MMM YYYY, h:mm a")} />
//             )}
//             {transfer.reference_name && <InfoRow label="Reference" value={transfer.reference_name} />}
//             {transfer.notes && <InfoRow label="Notes" value={transfer.notes} />}
//           </div>
//           <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
//             <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Timeline</h3>
//             <TransferTimeline status={transfer.status} transfer={transfer} />
//           </div>
//         </div>
//       </div>

//       {showReceiveModal && (
//         <ReceiveItemsModal
//           transfer={transfer}
//           onClose={() => setShowReceiveModal(false)}
//           onReceived={() => { setShowReceiveModal(false); fetchTransfer(); }}
//         />
//       )}
//     </div>
//   );
// };

// // ═════════════════════════════════════════════════════════════════════════════
// // ROOT
// // ═════════════════════════════════════════════════════════════════════════════
// const StockTransfers = () => {
//   const navigate = useNavigate();
//   const [view, setView] = useState("list");
//   const [detailId, setDetailId] = useState(null);
//   return (
//     <>
//       {view === "list" && (
//         <TransferList
//           onCreateNew={() => setView("create")}
//           onViewDetail={id => { setDetailId(id); setView("detail"); }} />
//       )}
//       {view === "create" && (
//         <CreateTransfer
//           onBack={() => setView("list")}
//           onCreated={id => { setDetailId(id); setView("detail"); }} />
//       )}
//       {view === "detail" && (
//         <TransferDetail transferId={detailId} onBack={() => setView("list")} />
//       )}
//     </>
//   );
// };

// export default StockTransfers;